import { DocButton, StorageIndicator, deleteRecordThenDocuments, describeDeleteResult } from './DocumentPanel'
import { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { getCached, setCached } from '../lib/queryCache'
import { matchPaymentsToContracts } from '../lib/contractFinancials'
import { computeIpcFinancials, derivePeriodMonth } from '../lib/ipcFinancials'
import { useProjectAuth } from './useProjectAuth'
import { useAuth } from '../context/AuthContext'
import * as XLSX from 'xlsx'

// Phase 2 (Excel IPC period correction) — local helper, shared by the
// Material and Equipment upload paths. Deliberately kept inside IPC.jsx
// (not promoted into ipcFinancials.js) per the approved scope: this is
// raw Excel-cell parsing/normalization, not a domain business rule —
// ipcFinancials.js's derivePeriodMonth() is the actual business rule and
// stays a pure function operating on already-normalized 'YYYY-MM-DD'
// strings. This function's only job is producing that normalized string
// (or null) from whatever the workbook reader handed back for a given cell.
//
// Bugfix (post-Phase-2 manual test): the original version of this function
// accepted a JS Date object (produced by XLSX.read's cellDates:true option)
// and read its LOCAL calendar components (getFullYear/getMonth/getDate).
// That was believed sufficient to avoid a timezone bug, but manual testing
// from a Saudi Arabia (UTC+3) browser proved otherwise: SheetJS's own
// internal Excel-serial-number-to-Date conversion (inside cellDates:true)
// is itself timezone-dependent — the SAME serial number produced a
// DIFFERENT Date object (a different calendar day entirely) depending on
// the runtime's timezone, before this function ever saw the value.
// Confirmed by direct testing: serial 46113 (2026-04-01) decoded to
// 2026-04-01T00:00:00.000Z under UTC, but 2026-03-31T20:59:08.000Z under
// Asia/Riyadh — a full calendar-day shift, not a formatting artifact.
//
// Fix: stop relying on cellDates:true / Date objects for period_from and
// period_to entirely. XLSX.read() is no longer called with cellDates:true
// (see handleIpcFileUpload/handleEqIpcFileUpload below) for these upload
// paths, so sheet_to_json() now hands back the RAW cell value for a date
// column: either the Excel serial number itself (a plain number, timezone-
// independent, exactly as stored in the file) or a string if the cell
// was entered as text. This function converts a raw Excel serial number to
// 'YYYY-MM-DD' using pure calendar arithmetic (Excel's day-since-epoch
// convention) — no Date constructor, no getTimezoneOffset-sensitive call,
// no runtime-timezone dependency anywhere in the calculation. The 1900
// leap-year quirk in Excel's serial numbering (Excel incorrectly treats
// 1900 as a leap year) is accounted for by using the same 25569-day offset
// SheetJS itself documents for converting to the Unix epoch, applied here
// with plain integer/UTC arithmetic instead of a local Date object.
//
// Accepts exactly two forms, rejects everything else instead of guessing:
//   1. A number — the raw Excel serial date (e.g. 46113 for 2026-04-01).
//      Converted via pure calendar math, immune to runtime timezone.
//   2. A plain string already in unambiguous 'YYYY-MM-DD' form (e.g. if a
//      user pastes text instead of using a real date cell). Matched via a
//      strict regex — no separator variants, no reordering guesses.
// Anything else (a Date object — should no longer occur now that
// cellDates:true is removed for these paths, but rejected defensively if
// it ever does; DD/MM/YYYY; MM/DD/YYYY; free text like 'Oct-2025') returns
// null — the caller must treat that exactly like a missing value, never
// attempt to interpret it further.
function parseExcelDateCell(cellValue) {
  if (typeof cellValue === 'number' && isFinite(cellValue) && cellValue > 0) {
    // Excel serial date -> calendar date, pure arithmetic, no Date object,
    // no timezone dependency. 25569 = number of days between Excel's
    // epoch (1899-12-30, accounting for the 1900 leap-year bug) and the
    // Unix epoch (1970-01-01) — the same constant SheetJS's own SSF module
    // uses internally. Using Date.UTC()/getUTC* here is safe (unlike the
    // getFullYear/getMonth/getDate approach this replaces) because the
    // input to Date.UTC() is already a fully-resolved, timezone-independent
    // millisecond count — there is no ambiguous "which timezone was this
    // day boundary in" step left to get wrong.
    const utcMillis = Math.round((cellValue - 25569) * 86400 * 1000)
    const d = new Date(utcMillis)
    if (isNaN(d.getTime())) return null
    const y = d.getUTCFullYear()
    const m = String(d.getUTCMonth() + 1).padStart(2, '0')
    const day = String(d.getUTCDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }
  if (typeof cellValue === 'string') {
    const trimmed = cellValue.trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed
  }
  return null
}

async function logAudit(entityId, entityType, action, actorName, comment, prevStatus, newStatus, projectId) {
  try {
    await supabase.from('audit_trail').insert({
      entity_id: entityId, entity_type: entityType, action,
      actor_name: actorName, comment, previous_status: prevStatus,
      new_status: newStatus, project_id: projectId,
    })
  } catch(e) { console.error('audit log failed', e) }
}

const STATUS_COLORS = {
  draft:        'bg-gray-100 text-[#2E2C29]',
  submitted:    'bg-[#DDD9D0] text-[#1A7A4A]',
  under_review: 'bg-yellow-100 text-yellow-700',
  approved:     'bg-green-100 text-green-700',
  rejected:     'bg-red-100 text-red-700',
  paid:         'bg-[#DDD9D0] text-[#1C1A18]',
  revoked:      'bg-purple-100 text-purple-700',
}
const STATUSES = ['draft','submitted','under_review','approved','rejected','paid','revoked']
const fmt  = n => new Intl.NumberFormat('en-US',{minimumFractionDigits:2,maximumFractionDigits:2}).format(n||0)
const fmtN = n => new Intl.NumberFormat('en-US',{maximumFractionDigits:0}).format(n||0)

// Project Currency aggregate helper: converts a single IPC's transaction-
// currency financial amount (gross_amount, net_payable, etc.) into the
// Project's primary currency using the PARENT CONTRACT's already-stored
// historical exchange_rate_applied -- never a live/today rate, never a
// second FX engine, never computeIpcFinancials()/any existing formula.
// Returns null (not 0, not a fabricated number) when the parent contract
// predates the Currency Snapshot architecture (exchange_rate_applied is
// NULL) -- that IPC's Project Currency contribution is then genuinely
// unknown, and callers must treat null as "unavailable", not "zero".
function ipcAmountInProjectCurrency(amount, contract) {
  const rate = contract?.exchange_rate_applied
  if (rate == null) return null
  return (parseFloat(amount) || 0) * parseFloat(rate)
}

// Sums a field across a list of IPCs into the Project Currency, using
// each IPC's own parent contract snapshot (never a single blended rate).
// hasIncomplete flags whether at least one included IPC's contract has
// no historical FX snapshot (legacy contract) -- the caller must surface
// this rather than silently presenting `total` as if it were complete.
function sumIpcsInProjectCurrency(ipcList, field, getContract) {
  let total = 0
  let hasIncomplete = false
  for (const ipc of ipcList) {
    const converted = ipcAmountInProjectCurrency(ipc[field], getContract(ipc))
    if (converted == null) hasIncomplete = true
    else total += converted
  }
  return { total, hasIncomplete }
}
const today = () => new Date().toISOString().split('T')[0]

// ─────────────────────────────────────────────────────────────
// WORKFLOW:
//  List → click "+ New IPC"
//       → Step 1: اختر عقد + فترة + رقم المستخلص → Next
//       → Step 2: بنود الـ PO تظهر، تحط كميات → Submit
//  List → click "View" على مستخلص موجود → تشوف/تعدل البنود
// ─────────────────────────────────────────────────────────────


// ── Audit Trail ──────────────────────────────────────────
function IPCAuditTrail({ entityId }) {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    if (!entityId) return
    supabase.from('audit_trail').select('*').eq('entity_id', entityId)
      .order('created_at', { ascending: false }).limit(20)
      .then(({ data }) => { setLogs(data||[]); setLoading(false) })
  }, [entityId])
  const s = (a) => a==='approved'?{c:'#1A7A4A',b:'#DCFCE7'}:a==='rejected'?{c:'#DC2626',b:'#FEF2F2'}:a==='submitted'?{c:'#D97706',b:'#FEF3C7'}:{c:'#6B7280',b:'#F3F4F6'}
  if (loading) return <div style={{padding:'16px 0',color:'#9CA3AF',fontSize:12}}>Loading trail…</div>
  if (!logs.length) return <div style={{padding:'16px 0',color:'#9CA3AF',fontSize:12}}>No audit history yet.</div>
  return (
    <div style={{display:'flex',flexDirection:'column',gap:10}}>
      {logs.map((log,i)=>{
        const st=s(log.action)
        return (
          <div key={log.id} style={{display:'flex',gap:10,alignItems:'flex-start'}}>
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',flexShrink:0}}>
              <div style={{width:28,height:28,borderRadius:'50%',background:st.b,border:`1px solid ${st.c}30`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,color:st.c}}>
                {log.action==='approved'?'✓':log.action==='rejected'?'✗':log.action==='submitted'?'→':'●'}
              </div>
              {i<logs.length-1&&<div style={{width:1,height:20,background:'#E5E7EB',marginTop:2}}/>}
            </div>
            <div style={{flex:1}}>
              <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:2}}>
                <span style={{fontSize:11,fontWeight:700,padding:'1px 6px',borderRadius:10,background:st.b,color:st.c}}>{log.action}</span>
                {log.actor_name&&<span style={{fontSize:11,fontWeight:600,color:'#0F1017'}}>{log.actor_name}</span>}
              </div>
              <div style={{fontSize:10,color:'#9CA3AF'}}>{new Date(log.created_at).toLocaleString('en-GB',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}</div>
              {log.comment&&<div style={{fontSize:11,color:'#374151',marginTop:3,fontStyle:'italic'}}>"{log.comment}"</div>}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function IPC({ projectId: initProjectId, onNavigate }) {
  // Floating horizontal-navigation state for the two main IPC tables
  // (Material/Equipment). Reuses the same pattern proven in
  // PurchaseOrders.jsx: ref is the single source of truth for scroll
  // position, no independent scroll state. Unlike PO, these tables
  // already have a correctly-working sticky <thead> (their own
  // overflow-auto + maxHeight:'70vh' wrapper IS the real vertical
  // scrolling ancestor, so no header-splitting is needed here) -- only
  // min-width + floating nav are added.
  const ipcMatScrollRef = useRef(null)
  const [ipcMatScrollState, setIpcMatScrollState] = useState({ hasOverflow: false, atStart: true, atEnd: true, ratio: 0 })
  const [ipcMatNavLeftRight, setIpcMatNavLeftRight] = useState({ left: 0, right: 0 })
  const updateIpcMatScrollState = useCallback(() => {
    const el = ipcMatScrollRef.current
    if (!el) return
    const hasOverflow = el.scrollWidth > el.clientWidth + 1
    const maxScroll = el.scrollWidth - el.clientWidth
    const ratio = maxScroll > 0 ? el.scrollLeft / maxScroll : 0
    setIpcMatScrollState({ hasOverflow, atStart: el.scrollLeft <= 1, atEnd: maxScroll <= 1 || el.scrollLeft >= maxScroll - 1, ratio })
    // Fixed nav bar (single "always visible near table, viewport-
    // anchored" choice) must line up with the table itself, not the
    // full window -- offsets are taken from the scroll container's own
    // bounding box since it already spans the table's actual width.
    const rect = el.getBoundingClientRect()
    setIpcMatNavLeftRight({ left: rect.left, right: window.innerWidth - rect.right })
  }, [])
  const ipcMatScrollBy = (dir) => { const el = ipcMatScrollRef.current; if (el) el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.75), behavior: 'smooth' }) }
  const ipcMatScrollToRatio = (ratio) => { const el = ipcMatScrollRef.current; if (!el) return; const maxScroll = el.scrollWidth - el.clientWidth; el.scrollLeft = ratio * maxScroll }

  const ipcEqScrollRef = useRef(null)
  const [ipcEqScrollState, setIpcEqScrollState] = useState({ hasOverflow: false, atStart: true, atEnd: true, ratio: 0 })
  const [ipcEqNavLeftRight, setIpcEqNavLeftRight] = useState({ left: 0, right: 0 })
  const updateIpcEqScrollState = useCallback(() => {
    const el = ipcEqScrollRef.current
    if (!el) return
    const hasOverflow = el.scrollWidth > el.clientWidth + 1
    const maxScroll = el.scrollWidth - el.clientWidth
    const ratio = maxScroll > 0 ? el.scrollLeft / maxScroll : 0
    setIpcEqScrollState({ hasOverflow, atStart: el.scrollLeft <= 1, atEnd: maxScroll <= 1 || el.scrollLeft >= maxScroll - 1, ratio })
    const rect = el.getBoundingClientRect()
    setIpcEqNavLeftRight({ left: rect.left, right: window.innerWidth - rect.right })
  }, [])
  const ipcEqScrollBy = (dir) => { const el = ipcEqScrollRef.current; if (el) el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.75), behavior: 'smooth' }) }
  const ipcEqScrollToRatio = (ratio) => { const el = ipcEqScrollRef.current; if (!el) return; const maxScroll = el.scrollWidth - el.clientWidth; el.scrollLeft = ratio * maxScroll }

  const [mainTab,        setMainTab]        = useState('material') // material | equipment
  const [projects,       setProjects]       = useState([])
  const [projectId,      setProjectId]      = useState(null)
  useEffect(() => { if (initProjectId) setProjectId(initProjectId) }, [initProjectId])
  const [projectName,    setProjectName]    = useState('')
  const [ipcs,           setIpcs]           = useState([])
  const [contracts,      setContracts]      = useState([])
  const [loading,        setLoading]        = useState(false)
  const [bulkDate,       setBulkDate]       = useState('')
  const [bulkUpdating,   setBulkUpdating]   = useState(false)

  // New IPC wizard
  const [wizard,         setWizard]         = useState(null)   // null | {step:1|2, form, poLines, lines}
  const [contractSearch,  setContractSearch]  = useState('')
  // View/edit existing IPC
  const [selectedIpc,    setSelectedIpc]    = useState(null)
  // Phase 3: Bulk Approval — Validate All -> Approve All, no partial
  // application. Kept as local component state (not persisted), reused
  // by the existing single-approval code path (handleIpcApproval) in a
  // loop rather than a new DB-level bulk operation, per the approved design.
  const [bulkSelectedIds, setBulkSelectedIds] = useState(new Set())
  const [bulkValidation,  setBulkValidation]  = useState(null) // null | { allValid: bool, results: [{id, ipc_number, ok, reason}] }
  const [bulkApproving,   setBulkApproving]   = useState(false)
  // Separate selection state for the Equipment tab (independent list/table).
  const [eqBulkSelectedIds, setEqBulkSelectedIds] = useState(new Set())
  const [eqBulkValidation,  setEqBulkValidation]  = useState(null)
  const [eqBulkApproving,   setEqBulkApproving]   = useState(false)
  const [contractHistory, setContractHistory] = useState(null) // { contractId, contractType, contractLabel, ipcs: [] }

  function openContractHistory(contractId, contractType) {
    const source = contractType === 'equipment' ? eqIpcs : ipcs
    const idKey = contractType === 'equipment' ? 'equipment_contract_id' : 'subcontract_id'
    const related = source.filter(x => x[idKey] === contractId).sort((a,b)=>(a.ipc_number||0)-(b.ipc_number||0))
    const first = related[0]
    const label = contractType === 'equipment'
      ? `${first?.equipment_contracts?.contract_no || ''} — ${first?.equipment_contracts?.contractor_name || ''}`
      : `${first?.subcontracts?.contract_no || ''} — ${first?.subcontracts?.contractor_name || ''}`
    setContractHistory({ contractId, contractType, contractLabel: label, ipcs: related })
  }
  const [ipcDetailTab,   setIpcDetailTab]   = useState('lines')
  const [newDedForm,     setNewDedForm]     = useState({ deduction_type:'material', description:'', amount:'' })
  const [eqNewDedForm,   setEqNewDedForm]   = useState({ deduction_type:'equipment', description:'', amount:'' })
  const [selectedLines,  setSelectedLines]  = useState([])
  const [selectedPoLines,setSelectedPoLines]= useState([])
  const [eqSelectedPoLines, setEqSelectedPoLines] = useState([])
  const [saving,         setSaving]         = useState(false)

  const [filterStatus,   setFilterStatus]   = useState('all')
  const [filterType,     setFilterType]     = useState('all')
  const [filterType2,    setFilterType2]    = useState('all')
  const [filterGross,    setFilterGross]    = useState('')
  const [filterRet,      setFilterRet]      = useState('')
  const [filterNet,      setFilterNet]      = useState('')
  const [searchText,     setSearchText]     = useState('')
  const [filterContract, setFilterContract] = useState('all')
  const [filterPO,       setFilterPO]       = useState('')
  const [stats,          setStats]          = useState({total:0,pending:0,certified:0,paid:0})
  const fileRef = useRef()
  // Real authorization source (Phase 3 migration) -- can() mirrors
  // has_permission() exactly (verified 25/25 against the 5 real test
  // users). Final cleanup: tempRole/IPC_ROLE_LABELS/the role-switcher
  // dropdown removed entirely -- actor names now come from the real
  // authenticated user's profile.full_name, and zero authorization
  // decision in this file has depended on tempRole since the earlier
  // migration.
  const { can } = useProjectAuth(projectId)
  const { profile } = useAuth()
  const actorName = profile?.full_name || profile?.email || 'Unknown'
  const IPC_APPROVAL_LIMITS = { cost_control: 1000000, director: Infinity }
  const [rejectIpc,      setRejectIpc]      = useState(null)
  const [rejectReason,   setRejectReason]   = useState('')

  // ── BULK UPLOAD STATE ──────────────────────────────────────
  const [showUpload,     setShowUpload]     = useState(false)
  const [uploadRows,     setUploadRows]     = useState([])
  const [uploadError,    setUploadError]    = useState('')
  const [uploading,      setUploading]      = useState(false)
  const [uploadResult,   setUploadResult]   = useState(null)

  // ═══ Equipment IPC upload state ═══
  const eqFileRef = useRef()
  const [eqShowUpload,   setEqShowUpload]   = useState(false)
  const [eqUploadRows,   setEqUploadRows]   = useState([])
  const [eqUploadError,  setEqUploadError]  = useState('')
  const [eqUploading,    setEqUploading]    = useState(false)
  const [eqUploadResult, setEqUploadResult] = useState(null)

  // ── ADVANCED PAYMENT TRACKING ─────────────────────────────
  const [advPayByContract, setAdvPayByContract] = useState({})
  const [paidByContract,   setPaidByContract]   = useState({}) // contractId → total cash paid (non-adv)

  // ── EDIT IPC STATE ─────────────────────────────────────────
  const [showEditModal,  setShowEditModal]  = useState(false)
  const [editForm,       setEditForm]       = useState({})

  // ═══ Equipment IPC state (same ipc_certificates table, equipment_contract_id FK) ═══
  const [eqIpcs,          setEqIpcs]          = useState([])
  const [eqContracts,     setEqContracts]     = useState([])
  const [eqWizard,        setEqWizard]        = useState(null)
  const [eqContractSearch,setEqContractSearch]= useState('')
  const [eqSelectedIpc,   setEqSelectedIpc]   = useState(null)
  const [eqIpcDetailTab,  setEqIpcDetailTab]  = useState('lines')
  const [eqSaving,        setEqSaving]        = useState(false)
  const [eqFilterStatus,  setEqFilterStatus]  = useState('all')
  const [eqSearchText,    setEqSearchText]    = useState('')
  const [eqFilterContract,setEqFilterContract]= useState('all')
  const [eqStats,         setEqStats]         = useState({total:0,pending:0,certified:0,paid:0})
  const [eqRejectIpc,     setEqRejectIpc]     = useState(null)
  const [eqRejectReason,  setEqRejectReason]  = useState('')
  const [eqAdvPayByContract, setEqAdvPayByContract] = useState({})
  const [eqPaidByContract,   setEqPaidByContract]   = useState({})
  const [eqBulkDate,      setEqBulkDate]      = useState('')
  const [eqBulkUpdating,  setEqBulkUpdating]  = useState(false)
  const [eqFilterGross,   setEqFilterGross]   = useState('')
  const [eqFilterRet,     setEqFilterRet]     = useState('')
  const [eqFilterNet,     setEqFilterNet]     = useState('')
  const [eqFilterPO,      setEqFilterPO]      = useState('')
  const [eqShowEditModal, setEqShowEditModal] = useState(false)
  const [eqEditForm,      setEqEditForm]      = useState({})

  useEffect(()=>{ fetchProjects() },[])
  useEffect(()=>{ if(projectId) fetchAll(projectId) },[projectId])
  useEffect(()=>{ if(projectId) eqFetchAll(projectId) },[projectId])

  // Auto-open wizard when navigated here from Subcontracts "+ IPC" button
  useEffect(() => {
    if (!projectId) return
    let raw
    try { raw = localStorage.getItem('costspace_open_ipc_for_contract') } catch(e) { return }
    if (!raw) return
    let parsed
    try { parsed = JSON.parse(raw) } catch(e) { localStorage.removeItem('costspace_open_ipc_for_contract'); return }
    if (!parsed?.contractId || Date.now() - (parsed.ts||0) > 60000) {
      localStorage.removeItem('costspace_open_ipc_for_contract')
      return
    }
    // Try material contract first
    const contract = contracts.find(c => c.id === parsed.contractId)
    if (contract) {
      localStorage.removeItem('costspace_open_ipc_for_contract')
      setMainTab('material')
      openNewWizard().then(() => {
        setWizard(w => w ? ({ ...w, form: { ...w.form, subcontract_id: contract.id } }) : w)
      })
      return
    }
    // Otherwise try equipment contract
    const eqContract = eqContracts.find(c => c.id === parsed.contractId)
    if (eqContract) {
      localStorage.removeItem('costspace_open_ipc_for_contract')
      setMainTab('equipment')
      eqOpenNewWizard().then(() => {
        setEqWizard(w => w ? ({ ...w, form: { ...w.form, equipment_contract_id: eqContract.id } }) : w)
      })
    }
  }, [projectId, contracts, eqContracts])

  // ═══ Equipment IPC ═══
  async function eqFetchAll(pid) {
    await Promise.all([eqFetchIPCs(pid), eqFetchContracts(pid)])
    await eqFetchAdvPay(pid)
  }

  async function eqFetchIPCs(pid) {
    const id = pid||projectId; if(!id) return
    const { data,error } = await supabase
      .from('ipc_certificates')
      .select('*, equipment_contracts(contract_no,contractor_name,contract_value,retention_pct,advance_pct,adv_recovery_rate,vat_case,vat_pct,currency,exchange_rate_applied,po_id,rental_equipment_pos(po_number,po_type))')
      .eq('project_id',id).not('equipment_contract_id','is',null).order('created_at',{ascending:false})
    if(error){ console.error(error.message); return }
    const list = data||[]
    if (list.length) {
      const { data: dateRows } = await supabase
        .from('ipc_line_items').select('ipc_id, actual_cost_date').in('ipc_id', list.map(x=>x.id))
      const dateMap = {}
      ;(dateRows||[]).forEach(r => { if (r.actual_cost_date && !dateMap[r.ipc_id]) dateMap[r.ipc_id] = r.actual_cost_date })
      list.forEach(ipc => { ipc._actual_date = dateMap[ipc.id] || '' })

      const { data: dedRows } = await supabase
        .from('ipc_deductions').select('*').in('ipc_id', list.map(x=>x.id)).order('created_at')
      const dedMap = {}
      ;(dedRows||[]).forEach(r => { (dedMap[r.ipc_id] = dedMap[r.ipc_id]||[]).push(r) })
      list.forEach(ipc => { ipc._deductions = dedMap[ipc.id] || [] })
    }
    setEqIpcs(list); eqCalcStats(list)
  }

  async function eqUpdateActualDateForIpc(ipcId, newDate) {
    const { error } = await supabase.from('ipc_line_items').update({ actual_cost_date: newDate || null }).eq('ipc_id', ipcId)
    if (error) { alert('فشل تحديث التاريخ: ' + error.message); return }
    setEqIpcs(prev => prev.map(ipc => ipc.id === ipcId ? { ...ipc, _actual_date: newDate } : ipc))
    if (eqSelectedIpc?.id === ipcId) await eqFetchIpcLines(ipcId)
  }

  async function eqFetchContracts(pid) {
    const id = pid||projectId; if(!id) return
    const { data } = await supabase
      .from('equipment_contracts')
      .select('id,contract_no,contractor_name,contract_value,retention_pct,advance_pct,adv_recovery_rate,vat_case,vat_pct,currency,exchange_rate_applied,po_id,rental_equipment_pos(po_number,po_type)')
      .eq('project_id',id).eq('status','approved').order('contract_no')
    setEqContracts(data||[])
  }

  async function eqFetchAdvPay(pid) {
    const id = pid||projectId; if(!id) return
    const { data: cons } = await supabase
      .from('equipment_contracts')
      .select('id, po_id')
      .eq('project_id', id)
    const poToContract = {}
    ;(cons||[]).forEach(s => { if (s.po_id) poToContract[s.po_id] = s.id })

    const { data } = await supabase
      .from('cf_payment_entries')
      .select('id, amount_inc_vat, po_id, category')
      .eq('project_id', id)
    if (!data?.length) { setEqAdvPayByContract({}); return }

    const advPayments = data.filter(e => e.category === 'advanced_payment')
    const map = {}
    advPayments.forEach(e => {
      const contractId = e.po_id ? poToContract[e.po_id] : null
      if (contractId) map[contractId] = (map[contractId]||0) + (parseFloat(e.amount_inc_vat)||0)
    })
    setEqAdvPayByContract(map)

    // Stage 2 (Source of Truth unification): replaced local paidMap
    // building with shared matchPaymentsToContracts(). Old logic archived
    // separately (see project cleanup notes) after Stage 2 approval.
    // Note: eqAdvPayByContract (above) is a separate, distinct calculation
    // (advance payments tracked alone) and is intentionally untouched here.
    const paidMap = Object.fromEntries(matchPaymentsToContracts(cons, data))
    setEqPaidByContract(paidMap)
  }

  async function eqFetchPoLinesForContract(poId) {
    if(!poId) return []
    const { data } = await supabase
      .from('rental_equipment_po_line_items')
      .select('id,equipment_type,unit,quantity,rate,cost_code_id,cost_codes(id,code,description,parent_id,level)')
      .eq('po_id',poId).order('created_at')
    return data||[]
  }

  const [eqIpcLines, setEqIpcLines] = useState([])
  async function eqFetchIpcLines(ipcId) {
    const { data: lines } = await supabase
      .from('ipc_line_items')
      .select('id, ipc_id, po_line_item_id, cost_code_id, cost_codes(code,description,parent_id,level), sub_code, description, unit, boq_qty, unit_rate, this_period_qty, this_period_amount, actual_cost_date, equipment_id, created_at')
      .eq('ipc_id', ipcId).order('created_at')

    // Resolve Resource Code by looking up the Sub Code (sub_code text) in the tree and
    // checking its own parent: if that parent is level 4, it's the Resource Code.
    // (cost_code_id already points correctly to the real Cost Code — it is NOT itself
    // ever a Resource Code row, so we must not try to "climb" from it.)
    const subCodes = [...new Set((lines||[]).map(l => l.sub_code).filter(Boolean))]
    let subCodeTreeMap = {}
    let subCodeDescMap = {}
    if (subCodes.length) {
      // .eq('project_id', projectId) added — without it, a Sub Code
      // whose name happens to match one in a DIFFERENT project could be
      // returned here instead, misclassifying this line's Resource Type
      // (same class of risk the Mat-Dew1 investigation found, on a
      // different table). Confirmed project-scoped business rule: cost
      // codes are unique per project, not globally, so any lookup by
      // code alone must also filter by project_id.
      const { data: subRows } = await supabase.from('cost_codes').select('id,code,description,level,parent_id').eq('project_id', projectId).eq('level', 2).in('code', subCodes)
      const subParentIds = [...new Set((subRows||[]).map(s => s.parent_id).filter(Boolean))]
      let subParentMap = {}
      if (subParentIds.length) {
        // Safe by construction — no project_id filter needed here: these
        // ids only ever come from subRows above, which is now itself
        // project-scoped, so a uuid pointing at another project's row
        // cannot appear in subParentIds.
        const { data: parents } = await supabase.from('cost_codes').select('id,code,description,level').in('id', subParentIds)
        ;(parents||[]).forEach(p => { subParentMap[p.id] = p })
      }
      ;(subRows||[]).forEach(s => {
        subCodeDescMap[s.code] = s.description || null
        const parent = subParentMap[s.parent_id]
        if (parent && parseInt(parent.level) === 4) subCodeTreeMap[s.code] = parent
      })
    }

    const enriched = (lines||[]).map(l => ({
      ...l,
      sub_code_desc: l.sub_code ? (subCodeDescMap[l.sub_code] || null) : null,
      resource_code_info: l.sub_code ? (subCodeTreeMap[l.sub_code] || null) : null,
    }))
    setEqIpcLines(enriched)
    return enriched
  }

  async function eqUpdateIpcLineField(lineId, field, value) {
    setEqIpcLines(prev => prev.map(l => {
      if (l.id !== lineId) return l
      const updated = { ...l, [field]: value }
      if (field === 'this_period_qty') {
        const q = parseFloat(value) || 0
        updated.this_period_amount = +(q * (parseFloat(l.unit_rate)||0)).toFixed(2)
      }
      return updated
    }))
    const payload = { [field]: value }
    if (field === 'this_period_qty') {
      const line = eqIpcLines.find(l => l.id === lineId)
      const q = parseFloat(value) || 0
      payload.this_period_amount = +(q * (parseFloat(line?.unit_rate)||0)).toFixed(2)
    }
    await supabase.from('ipc_line_items').update(payload).eq('id', lineId)
    // Refresh IPC totals after a qty/amount change
    if (field === 'this_period_qty') {
      const { data: allLines } = await supabase.from('ipc_line_items').select('this_period_amount').eq('ipc_id', eqSelectedIpc.id)
      const gross = (allLines||[]).reduce((s,x)=>s+(parseFloat(x.this_period_amount)||0),0)
      await supabase.from('ipc_certificates').update({ gross_amount: gross }).eq('id', eqSelectedIpc.id)
    }
  }

  async function eqDeleteIpcLine(lineId, ipcId) {
    if (!window.confirm('حذف هذا البند من المستخلص؟')) return
    await supabase.from('ipc_line_items').delete().eq('id', lineId)
    await eqFetchIpcLines(ipcId)
    const { data: allLines } = await supabase.from('ipc_line_items').select('this_period_amount').eq('ipc_id', ipcId)
    const gross = (allLines||[]).reduce((s,x)=>s+(parseFloat(x.this_period_amount)||0),0)
    await supabase.from('ipc_certificates').update({ gross_amount: gross }).eq('id', ipcId)
  }

  async function eqAddIpcLine(ipcId) {
    // Let the user pick which PO line (sub_code) to duplicate as a new equipment-specific row
    const contract = eqContracts.find(c => c.id === eqSelectedIpc?.equipment_contract_id)
    const poLines = contract?.po_id ? await eqFetchPoLinesForContract(contract.po_id) : []
    if (!poLines.length) { alert('لا توجد بنود متاحة من الـPO المرتبط.'); return }
    const options = poLines.map((pl,i) => `${i+1}. ${pl.equipment_type}`).join('\n')
    const choice = prompt(`اختر رقم البند (Sub Code) اللي عايز تضيف منه صف جديد:\n${options}`)
    const idx = parseInt(choice) - 1
    if (isNaN(idx) || !poLines[idx]) return
    const pl = poLines[idx]
    const isResourceLevel = pl.cost_codes && parseInt(pl.cost_codes.level) !== 1 && pl.cost_codes.parent_id
    let realCostCodeId = pl.cost_code_id || pl.cost_codes?.id || null
    if (isResourceLevel) {
      const { data: parent } = await supabase.from('cost_codes').select('id').eq('id', pl.cost_codes.parent_id).single()
      realCostCodeId = parent?.id || realCostCodeId
    }
    const { error } = await supabase.from('ipc_line_items').insert({
      ipc_id: ipcId, po_line_item_id: pl.id, cost_code_id: realCostCodeId,
      sub_code: pl.equipment_type,
      description: pl.equipment_type, unit: pl.unit||'', unit_rate: parseFloat(pl.rate)||0,
      this_period_qty: 0, this_period_amount: 0, equipment_id: '',
    })
    if (error) { alert('فشل: ' + error.message); return }
    await eqFetchIpcLines(ipcId)
  }

  // ── FETCH ──────────────────────────────────────────────────
  async function applyBulkDateToLines(lineIds) {
    if (!bulkDate) return alert('Select a date first')
    if (!window.confirm('Update ' + lineIds.length + ' IPC line items to ' + bulkDate + '?')) return
    setBulkUpdating(true)
    const { error } = await supabase.from('ipc_line_items').update({ actual_cost_date: bulkDate }).in('id', lineIds)
    if (error) alert('Error: ' + error.message)
    else { fetchAll(projectId); alert('Updated ' + lineIds.length + ' lines') }
    setBulkUpdating(false)
  }

  async function fetchProjects() {
    const cached = getCached('projects_list')
    if(cached){ setProjects(cached); return }
    const { data } = await supabase.from('projects').select('id,name,currency,organizations(name)').order('name')
    setProjects(data||[]); if(data) setCached('projects_list',data)
  }

  async function fetchAll(pid) {
    setLoading(true)
    await Promise.all([fetchIPCs(pid), fetchContracts(pid)])
    await fetchAdvPay(pid)
    setLoading(false)
  }

  async function fetchIPCs(pid) {
    const id = pid||projectId; if(!id) return
    const { data,error } = await supabase
      .from('ipc_certificates')
      .select('*, subcontracts(contract_no,contractor_name,contract_value,retention_pct,advance_pct,adv_recovery_rate,vat_case,vat_pct,currency,exchange_rate_applied,po_id,logistics_type,purchase_orders(po_number,po_type))')
      .eq('project_id',id).is('equipment_contract_id', null).order('created_at',{ascending:false})
    if(error){ console.error(error.message); return }
    const list = data||[]
    if (list.length) {
      const { data: dateRows } = await supabase
        .from('ipc_line_items').select('ipc_id, actual_cost_date').in('ipc_id', list.map(x=>x.id))
      const dateMap = {}
      ;(dateRows||[]).forEach(r => { if (r.actual_cost_date && !dateMap[r.ipc_id]) dateMap[r.ipc_id] = r.actual_cost_date })
      list.forEach(ipc => { ipc._actual_date = dateMap[ipc.id] || '' })

      const { data: dedRows } = await supabase
        .from('ipc_deductions').select('*').in('ipc_id', list.map(x=>x.id)).order('created_at')
      const dedMap = {}
      ;(dedRows||[]).forEach(r => { (dedMap[r.ipc_id] = dedMap[r.ipc_id]||[]).push(r) })
      list.forEach(ipc => { ipc._deductions = dedMap[ipc.id] || [] })
    }
    setIpcs(list); calcStats(list)
  }

  async function updateActualDateForIpc(ipcId, newDate) {
    const { error } = await supabase.from('ipc_line_items').update({ actual_cost_date: newDate || null }).eq('ipc_id', ipcId)
    if (error) { alert('فشل تحديث التاريخ: ' + error.message); return }
    setIpcs(prev => prev.map(ipc => ipc.id === ipcId ? { ...ipc, _actual_date: newDate } : ipc))
    if (selectedIpc?.id === ipcId) await fetchIpcLines(ipcId)
  }

  async function fetchContracts(pid) {
    const id = pid||projectId; if(!id) return
    const { data } = await supabase
      .from('subcontracts')
      .select('id,contract_no,contractor_name,contract_value,retention_pct,advance_pct,adv_recovery_rate,vat_case,vat_pct,currency,exchange_rate_applied,po_id,logistics_type,purchase_orders(po_number,po_type)')
      .eq('project_id',id).eq('status','approved').order('contract_no')
    setContracts(data||[])
  }

  async function fetchAdvPay(pid) {
    const id = pid||projectId; if(!id) return
    // Get subcontracts map: po_id → subcontract.id
    const { data: subs } = await supabase
      .from('subcontracts')
      .select('id, po_id')
      .eq('project_id', id)
    const poToContract = {}
    ;(subs||[]).forEach(s => { if (s.po_id) poToContract[s.po_id] = s.id })

    // Get ALL payments for this project then filter client-side (avoid server-side category filter issues)
    const { data } = await supabase
      .from('cf_payment_entries')
      .select('id, amount_inc_vat, po_id, category')
      .eq('project_id', id)
    if (!data?.length) { setAdvPayByContract({}); return }

    const advPayments = data.filter(e => e.category === 'advanced_payment')
    console.log('[IPC] Advanced payments found:', advPayments.length, advPayments)

    const map = {}
    advPayments.forEach(e => {
      const contractId = e.po_id ? poToContract[e.po_id] : null
      console.log('[IPC] payment po_id:', e.po_id, '→ contractId:', contractId)
      if (contractId) {
        map[contractId] = (map[contractId]||0) + (parseFloat(e.amount_inc_vat)||0)
      }
    })
    console.log('[IPC] advPayByContract map:', JSON.stringify(map))
    setAdvPayByContract(map)

    // Stage 2b (Source of Truth unification): replaced local paidMap
    // building with shared matchPaymentsToContracts(). Old logic archived
    // separately (see project cleanup notes) after Stage 2 approval.
    // Note: advPayByContract (above) is a separate, distinct calculation
    // (advance payments tracked alone) and is intentionally untouched here.
    const paidMap = Object.fromEntries(matchPaymentsToContracts(subs, data))
    setPaidByContract(paidMap)
  }

  async function fetchPoLinesForContract(poId) {
    if(!poId) return []
    const { data } = await supabase
      .from('po_line_items')
      .select('id,description,unit,qty,unit_rate,ref_code,sub_code,cost_code_id,cost_codes(id,code,description,parent_id)')
      .eq('po_id',poId).order('created_at')
    return data||[]
  }

  async function fetchIpcLines(ipcId) {
    const { data: lines } = await supabase
      .from('ipc_line_items')
      .select('id, ipc_id, po_line_item_id, cost_code_id, cost_codes(code,description,parent_id), sub_code, description, unit, boq_qty, unit_rate, this_period_qty, this_period_amount, actual_cost_date, created_at')
      .eq('ipc_id', ipcId).order('created_at')
    if (!lines?.length) return []

    // Always fetch po_line_items to get sub_code (most reliable)
    const poLineIds = [...new Set(lines.map(l => l.po_line_item_id).filter(Boolean))]
    let poLinesMap = {}
    if (poLineIds.length) {
      const { data: poLines } = await supabase
        .from('po_line_items')
        .select('id, sub_code, description, unit, unit_rate, qty, cost_code_id, cost_codes(code,description,parent_id)')
        .in('id', poLineIds)
      ;(poLines || []).forEach(pl => { poLinesMap[pl.id] = pl })
    }

    // Resolve Resource Code by looking up the Sub Code (sub_code text) in the tree and
    // checking its own parent: if that parent is level 4, it's the Resource Code.
    // (cost_code_id already points correctly to the real Cost Code — it is NOT itself
    // ever a Resource Code row, so we must not try to "climb" from it.)
    const allSubCodes = [...new Set(lines.map(l => l.sub_code || poLinesMap[l.po_line_item_id]?.sub_code).filter(Boolean))]
    let subCodeTreeMap = {}
    let subCodeDescMap = {}
    if (allSubCodes.length) {
      // .eq('project_id', projectId) added — see the identical fix and
      // rationale at eqFetchIpcLines() above (same cross-project name
      // collision risk).
      const { data: subRows } = await supabase.from('cost_codes').select('id,code,description,level,parent_id').eq('project_id', projectId).eq('level', 2).in('code', allSubCodes)
      const subParentIds = [...new Set((subRows||[]).map(s => s.parent_id).filter(Boolean))]
      let subParentMap = {}
      if (subParentIds.length) {
        const { data: parents } = await supabase.from('cost_codes').select('id,code,description,level').in('id', subParentIds)
        ;(parents||[]).forEach(p => { subParentMap[p.id] = p })
      }
      ;(subRows||[]).forEach(s => {
        subCodeDescMap[s.code] = s.description || null
        const parent = subParentMap[s.parent_id]
        if (parent && parseInt(parent.level) === 4) subCodeTreeMap[s.code] = parent
      })
    }

    return lines.map(l => {
      const poLine = l.po_line_item_id ? poLinesMap[l.po_line_item_id] : null
      const resolvedSubCode = l.sub_code || poLine?.sub_code || null
      return {
        ...l,
        // sub_code: prefer DB column, fallback to po_line_items lookup
        sub_code: resolvedSubCode,
        sub_code_desc: resolvedSubCode ? (subCodeDescMap[resolvedSubCode] || null) : null,
        po_line_items: poLine || null,
        cost_codes: l.cost_codes || poLine?.cost_codes || null,
        resource_code_info: resolvedSubCode ? (subCodeTreeMap[resolvedSubCode] || null) : null,
      }
    })
  }

  function calcStats(data) {
    const pending   = data.filter(i=>['submitted','under_review'].includes(i.status)).length
    const certified = data.filter(i=>['approved','paid'].includes(i.status)).reduce((s,i)=>s+(parseFloat(i.gross_amount)||0),0)
    const paid      = data.filter(i=>i.status==='paid').reduce((s,i)=>s+(parseFloat(i.net_payable)||0),0)
    setStats({total:data.length,pending,certified,paid})
  }

  // ═══ Equipment IPC ═══
  function eqCalcStats(data) {
    const pending   = data.filter(i=>['submitted','under_review'].includes(i.status)).length
    const certified = data.filter(i=>['approved','paid'].includes(i.status)).reduce((s,i)=>s+(parseFloat(i.gross_amount)||0),0)
    const paid      = data.filter(i=>i.status==='paid').reduce((s,i)=>s+(parseFloat(i.net_payable)||0),0)
    setEqStats({total:data.length,pending,certified,paid})
  }

  // ── WIZARD: NEW IPC ────────────────────────────────────────
  async function openNewWizard() {
    if(!projectId){ alert('Select a project first'); return }
    setWizard({
      step: 1,
      form: {
        subcontract_id:'', ipc_number: null, // resolved once a contract is picked, in wizardStep1Next
        period_from:'', period_to:'',
        submission_date: today(), notes:'',
        retention_pct: '', advance_pct: '', is_final: false
      },
      poLines: [],
      lines: [],   // { po_line_item_id, description, unit, boq_qty, unit_rate, this_period_qty:'', this_period_amount:0 }
      deductions: []  // { deduction_type, description, amount }
    })
  }

  async function wizardStep1Next() {
    const { form } = wizard
    if(!form.subcontract_id){ alert('Select a contract'); return }
    if(!form.period_from||!form.period_to){ alert('حدد الفترة (من / إلى)'); return }
    const contract = contracts.find(c=>c.id===form.subcontract_id)
    const poLines = contract?.po_id ? await fetchPoLinesForContract(contract.po_id) : []
    if(!poLines.length){ alert('هذا العقد غير مرتبط بـ PO أو لا توجد بنود. لا يمكن إنشاء مستخلص تفصيلي.'); return }
    // IPC numbering is per-contract, not project-wide
    const { data: existingForContract } = await supabase
      .from('ipc_certificates').select('ipc_number').eq('subcontract_id', form.subcontract_id)
    const maxNo = (existingForContract||[]).reduce((m,x)=>Math.max(m,parseInt(x.ipc_number)||0),0)

    // Resolve Cost Code + Resource Code by looking up each line's Sub Code in the tree
    // directly (level=2 -> its parent). This is the single source of truth used everywhere.
    const subCodesHere = [...new Set(poLines.map(pl => pl.sub_code).filter(Boolean))]
    let subTreeMap = {}
    let subDescMap = {}
    if (subCodesHere.length) {
      // .eq('project_id', projectId) added — see the identical fix and
      // rationale at eqFetchIpcLines() above (same cross-project name
      // collision risk).
      const { data: subRows } = await supabase.from('cost_codes').select('id,code,description,level,parent_id').eq('project_id', projectId).eq('level', 2).in('code', subCodesHere)
      const parentIds = [...new Set((subRows||[]).map(s => s.parent_id).filter(Boolean))]
      let parentMap = {}
      if (parentIds.length) {
        // Safe by construction (see eqFetchIpcLines() above) — parentIds
        // and, transitively, grandparentIds below only ever derive from
        // the now project-scoped subRows.
        const { data: parents } = await supabase.from('cost_codes').select('id,code,description,level,parent_id').in('id', parentIds)
        ;(parents||[]).forEach(p => { parentMap[p.id] = p })
      }
      const grandparentIds = [...new Set(Object.values(parentMap).filter(p => parseInt(p.level) === 4).map(p => p.parent_id).filter(Boolean))]
      let grandparentMap = {}
      if (grandparentIds.length) {
        const { data: grandparents } = await supabase.from('cost_codes').select('id,code,description').in('id', grandparentIds)
        ;(grandparents||[]).forEach(g => { grandparentMap[g.id] = g })
      }
      ;(subRows||[]).forEach(s => {
        subDescMap[s.code] = s.description || null
        const parent = parentMap[s.parent_id]
        if (!parent) return
        if (parseInt(parent.level) === 4) {
          subTreeMap[s.code] = { costCode: grandparentMap[parent.parent_id] || null, resourceCode: parent }
        } else if (parseInt(parent.level) === 1) {
          subTreeMap[s.code] = { costCode: parent, resourceCode: null }
        }
      })
    }

    const lines = poLines.map(pl=>{
      const resolved = pl.sub_code ? subTreeMap[pl.sub_code] : null
      const realCostCode = resolved?.costCode || pl.cost_codes || null
      const resourceCode = resolved?.resourceCode || null
      return {
        po_line_item_id: pl.id,
        sub_code:        pl.sub_code || null,
        sub_code_desc:   pl.sub_code ? (subDescMap[pl.sub_code] || null) : null,
        cost_code_id:    realCostCode?.id || null,
        cost_code_label: realCostCode?.code || null,
        cost_code_desc:  realCostCode?.description || null,
        resource_code_label: resourceCode?.code || null,
        resource_code_desc:  resourceCode?.description || null,
        description:     pl.description,
        unit:            pl.unit||'',
        boq_qty:         parseFloat(pl.qty)||0,
        unit_rate:       parseFloat(pl.unit_rate)||0,
        this_period_qty: '',
        this_period_amount: 0,
        actual_cost_date: '2026-04-30',
      }
    })
    setWizard(w=>({...w, step:2, poLines, lines, form: {...w.form, ipc_number: maxNo+1}}))
  }

  function wizardUpdateQty(idx, qty) {
    const q = parseFloat(qty)||0
    setWizard(w=>{
      const lines = w.lines.map((l,i)=>
        i===idx ? {...l, this_period_qty: qty, this_period_amount: +(q * l.unit_rate).toFixed(2)} : l
      )
      return {...w, lines}
    })
  }

  // Phase 1 (IPC financial engine consolidation): this is now a thin wrapper.
  // It only resolves inputs from React state (wizard/contracts/ipcs) and hands
  // them to the centralized, pure computeIpcFinancials() in lib/ipcFinancials.js
  // — the SAME engine confirmIpcUpload()/eqConfirmIpcUpload()/eqWizardTotals()
  // now call too. No calculation logic lives in this function body anymore.
  // Extra fields not returned by computeIpcFinancials() (vatRate, retPct,
  // advRecoveryRate, vatCase, totalDeductions, contractValue) are inputs, not
  // calculated outputs — they are preserved here in the returned object
  // unchanged, since existing callers/screens read them off this function's
  // result (e.g. totals.retPct in the wizard summary view).
  function wizardTotals() {
    const lines = wizard?.lines||[]
    const form = wizard?.form
    const contract = contracts.find(c=>c.id===form?.subcontract_id)
    const gross  = lines.reduce((s,l)=>s+(parseFloat(l.this_period_amount)||0),0)
    const retPct = form?.retention_pct!=='' && form?.retention_pct!==undefined
      ? parseFloat(form.retention_pct)||0
      : parseFloat(contract?.retention_pct)||0
    const advRecoveryRate = parseFloat(contract?.adv_recovery_rate)||0
    const vatCase         = parseInt(contract?.vat_case)||1
    const vatRate         = (parseFloat(contract?.vat_pct) || 15) / 100
    const contractValue   = parseFloat(contract?.contract_value) || 0

    const prevRetentionHeld = ipcs
      .filter(i => i.subcontract_id === form?.subcontract_id && i.id !== form?.id && ['approved','paid'].includes(i.status))
      .reduce((s,i) => s + (parseFloat(i.retention_amount ?? i.retention_held)||0), 0)
    const prevCertifiedForRet = ipcs
      .filter(i => i.subcontract_id === form?.subcontract_id && i.id !== form?.id && ['approved','paid'].includes(i.status))
      .reduce((s,i) => s + (parseFloat(i.gross_amount)||0), 0)
    const realAdvPay = advPayByContract[form?.subcontract_id] || 0
    const advPct0     = parseFloat(contract?.advance_pct) || 0
    const prevRecoveries = ipcs
      .filter(i => i.subcontract_id === form?.subcontract_id)
      .reduce((s,i) => s + (parseFloat(i.advance_recovery)||0), 0)
    const totalDeductions = (wizard?.deductions||[]).reduce((s,d)=>s+(parseFloat(d.amount)||0),0)

    const result = computeIpcFinancials({
      gross, retPct, contractValue, isFinal: !!form?.is_final,
      prevRetentionHeld, prevCertifiedForRet,
      advRecoveryRate, realAdvPay, advPct0, prevRecoveries,
      vatCase, vatRate, totalDeductions,
    })

    return { ...result, vatRate, retPct, advRecoveryRate, vatCase, totalDeductions, contractValue, prevRecoveries, currency: contract?.currency || 'SAR' }
  }

  async function wizardSave(submitNow=false) {
    const { form, lines } = wizard
    const totals = wizardTotals()
    const periodSrc = form.period_from||form.period_to||today()
    setSaving(true)

    // 1. Insert IPC header
    const contract = contracts.find(c=>c.id===form.subcontract_id)
    const poType = contract?.purchase_orders?.po_type || null
    const { data:ipcData, error:ipcErr } = await supabase
      .from('ipc_certificates')
      .insert({
        project_id:        projectId,
        subcontract_id:    form.subcontract_id,
        ipc_number:        parseInt(form.ipc_number)||1,
        period_month:      periodSrc.substring(0,7)+'-01',
        period_from:       form.period_from||null,
        period_to:         form.period_to||null,
        submission_date:   form.submission_date||null,
        gross_amount:      totals.gross,
        vat_amount:        totals.vat,
        total_inc_vat:     totals.totalIncVat,
        retention_held:    totals.ret,
        retention_amount:  totals.ret,
        advance_recovery:  totals.advRecovery,
        advance_recovery_vat: totals.advRecoveryVat,
        total_adv_inc_vat: totals.totalAdvIncVat,
        advance_deduction: totals.advRecovery,
        net_exc_vat:       totals.gross,
        net_payable:       totals.net,
        status:            submitNow ? 'submitted' : 'draft',
        approval_stage:    submitNow ? 'cost_control' : 'draft',
        is_final:          !!form.is_final,
        notes:             form.notes||null,
        ipc_type:          'subcontractor',
        type_2:            poType,
      })
      .select().single()

    if(ipcErr){ alert('Error: '+ipcErr.message); setSaving(false); return }

    // 2. Insert line items (only lines with qty > 0)
    const linePayloads = lines
      .filter(l=>parseFloat(l.this_period_qty)>0)
      .map(l=>({
        ipc_id:             ipcData.id,
        po_line_item_id:    l.po_line_item_id,
        cost_code_id:       l.cost_code_id || null,
        sub_code:           l.sub_code || l.po_line_items?.sub_code || null,
        description:        l.description,
        unit:               l.unit,
        boq_qty:            l.boq_qty,
        unit_rate:          l.unit_rate,
        this_period_qty:    parseFloat(l.this_period_qty)||0,
        actual_cost_date:   l.actual_cost_date || '2026-04-30',
      }))

    if(linePayloads.length>0){
      const { error:lineErr } = await supabase.from('ipc_line_items').insert(linePayloads)
      if(lineErr){ alert('Lines error: '+lineErr.message); setSaving(false); return }
    }

    // 3. Insert deduction line items (only rows with amount > 0)
    const dedPayloads = (wizard.deductions||[])
      .filter(d=>parseFloat(d.amount)>0)
      .map(d=>({
        ipc_id:          ipcData.id,
        deduction_type:  d.deduction_type||'other',
        description:     d.description||null,
        amount:          parseFloat(d.amount)||0,
        created_by:      actorName,
      }))
    if(dedPayloads.length>0){
      const { error:dedErr } = await supabase.from('ipc_deductions').insert(dedPayloads)
      if(dedErr){ alert('Deductions error: '+dedErr.message); setSaving(false); return }
    }

    setSaving(false)
    setWizard(null)
    await fetchIPCs(projectId)
  }

  // ═══ Equipment IPC Wizard ═══
  async function eqOpenNewWizard() {
    if(!projectId){ alert('Select a project first'); return }
    setEqWizard({
      step: 1,
      form: {
        equipment_contract_id:'', ipc_number: null, // resolved once a contract is picked, in eqWizardStep1Next
        period_from:'', period_to:'',
        submission_date: today(), notes:'',
        retention_pct: '', advance_pct: '', is_final: false
      },
      poLines: [],
      lines: [],
      deductions: []
    })
  }

  async function eqWizardStep1Next() {
    const { form } = eqWizard
    if(!form.equipment_contract_id){ alert('Select a contract'); return }
    if(!form.period_from||!form.period_to){ alert('حدد الفترة (من / إلى)'); return }
    const contract = eqContracts.find(c=>c.id===form.equipment_contract_id)
    const poLines = contract?.po_id ? await eqFetchPoLinesForContract(contract.po_id) : []
    if(!poLines.length){ alert('هذا العقد غير مرتبط بـ PO أو لا توجد بنود. لا يمكن إنشاء مستخلص تفصيلي.'); return }
    // IPC numbering is per-contract, not project-wide
    const { data: existingForContract } = await supabase
      .from('ipc_certificates').select('ipc_number').eq('equipment_contract_id', form.equipment_contract_id)
    const maxNo = (existingForContract||[]).reduce((m,x)=>Math.max(m,parseInt(x.ipc_number)||0),0)

    // Resolve Cost Code + Resource Code by looking up each line's Sub Code (equipment_type)
    // in the tree directly (level=2 -> its parent). Single source of truth used everywhere.
    const eqSubCodesHere = [...new Set(poLines.map(pl => pl.equipment_type).filter(Boolean))]
    let eqSubTreeMap = {}
    let eqSubDescMap = {}
    if (eqSubCodesHere.length) {
      // .eq('project_id', projectId) added — see the identical fix and
      // rationale at eqFetchIpcLines() above (same cross-project name
      // collision risk).
      const { data: subRows } = await supabase.from('cost_codes').select('id,code,description,level,parent_id').eq('project_id', projectId).eq('level', 2).in('code', eqSubCodesHere)
      const parentIds = [...new Set((subRows||[]).map(s => s.parent_id).filter(Boolean))]
      let parentMap = {}
      if (parentIds.length) {
        // Safe by construction (see eqFetchIpcLines() above) — parentIds
        // and, transitively, grandparentIds below only ever derive from
        // the now project-scoped subRows.
        const { data: parents } = await supabase.from('cost_codes').select('id,code,description,level,parent_id').in('id', parentIds)
        ;(parents||[]).forEach(p => { parentMap[p.id] = p })
      }
      const grandparentIds = [...new Set(Object.values(parentMap).filter(p => parseInt(p.level) === 4).map(p => p.parent_id).filter(Boolean))]
      let grandparentMap = {}
      if (grandparentIds.length) {
        const { data: grandparents } = await supabase.from('cost_codes').select('id,code,description').in('id', grandparentIds)
        ;(grandparents||[]).forEach(g => { grandparentMap[g.id] = g })
      }
      ;(subRows||[]).forEach(s => {
        eqSubDescMap[s.code] = s.description || null
        const parent = parentMap[s.parent_id]
        if (!parent) return
        if (parseInt(parent.level) === 4) {
          eqSubTreeMap[s.code] = { costCode: grandparentMap[parent.parent_id] || null, resourceCode: parent }
        } else if (parseInt(parent.level) === 1) {
          eqSubTreeMap[s.code] = { costCode: parent, resourceCode: null }
        }
      })
    }

    const lines = poLines.map(pl=>{
      const resolved = pl.equipment_type ? eqSubTreeMap[pl.equipment_type] : null
      const realCostCode = resolved?.costCode || pl.cost_codes || null
      const resourceCode = resolved?.resourceCode || null
      return {
        po_line_item_id: pl.id,
        sub_code:        pl.equipment_type || null,
        sub_code_desc:   pl.equipment_type ? (eqSubDescMap[pl.equipment_type] || null) : null,
        cost_code_id:    realCostCode?.id || null,
        cost_code:       realCostCode?.code || '',
        cost_code_label: realCostCode?.code || null,
        cost_code_desc:  realCostCode?.description || null,
        resource_code_label: resourceCode?.code || null,
        resource_code_desc:  resourceCode?.description || null,
        description:     pl.equipment_type,
        unit:            pl.unit||'',
        boq_qty:         parseFloat(pl.quantity)||0,
        unit_rate:       parseFloat(pl.rate)||0,
        this_period_qty: '',
        this_period_amount: 0,
        actual_cost_date: '2026-04-30',
      }
    })
    setEqWizard(w=>({...w, step:2, poLines, lines, form: {...w.form, ipc_number: maxNo+1}}))
  }

  function eqWizardUpdateQty(idx, qty) {
    const q = parseFloat(qty)||0
    setEqWizard(w=>{
      const lines = w.lines.map((l,i)=>
        i===idx ? {...l, this_period_qty: qty, this_period_amount: +(q * l.unit_rate).toFixed(2)} : l
      )
      return {...w, lines}
    })
  }

  // Phase 1 (IPC financial engine consolidation): thin wrapper — same
  // pattern and same reasoning as wizardTotals() above. Calls the SAME
  // computeIpcFinancials() engine; only the data source differs
  // (eqContracts/eqIpcs/equipment_contract_id instead of
  // contracts/ipcs/subcontract_id) — confirmed the underlying formula was
  // already structurally identical to the material path before this change.
  function eqWizardTotals() {
    const lines = eqWizard?.lines||[]
    const form = eqWizard?.form
    const contract = eqContracts.find(c=>c.id===form?.equipment_contract_id)
    const gross  = lines.reduce((s,l)=>s+(parseFloat(l.this_period_amount)||0),0)
    const retPct = form?.retention_pct!=='' && form?.retention_pct!==undefined
      ? parseFloat(form.retention_pct)||0
      : parseFloat(contract?.retention_pct)||0
    const advRecoveryRate = parseFloat(contract?.adv_recovery_rate)||0
    const vatCase         = parseInt(contract?.vat_case)||1
    const vatRate         = (parseFloat(contract?.vat_pct) || 15) / 100
    const contractValue   = parseFloat(contract?.contract_value) || 0

    const prevRetentionHeld = eqIpcs
      .filter(i => i.equipment_contract_id === form?.equipment_contract_id && i.id !== form?.id && ['approved','paid'].includes(i.status))
      .reduce((s,i) => s + (parseFloat(i.retention_amount ?? i.retention_held)||0), 0)
    const prevCertifiedForRet = eqIpcs
      .filter(i => i.equipment_contract_id === form?.equipment_contract_id && i.id !== form?.id && ['approved','paid'].includes(i.status))
      .reduce((s,i) => s + (parseFloat(i.gross_amount)||0), 0)
    const realAdvPay = eqAdvPayByContract[form?.equipment_contract_id] || 0
    const advPct0     = parseFloat(contract?.advance_pct) || 0
    const prevRecoveries = eqIpcs
      .filter(i => i.equipment_contract_id === form?.equipment_contract_id)
      .reduce((s,i) => s + (parseFloat(i.advance_recovery)||0), 0)
    const totalDeductions = (eqWizard?.deductions||[]).reduce((s,d)=>s+(parseFloat(d.amount)||0),0)

    const result = computeIpcFinancials({
      gross, retPct, contractValue, isFinal: !!form?.is_final,
      prevRetentionHeld, prevCertifiedForRet,
      advRecoveryRate, realAdvPay, advPct0, prevRecoveries,
      vatCase, vatRate, totalDeductions,
    })

    return { ...result, vatRate, retPct, advRecoveryRate, vatCase, totalDeductions, contractValue }
  }

  async function eqWizardSave(submitNow=false) {
    const { form, lines } = eqWizard
    const totals = eqWizardTotals()
    const periodSrc = form.period_from||form.period_to||today()
    setEqSaving(true)

    const contract = eqContracts.find(c=>c.id===form.equipment_contract_id)
    const poType = contract?.rental_equipment_pos?.po_type || 'equipment'
    const { data:ipcData, error:ipcErr } = await supabase
      .from('ipc_certificates')
      .insert({
        project_id:        projectId,
        equipment_contract_id: form.equipment_contract_id,
        ipc_number:        parseInt(form.ipc_number)||1,
        period_month:      periodSrc.substring(0,7)+'-01',
        period_from:       form.period_from||null,
        period_to:         form.period_to||null,
        submission_date:   form.submission_date||null,
        gross_amount:      totals.gross,
        vat_amount:        totals.vat,
        total_inc_vat:     totals.totalIncVat,
        retention_held:    totals.ret,
        retention_amount:  totals.ret,
        advance_recovery:  totals.advRecovery,
        advance_recovery_vat: totals.advRecoveryVat,
        total_adv_inc_vat: totals.totalAdvIncVat,
        advance_deduction: totals.advRecovery,
        net_exc_vat:       totals.gross,
        net_payable:       totals.net,
        status:            submitNow ? 'submitted' : 'draft',
        approval_stage:    submitNow ? 'cost_control' : 'draft',
        is_final:          !!form.is_final,
        notes:             form.notes||null,
        ipc_type:          'equipment',
        type_2:            poType,
      })
      .select().single()

    if(ipcErr){ alert('Error: '+ipcErr.message); setEqSaving(false); return }

    const linePayloads = lines
      .filter(l=>parseFloat(l.this_period_qty)>0)
      .map(l=>({
        ipc_id:             ipcData.id,
        po_line_item_id:    l.po_line_item_id,
        cost_code_id:       l.cost_code_id || null,
        sub_code:           l.description || null,
        description:        l.description,
        unit:               l.unit,
        boq_qty:            l.boq_qty,
        unit_rate:          l.unit_rate,
        this_period_qty:    parseFloat(l.this_period_qty)||0,
        actual_cost_date:   l.actual_cost_date || '2026-04-30',
      }))

    if(linePayloads.length>0){
      const { error:lineErr } = await supabase.from('ipc_line_items').insert(linePayloads)
      if(lineErr){ alert('Lines error: '+lineErr.message); setEqSaving(false); return }
    }

    const dedPayloads = (eqWizard.deductions||[])
      .filter(d=>parseFloat(d.amount)>0)
      .map(d=>({
        ipc_id:          ipcData.id,
        deduction_type:  d.deduction_type||'other',
        description:     d.description||null,
        amount:          parseFloat(d.amount)||0,
        created_by:      actorName,
      }))
    if(dedPayloads.length>0){
      const { error:dedErr } = await supabase.from('ipc_deductions').insert(dedPayloads)
      if(dedErr){ alert('Deductions error: '+dedErr.message); setEqSaving(false); return }
    }

    setEqSaving(false)
    setEqWizard(null)
    await eqFetchIPCs(projectId)
  }

  // ── VIEW EXISTING IPC ──────────────────────────────────────
  function computeIpcTotals(ipc) {
    const gross           = parseFloat(ipc.gross_amount)||0
    const vatRate          = (parseFloat(ipc.subcontracts?.vat_pct) || 15) / 100
    const vat              = +(gross*vatRate).toFixed(2)
    const totalIncVat      = +(gross+vat).toFixed(2)
    const ret              = parseFloat(ipc.retention_amount||ipc.retention_held)||0

    const contractId       = ipc.subcontract_id
    const realAdvPay       = advPayByContract[contractId]||0
    const hasRealAdvPayment = realAdvPay > 0
    const contractValue0   = parseFloat(ipc.subcontracts?.contract_value) || 0
    const advPct0          = parseFloat(ipc.subcontracts?.advance_pct) || 0
    const totalAdvPay      = hasRealAdvPayment ? realAdvPay : +(contractValue0 * advPct0 / 100).toFixed(2)
    const advRecoveryRate  = parseFloat(ipc.subcontracts?.adv_recovery_rate)||0
    const vatCase          = parseInt(ipc.subcontracts?.vat_case)||1

    const prevRecoveries  = ipcs
      .filter(i => i.subcontract_id===contractId && i.id!==ipc.id)
      .reduce((s,i)=>s+(parseFloat(i.advance_recovery)||0),0)

    // Advance recovery = Gross Invoice x recovery rate — computed, but never stored value overrides once a real advance payment exists.
    const computedAdvRecovery = +(gross * advRecoveryRate / 100).toFixed(2)
    const advRecovery     = ipc.advance_recovery != null ? parseFloat(ipc.advance_recovery) : computedAdvRecovery
    const outstanding     = Math.max(0, totalAdvPay - prevRecoveries - advRecovery)

    const otherDeductions = (ipc._deductions||[]).reduce((s,d)=>s+(parseFloat(d.amount)||0),0)

    // Contract Value Ceiling info for the view screen
    const contractValue  = contractValue0
    const prevCertified  = ipcs
      .filter(i => i.subcontract_id===contractId && i.id!==ipc.id && ['approved','paid'].includes(i.status))
      .reduce((s,i) => s + (parseFloat(i.gross_amount)||0), 0)
    const cumulativeCertified    = +(prevCertified + gross).toFixed(2)
    const remainingContractValue = +(contractValue - prevCertified).toFixed(2)
    const overContractValue      = contractValue > 0 && cumulativeCertified > contractValue
    const retPct                 = parseFloat(ipc.subcontracts?.retention_pct) || 0
    const prevRetentionHeld      = ipcs
      .filter(i => i.subcontract_id===contractId && i.id!==ipc.id && ['approved','paid'].includes(i.status))
      .reduce((s,i) => s + (parseFloat(i.retention_amount ?? i.retention_held)||0), 0)
    const cumulativeRetentionHeld = +(prevRetentionHeld + ret).toFixed(2)

    let advRecoveryVat = 0, totalAdvIncVat = advRecovery, net
    if (vatCase === 2) {
      advRecoveryVat  = ipc.adv_recovery_vat != null ? parseFloat(ipc.adv_recovery_vat) : +(advRecovery*vatRate).toFixed(2)
      totalAdvIncVat  = +(advRecovery+advRecoveryVat).toFixed(2)
      net             = +(totalIncVat - ret - advRecovery - advRecoveryVat - otherDeductions).toFixed(2)
    } else {
      net             = +(totalIncVat - ret - advRecovery - otherDeductions).toFixed(2)
    }

    return { gross, vat, vatRate, totalIncVat, ret, retPct, prevRetentionHeld, cumulativeRetentionHeld, advRecovery, advRecoveryVat, totalAdvIncVat, net, totalAdvPay, prevRecoveries, outstanding, otherDeductions, vatCase, advRecoveryRate, hasRealAdvPayment, contractValue, prevCertified, cumulativeCertified, remainingContractValue, overContractValue, currency: ipc.subcontracts?.currency || 'SAR' }
  }

  function computeEqIpcTotals(ipc) {
    const gross           = parseFloat(ipc.gross_amount)||0
    const vatRate          = (parseFloat(ipc.equipment_contracts?.vat_pct) || 15) / 100
    const vat              = +(gross*vatRate).toFixed(2)
    const totalIncVat      = +(gross+vat).toFixed(2)
    const ret              = parseFloat(ipc.retention_amount||ipc.retention_held)||0

    const contractId       = ipc.equipment_contract_id
    const realAdvPay       = eqAdvPayByContract[contractId]||0
    const hasRealAdvPayment = realAdvPay > 0
    const contractValue0   = parseFloat(ipc.equipment_contracts?.contract_value) || 0
    const advPct0          = parseFloat(ipc.equipment_contracts?.advance_pct) || 0
    const totalAdvPay      = hasRealAdvPayment ? realAdvPay : +(contractValue0 * advPct0 / 100).toFixed(2)
    const advRecoveryRate  = parseFloat(ipc.equipment_contracts?.adv_recovery_rate)||0
    const vatCase          = parseInt(ipc.equipment_contracts?.vat_case)||1

    const prevRecoveries  = eqIpcs
      .filter(i => i.equipment_contract_id===contractId && i.id!==ipc.id)
      .reduce((s,i)=>s+(parseFloat(i.advance_recovery)||0),0)

    const computedAdvRecovery = +(gross * advRecoveryRate / 100).toFixed(2)
    const advRecovery     = ipc.advance_recovery != null ? parseFloat(ipc.advance_recovery) : computedAdvRecovery
    const outstanding     = Math.max(0, totalAdvPay - prevRecoveries - advRecovery)

    const otherDeductions = (ipc._deductions||[]).reduce((s,d)=>s+(parseFloat(d.amount)||0),0)

    // Contract Value Ceiling info for the view screen
    const contractValue  = contractValue0
    const prevCertified  = eqIpcs
      .filter(i => i.equipment_contract_id===contractId && i.id!==ipc.id && ['approved','paid'].includes(i.status))
      .reduce((s,i) => s + (parseFloat(i.gross_amount)||0), 0)
    const cumulativeCertified    = +(prevCertified + gross).toFixed(2)
    const remainingContractValue = +(contractValue - prevCertified).toFixed(2)
    const overContractValue      = contractValue > 0 && cumulativeCertified > contractValue
    const retPct                 = parseFloat(ipc.equipment_contracts?.retention_pct) || 0
    const prevRetentionHeld      = eqIpcs
      .filter(i => i.equipment_contract_id===contractId && i.id!==ipc.id && ['approved','paid'].includes(i.status))
      .reduce((s,i) => s + (parseFloat(i.retention_amount ?? i.retention_held)||0), 0)
    const cumulativeRetentionHeld = +(prevRetentionHeld + ret).toFixed(2)

    let advRecoveryVat = 0, totalAdvIncVat = advRecovery, net
    if (vatCase === 2) {
      advRecoveryVat  = ipc.adv_recovery_vat != null ? parseFloat(ipc.adv_recovery_vat) : +(advRecovery*vatRate).toFixed(2)
      totalAdvIncVat  = +(advRecovery+advRecoveryVat).toFixed(2)
      net             = +(totalIncVat - ret - advRecovery - advRecoveryVat - otherDeductions).toFixed(2)
    } else {
      net             = +(totalIncVat - ret - advRecovery - otherDeductions).toFixed(2)
    }

    return { gross, vat, vatRate, totalIncVat, ret, retPct, prevRetentionHeld, cumulativeRetentionHeld, advRecovery, advRecoveryVat, totalAdvIncVat, net, totalAdvPay, prevRecoveries, outstanding, otherDeductions, vatCase, advRecoveryRate, hasRealAdvPayment, contractValue, prevCertified, cumulativeCertified, remainingContractValue, overContractValue, currency: ipc.equipment_contracts?.currency || 'SAR' }
  }

  async function openDetail(ipc) {
    setIpcDetailTab('lines')
    setSelectedIpc(ipc)
    const [lines, poLines] = await Promise.all([
      fetchIpcLines(ipc.id),
      ipc.subcontracts?.po_id ? fetchPoLinesForContract(ipc.subcontracts.po_id) : Promise.resolve([])
    ])
    setSelectedLines(lines)
    setSelectedPoLines(poLines)
  }

  async function eqOpenDetail(ipc) {
    setEqIpcDetailTab('lines')
    setEqSelectedIpc(ipc)
    const [lines, poLines] = await Promise.all([
      eqFetchIpcLines(ipc.id),
      ipc.equipment_contracts?.po_id ? eqFetchPoLinesForContract(ipc.equipment_contracts.po_id) : Promise.resolve([])
    ])
    setEqSelectedPoLines(poLines)
  }

  async function updateExistingLine(lineId, qty) {
    const line = selectedLines.find(l=>l.id===lineId); if(!line) return
    const q = parseFloat(qty)||0
    const amount = +(q * (parseFloat(line.unit_rate)||0)).toFixed(2)
    await supabase.from('ipc_line_items').update({ this_period_qty:q }).eq('id',lineId)
    // re-fetch to get generated this_period_amount
    const updatedLines = await fetchIpcLines(selectedIpc.id)
    setSelectedLines(updatedLines)
    const newLines = updatedLines
    const contract    = contracts.find(c=>c.id===selectedIpc.subcontract_id)
    const gross       = newLines.reduce((s,l)=>s+(parseFloat(l.this_period_amount)||0),0)
    const retPct      = parseFloat(contract?.retention_pct)||0
    const advRecoveryRate = parseFloat(contract?.adv_recovery_rate)||0
    const vatCase     = parseInt(contract?.vat_case)||1
    const vatRate     = (parseFloat(contract?.vat_pct) || 15) / 100
    const ret         = +(gross * retPct / 100).toFixed(2)
    const vat         = +(gross * vatRate).toFixed(2)
    const totalIncVat = +(gross + vat).toFixed(2)
    const realAdvPay  = advPayByContract[selectedIpc.subcontract_id]||0
    const contractValue0 = parseFloat(contract?.contract_value) || 0
    const advPct0     = parseFloat(contract?.advance_pct) || 0
    const totalAdvPay = realAdvPay > 0 ? realAdvPay : +(contractValue0 * advPct0 / 100).toFixed(2)
    const prevRecoveries = ipcs
      .filter(i=>i.subcontract_id===selectedIpc.subcontract_id&&i.id!==selectedIpc.id)
      .reduce((s,i)=>s+(parseFloat(i.advance_recovery)||0),0)
    const outstanding    = Math.max(0, totalAdvPay - prevRecoveries)
    const rawRecovery    = +(gross * advRecoveryRate / 100).toFixed(2)
    const advRecovery    = +Math.min(rawRecovery, outstanding).toFixed(2)
    let advRecoveryVat = 0, totalAdvIncVat = advRecovery, net
    if (vatCase === 2) {
      advRecoveryVat  = +(advRecovery * vatRate).toFixed(2)
      totalAdvIncVat  = +(advRecovery + advRecoveryVat).toFixed(2)
      net             = +(totalIncVat - ret - advRecovery - advRecoveryVat).toFixed(2)
    } else {
      net             = +(totalIncVat - ret - advRecovery).toFixed(2)
    }
    await supabase.from('ipc_certificates').update({
      gross_amount: gross, vat_amount: vat, total_inc_vat: totalIncVat,
      retention_held: ret, retention_amount: ret,
      advance_recovery: advRecovery, advance_recovery_vat: advRecoveryVat,
      total_adv_inc_vat: totalAdvIncVat,
      advance_deduction: advRecovery, net_exc_vat: gross, net_payable: net
    }).eq('id', selectedIpc.id)
    setSelectedIpc(p=>({...p, gross_amount:gross, vat_amount:vat, total_inc_vat:totalIncVat, retention_amount:ret, advance_recovery:advRecovery, advance_recovery_vat:advRecoveryVat, total_adv_inc_vat:totalAdvIncVat, net_payable:net}))
    fetchIPCs(projectId)
  }

  async function addLineToExisting(poLine) {
    if(selectedLines.find(l=>l.po_line_item_id===poLine.id)){ alert('البند موجود بالفعل'); return }
    const { error } = await supabase.from('ipc_line_items').insert({
      ipc_id:             selectedIpc.id,
      po_line_item_id:    poLine.id,
      description:        poLine.description,
      unit:               poLine.unit||'',
      boq_qty:            parseFloat(poLine.qty)||0,
      unit_rate:          parseFloat(poLine.unit_rate)||0,
      this_period_qty:    0,
    })
    if(error){ alert('Error: '+error.message); return }
    const lines = await fetchIpcLines(selectedIpc.id)
    setSelectedLines(lines)
  }

  async function deleteExistingLine(id) {
    await supabase.from('ipc_line_items').delete().eq('id',id)
    const lines = await fetchIpcLines(selectedIpc.id)
    setSelectedLines(lines)
    // recalc header
    const contract = contracts.find(c=>c.id===selectedIpc.subcontract_id)
    const gross = lines.reduce((s,l)=>s+(parseFloat(l.this_period_amount)||0),0)
    const ret   = gross * (parseFloat(contract?.retention_pct)||0) / 100
    const adv   = gross * (parseFloat(contract?.advance_pct)||0) / 100
    const net   = gross - ret - adv
    await supabase.from('ipc_certificates').update({
      gross_amount:gross, retention_held:ret, retention_amount:ret,
      advance_deduction:adv, net_exc_vat:gross, net_payable:net
    }).eq('id',selectedIpc.id)
    setSelectedIpc(p=>({...p,gross_amount:gross,retention_amount:ret,advance_deduction:adv,net_payable:net}))
    fetchIPCs(projectId)
  }

  async function eqUpdateExistingLine(lineId, qty) {
    const line = eqIpcLines.find(l=>l.id===lineId); if(!line) return
    const q = parseFloat(qty)||0
    await supabase.from('ipc_line_items').update({ this_period_qty:q }).eq('id',lineId)
    const updatedLines = await eqFetchIpcLines(eqSelectedIpc.id)
    const contract    = eqContracts.find(c=>c.id===eqSelectedIpc.equipment_contract_id)
    const gross       = updatedLines.reduce((s,l)=>s+(parseFloat(l.this_period_amount)||0),0)
    const retPct      = parseFloat(contract?.retention_pct)||0
    const advRecoveryRate = parseFloat(contract?.adv_recovery_rate)||0
    const vatCase     = parseInt(contract?.vat_case)||1
    const vatRate     = (parseFloat(contract?.vat_pct) || 15) / 100
    const ret         = +(gross * retPct / 100).toFixed(2)
    const vat         = +(gross * vatRate).toFixed(2)
    const totalIncVat = +(gross + vat).toFixed(2)
    const realAdvPay  = eqAdvPayByContract[eqSelectedIpc.equipment_contract_id]||0
    const contractValue0 = parseFloat(contract?.contract_value) || 0
    const advPct0     = parseFloat(contract?.advance_pct) || 0
    const totalAdvPay = realAdvPay > 0 ? realAdvPay : +(contractValue0 * advPct0 / 100).toFixed(2)
    const prevRecoveries = eqIpcs
      .filter(i=>i.equipment_contract_id===eqSelectedIpc.equipment_contract_id&&i.id!==eqSelectedIpc.id)
      .reduce((s,i)=>s+(parseFloat(i.advance_recovery)||0),0)
    const outstanding    = Math.max(0, totalAdvPay - prevRecoveries)
    const rawRecovery    = +(gross * advRecoveryRate / 100).toFixed(2)
    const advRecovery    = +Math.min(rawRecovery, outstanding).toFixed(2)
    let advRecoveryVat = 0, totalAdvIncVat = advRecovery, net
    if (vatCase === 2) {
      advRecoveryVat  = +(advRecovery * vatRate).toFixed(2)
      totalAdvIncVat  = +(advRecovery + advRecoveryVat).toFixed(2)
      net             = +(totalIncVat - ret - advRecovery - advRecoveryVat).toFixed(2)
    } else {
      net             = +(totalIncVat - ret - advRecovery).toFixed(2)
    }
    await supabase.from('ipc_certificates').update({
      gross_amount: gross, vat_amount: vat, total_inc_vat: totalIncVat,
      retention_held: ret, retention_amount: ret,
      advance_recovery: advRecovery, advance_recovery_vat: advRecoveryVat,
      total_adv_inc_vat: totalAdvIncVat,
      advance_deduction: advRecovery, net_exc_vat: gross, net_payable: net
    }).eq('id', eqSelectedIpc.id)
    setEqSelectedIpc(p=>({...p, gross_amount:gross, vat_amount:vat, total_inc_vat:totalIncVat, retention_amount:ret, advance_recovery:advRecovery, advance_recovery_vat:advRecoveryVat, total_adv_inc_vat:totalAdvIncVat, net_payable:net}))
    eqFetchIPCs(projectId)
  }

  async function eqAddLineToExisting(poLine) {
    if(eqIpcLines.find(l=>l.po_line_item_id===poLine.id)){ alert('البند موجود بالفعل'); return }
    const isResourceLevel = poLine.cost_codes && parseInt(poLine.cost_codes.level) !== 1 && poLine.cost_codes.parent_id
    let realCostCodeId = poLine.cost_code_id || poLine.cost_codes?.id || null
    if (isResourceLevel) {
      const { data: parent } = await supabase.from('cost_codes').select('id').eq('id', poLine.cost_codes.parent_id).single()
      realCostCodeId = parent?.id || realCostCodeId
    }
    const { error } = await supabase.from('ipc_line_items').insert({
      ipc_id:             eqSelectedIpc.id,
      po_line_item_id:    poLine.id,
      cost_code_id:       realCostCodeId,
      sub_code:           poLine.equipment_type,
      description:        poLine.equipment_type,
      unit:               poLine.unit||'',
      boq_qty:            parseFloat(poLine.quantity)||0,
      unit_rate:          parseFloat(poLine.rate)||0,
      this_period_qty:    0,
      equipment_id:       '',
    })
    if(error){ alert('Error: '+error.message); return }
    await eqFetchIpcLines(eqSelectedIpc.id)
  }

  async function eqDeleteExistingLine(id) {
    await supabase.from('ipc_line_items').delete().eq('id',id)
    const lines = await eqFetchIpcLines(eqSelectedIpc.id)
    const contract = eqContracts.find(c=>c.id===eqSelectedIpc.equipment_contract_id)
    const gross = lines.reduce((s,l)=>s+(parseFloat(l.this_period_amount)||0),0)
    const ret   = gross * (parseFloat(contract?.retention_pct)||0) / 100
    const adv   = gross * (parseFloat(contract?.advance_pct)||0) / 100
    const net   = gross - ret - adv
    await supabase.from('ipc_certificates').update({
      gross_amount:gross, retention_held:ret, retention_amount:ret,
      advance_deduction:adv, net_exc_vat:gross, net_payable:net
    }).eq('id',eqSelectedIpc.id)
    setEqSelectedIpc(p=>({...p,gross_amount:gross,retention_amount:ret,advance_deduction:adv,net_payable:net}))
    eqFetchIPCs(projectId)
  }

  const getIpcRequiredApprover = (amount) => amount <= IPC_APPROVAL_LIMITS.cost_control ? 'cost_control' : 'director'

  async function handleIpcApproval(ipc, action, reason = '') {
    const stage = ipc.approval_stage || 'draft'
    const amount = parseFloat(ipc.gross_amount) || 0
    const requiredApprover = getIpcRequiredApprover(amount)
    let update = {}
    if (action === 'submit' || action === 'resubmit') {
      update = { approval_stage: 'cost_control', status: 'submitted', rejection_reason: null }
    } else if (action === 'approve') {
      if (stage === 'cost_control') {
        if (requiredApprover === 'cost_control') {
          update = { approval_stage: 'approved', status: 'approved', approved_date: today(), cc_approved_by: actorName, cc_approved_at: new Date().toISOString() }
        } else {
          update = { approval_stage: 'director', cc_approved_by: actorName, cc_approved_at: new Date().toISOString() }
        }
      } else if (stage === 'director') {
        update = { approval_stage: 'approved', status: 'approved', approved_date: today(), dir_approved_by: actorName, dir_approved_at: new Date().toISOString() }
      }
    } else if (action === 'reject') {
      update = { approval_stage: 'rejected', status: 'rejected', rejection_reason: reason }
    } else if (action === 'revoke') {
      // Phase 3 (Revoked lifecycle): a revoke NO LONGER resets to 'draft'.
      // 'draft' means "never yet submitted"; 'revoked' means "was once
      // approved, then reversed" -- these are different histories and the
      // DB's ipc_delete_guard trigger relies on this exact distinction to
      // make a revoked IPC permanently undeletable while still correctable
      // (draft remains deletable, revoked never is). This mirrors the DB
      // trigger's already-live behavior -- see enforce_ipc_delete_restriction().
      //
      // Final IPC extra confirmation: revoking an approved Final IPC
      // reopens the subcontract for new IPCs (prevent_ipc_after_final()
      // only blocks while an approved/paid Final exists), which is a
      // materially bigger decision than an ordinary revoke -- require an
      // explicit director-tier confirmation regardless of who originally
      // approved it, before proceeding.
      if (ipc.is_final) {
        const confirmed = window.confirm(
          'تحذير: هذا مستخلص نهائي (Final IPC).\n' +
          'التراجع عن اعتماده سيعيد فتح العقد للسماح بمستخلصات جديدة.\n' +
          'هذا القرار يتطلب تأكيد على مستوى Director.\n\n' +
          'هل أنت متأكد من المتابعة؟'
        )
        if (!confirmed) return
        if (!can('IPC.ApproveDirector')) {
          alert('التراجع عن اعتماد مستخلص نهائي (Final IPC) يتطلب دور Director.')
          return
        }
      }
      update = { approval_stage: 'revoked', status: 'revoked', cc_approved_by: null, cc_approved_at: null, dir_approved_by: null, dir_approved_at: null, rejection_reason: null, approved_date: null }
    }
    if (!Object.keys(update).length) return
    const { error, data: updRows } = await supabase.from('ipc_certificates').update(update).eq('id', ipc.id).select('id')
    if (error) { alert('فشل تنفيذ العملية: ' + error.message); return }
    if (!updRows || updRows.length === 0) { alert('لم يتم تحديث أي صف — تحقق من صلاحيات RLS أو من أن المستخلص لا يزال موجودًا.'); return }
    await logAudit(ipc.id, 'ipc_certificate', action, actorName, reason, stage, update.approval_stage || stage, projectId)
    fetchIPCs(projectId)
    if (selectedIpc?.id === ipc.id) setSelectedIpc(p => ({ ...p, ...update }))
  }

  // ═══ Phase 3: Bulk Approval — Validate All -> Approve All ═══════════
  // Design (per approved plan): validation happens for EVERY selected IPC
  // BEFORE any write begins. If any single IPC fails ANY check, the
  // ENTIRE batch is rejected -- nothing is written. No partial approval.
  // This function only VALIDATES (read-only, no writes) -- see
  // runBulkApprove() below for the actual write phase, which only runs
  // if this returns allValid: true.
  function validateBulkSelection(selectedIds, allIpcs) {
    const selected = allIpcs.filter(i => selectedIds.has(i.id))
    const results = selected.map(ipc => {
      const stage = ipc.approval_stage || 'draft'
      const amount = parseFloat(ipc.gross_amount) || 0
      const requiredApprover = getIpcRequiredApprover(amount)

      // Check 1: must currently be at a stage this user's role can approve
      if (stage === 'cost_control' && !can('IPC.ApproveCostControl')) {
        return { id: ipc.id, ipc_number: ipc.ipc_number, ok: false, reason: 'يتطلب دور Cost Control للاعتماد في هذه المرحلة' }
      }
      if (stage === 'director' && !can('IPC.ApproveDirector')) {
        return { id: ipc.id, ipc_number: ipc.ipc_number, ok: false, reason: 'يتطلب دور Director للاعتماد في هذه المرحلة' }
      }
      if (!['cost_control','director'].includes(stage)) {
        return { id: ipc.id, ipc_number: ipc.ipc_number, ok: false, reason: `الحالة الحالية "${stage}" غير قابلة للاعتماد` }
      }
      // If this is a cost_control-stage IPC that actually requires
      // director approval (amount > threshold), a cost_control approval
      // would only move it to 'director', not fully approve it -- flag
      // this explicitly so the batch summary is accurate, not misleading.
      if (stage === 'cost_control' && requiredApprover === 'director') {
        return { id: ipc.id, ipc_number: ipc.ipc_number, ok: false, reason: 'المبلغ يتجاوز حد Cost Control ويتطلب اعتماد Director أولاً — لا يمكن اعتماده بالكامل في هذه الدفعة' }
      }

      // Check 2: Sequential Approval -- mirrors the DB trigger's own logic
      // client-side, so the validation step can show a clear per-row
      // reason BEFORE attempting any write (the DB trigger is still the
      // real enforcement; this is a preview, not a replacement).
      //
      // Bugfix: a blocking IPC that is ITSELF part of the current batch
      // must not count as a blocker -- it will be approved in the same
      // batch (runBulkApprove() processes selected IPCs in ipc_number
      // order via the array's natural order from the sort below, and the
      // real DB trigger re-checks status at each individual write, so by
      // the time IPC N's UPDATE runs, IPC N-1's UPDATE from earlier in
      // the same loop has already committed and satisfies the DB-side
      // check too). Without this exclusion, selecting IPC 1, 2, and 3
      // together was being rejected outright, because 2 saw 1 as "not yet
      // approved" and 3 saw 2 as "not yet approved" -- even though both
      // were included in the very batch being validated. Confirmed by
      // direct UI testing.
      if (ipc.ipc_number > 0) {
        const contractKey = ipc.subcontract_id ? 'subcontract_id' : 'equipment_contract_id'
        const contractId = ipc[contractKey]
        const blocking = allIpcs.find(other =>
          other[contractKey] === contractId &&
          other.ipc_number > 0 &&
          other.ipc_number < ipc.ipc_number &&
          !['approved','paid'].includes(other.status) &&
          other.id !== ipc.id &&
          !selectedIds.has(other.id)
        )
        if (blocking) {
          return { id: ipc.id, ipc_number: ipc.ipc_number, ok: false, reason: `IPC رقم ${blocking.ipc_number} لنفس العقد لسه حالته "${blocking.status}" ولازم يتحل الأول` }
        }
      }

      return { id: ipc.id, ipc_number: ipc.ipc_number, ok: true, reason: null }
    })
    return { allValid: results.every(r => r.ok), results }
  }

  // Write phase -- only reached after validateBulkSelection() returned
  // allValid: true. Loops the existing single-approval path
  // (handleIpcApproval) per IPC rather than a new bulk DB operation, per
  // the approved minimum-change design. Each write still passes through
  // the DB's own triggers as the real enforcement layer.
  async function runBulkApprove(isEquipment=false) {
    const setApproving  = isEquipment ? setEqBulkApproving  : setBulkApproving
    const setValidation = isEquipment ? setEqBulkValidation : setBulkValidation
    const setSelectedIds = isEquipment ? setEqBulkSelectedIds : setBulkSelectedIds
    const selectedIds = isEquipment ? eqBulkSelectedIds : bulkSelectedIds

    setApproving(true)
    const sourceList = isEquipment ? eqIpcs : ipcs
    const validation = validateBulkSelection(selectedIds, sourceList)
    setValidation(validation)
    if (!validation.allValid) { setApproving(false); return }

    const selected = sourceList.filter(i => selectedIds.has(i.id))
      // Bugfix: must approve in ascending ipc_number order within the
      // batch -- the filter() above preserves the source array's
      // (arbitrary) order, not numeric order. Since the real DB trigger
      // (enforce_ipc_sequential_approval) checks at the moment of each
      // individual UPDATE, approving out of order here would cause a
      // later IPC's write to fail the trigger even though the earlier
      // validateBulkSelection() pass said the whole batch was valid
      // (that check only confirms every blocker is EITHER already
      // approved OR in this same batch -- it does not by itself guarantee
      // write order, this sort does).
      .sort((a, b) => (a.ipc_number||0) - (b.ipc_number||0))
    let successCount = 0
    for (const ipc of selected) {
      if (isEquipment) await eqHandleIpcApproval(ipc, 'approve')
      else await handleIpcApproval(ipc, 'approve')
      successCount++
    }
    // Bugfix: audit_trail.entity_id is NOT NULL -- a bulk summary entry
    // has no single natural entity, so this uses the first approved IPC's
    // id as the anchor record for the summary row (the per-IPC audit rows
    // already written inside handleIpcApproval/eqHandleIpcApproval above
    // remain the authoritative per-record trail; this is only an
    // additional summary marker, not a replacement for those).
    if (selected.length > 0) {
      await logAudit(selected[0].id, isEquipment ? 'equipment_ipc' : 'ipc_certificate', 'bulk_approve',
        actorName, `Bulk approved ${successCount} IPC(s): ${selected.map(i=>i.ipc_number).join(', ')}`,
        'multiple', 'approved', projectId)
    }
    setSelectedIds(new Set())
    setValidation(null)
    setApproving(false)
  }

  function getCommercialStatus(ipc) {
    const stage = ipc.approval_stage || 'draft'
    // Phase 3: 'revoked' is a distinct state from 'draft' (previously a
    // revoke reset approval_stage to 'draft' directly, which is why no
    // dedicated visual existed for it). Kept visually distinct from both
    // 'rejected' (review failure) and 'draft' (never yet submitted) since
    // it means "was once approved, then reversed" — a materially
    // different history than either.
    if (stage === 'revoked') return { emoji:'↺', label:'مُلغى (كان معتمد)', color:'bg-purple-100 text-purple-700 border-purple-200' }
    if (stage === 'rejected') return { emoji:'🔴', label:'موقوف', color:'bg-red-100 text-red-700 border-red-200' }
    if (stage === 'approved') return { emoji:'🟢', label:'جاهز للصرف', color:'bg-green-100 text-green-700 border-green-200' }
    if (stage === 'cost_control' || stage === 'director') return { emoji:'🟡', label:'تحت المراجعة', color:'bg-amber-100 text-amber-700 border-amber-200' }
    return { emoji:'🟠', label:'بانتظار المستندات', color:'bg-orange-100 text-orange-700 border-orange-200' }
  }

  function getIpcApprovalButtons(ipc) {
    const stage = ipc.approval_stage || 'draft'
    const amount = parseFloat(ipc.gross_amount) || 0
    const requiredApprover = getIpcRequiredApprover(amount)
    const btns = []
    if (stage === 'draft' && can('IPC.Submit')) btns.push({ label: 'Submit for Approval', action: 'submit', color: 'bg-blue-600 text-white' })
    // Bugfix (Phase 3 follow-up): Resubmit was gated to 'rejected' only,
    // an oversight from when 'revoked' was introduced -- the Edit button's
    // visibility condition was updated to include 'revoked' at the time,
    // but this sibling Resubmit condition was missed, leaving a revoked
    // IPC correctable via Edit but with no way back into the approval
    // queue afterward. Confirmed by direct UI testing.
    if (['rejected','revoked'].includes(stage) && can('IPC.Submit')) btns.push({ label: '↻ Resubmit', action: 'resubmit', color: 'bg-blue-600 text-white' })
    if (stage === 'cost_control' && can('IPC.ApproveCostControl')) {
      btns.push({ label: '✓ Approve', action: 'approve', color: 'bg-green-600 text-white' })
      btns.push({ label: '✗ Reject', action: 'reject', color: 'bg-red-100 text-red-700' })
    }
    if (stage === 'director' && can('IPC.ApproveDirector')) {
      btns.push({ label: '✓ Final Approve', action: 'approve', color: 'bg-green-600 text-white' })
      btns.push({ label: '✗ Reject', action: 'reject', color: 'bg-red-100 text-red-700' })
    }
    // Revoke: cost_control can undo its own approval (stage director or approved via cost_control-only path);
    // director can undo its own final approval. Requires an actual recorded approval to revoke.
    if (can('IPC.ApproveCostControl') && ipc.cc_approved_by && (stage === 'director' || (stage === 'approved' && requiredApprover === 'cost_control'))) {
      btns.push({ label: '↺ Revoke', action: 'revoke', color: 'bg-amber-100 text-amber-700' })
    }
    if (can('IPC.ApproveDirector') && stage === 'approved' && ipc.dir_approved_by) {
      btns.push({ label: '↺ Revoke', action: 'revoke', color: 'bg-amber-100 text-amber-700' })
    }
    return { btns, requiredApprover }
  }

  function getEqIpcApprovalButtons(ipc) {
    const stage = ipc.approval_stage || 'draft'
    const amount = parseFloat(ipc.gross_amount) || 0
    const requiredApprover = getIpcRequiredApprover(amount)
    const btns = []
    if (stage === 'draft' && can('IPC.Submit')) btns.push({ label: 'Submit for Approval', action: 'submit', color: 'bg-blue-600 text-white' })
    // Bugfix: same fix as getIpcApprovalButtons() above.
    if (['rejected','revoked'].includes(stage) && can('IPC.Submit')) btns.push({ label: '↻ Resubmit', action: 'resubmit', color: 'bg-blue-600 text-white' })
    if (stage === 'cost_control' && can('IPC.ApproveCostControl')) {
      btns.push({ label: '✓ Approve', action: 'approve', color: 'bg-green-600 text-white' })
      btns.push({ label: '✗ Reject', action: 'reject', color: 'bg-red-100 text-red-700' })
    }
    if (stage === 'director' && can('IPC.ApproveDirector')) {
      btns.push({ label: '✓ Final Approve', action: 'approve', color: 'bg-green-600 text-white' })
      btns.push({ label: '✗ Reject', action: 'reject', color: 'bg-red-100 text-red-700' })
    }
    if (can('IPC.ApproveCostControl') && ipc.cc_approved_by && (stage === 'director' || (stage === 'approved' && requiredApprover === 'cost_control'))) {
      btns.push({ label: '↺ Revoke', action: 'revoke', color: 'bg-amber-100 text-amber-700' })
    }
    if (can('IPC.ApproveDirector') && stage === 'approved' && ipc.dir_approved_by) {
      btns.push({ label: '↺ Revoke', action: 'revoke', color: 'bg-amber-100 text-amber-700' })
    }
    return { btns, requiredApprover }
  }

  // ═══ Equipment IPC approval (same logic, updates eqIpcs) ═══
  async function eqHandleIpcApproval(ipc, action, reason = '') {
    const stage = ipc.approval_stage || 'draft'
    const amount = parseFloat(ipc.gross_amount) || 0
    const requiredApprover = getIpcRequiredApprover(amount)
    let update = {}
    if (action === 'submit' || action === 'resubmit') {
      update = { approval_stage: 'cost_control', status: 'submitted', rejection_reason: null }
    } else if (action === 'approve') {
      if (stage === 'cost_control') {
        if (requiredApprover === 'cost_control') {
          update = { approval_stage: 'approved', status: 'approved', approved_date: today(), cc_approved_by: actorName, cc_approved_at: new Date().toISOString() }
        } else {
          update = { approval_stage: 'director', cc_approved_by: actorName, cc_approved_at: new Date().toISOString() }
        }
      } else if (stage === 'director') {
        update = { approval_stage: 'approved', status: 'approved', approved_date: today(), dir_approved_by: actorName, dir_approved_at: new Date().toISOString() }
      }
    } else if (action === 'reject') {
      update = { approval_stage: 'rejected', status: 'rejected', rejection_reason: reason }
    } else if (action === 'revoke') {
      // Phase 3: same revoked-not-draft logic as handleIpcApproval() above.
      if (ipc.is_final) {
        const confirmed = window.confirm(
          'تحذير: هذا مستخلص نهائي (Final IPC).\n' +
          'التراجع عن اعتماده سيعيد فتح العقد للسماح بمستخلصات جديدة.\n' +
          'هذا القرار يتطلب تأكيد على مستوى Director.\n\n' +
          'هل أنت متأكد من المتابعة؟'
        )
        if (!confirmed) return
        if (!can('IPC.ApproveDirector')) {
          alert('التراجع عن اعتماد مستخلص نهائي (Final IPC) يتطلب دور Director.')
          return
        }
      }
      update = { approval_stage: 'revoked', status: 'revoked', cc_approved_by: null, cc_approved_at: null, dir_approved_by: null, dir_approved_at: null, rejection_reason: null, approved_date: null }
    }
    if (!Object.keys(update).length) return
    const { error, data: updRows } = await supabase.from('ipc_certificates').update(update).eq('id', ipc.id).select('id')
    if (error) { alert('فشل تنفيذ العملية: ' + error.message); return }
    if (!updRows || updRows.length === 0) { alert('لم يتم تحديث أي صف — تحقق من صلاحيات RLS أو من أن المستخلص لا يزال موجودًا.'); return }
    await logAudit(ipc.id, 'equipment_ipc', action, actorName, reason, stage, update.approval_stage || stage, projectId)
    eqFetchIPCs(projectId)
    if (eqSelectedIpc?.id === ipc.id) setEqSelectedIpc(p => ({ ...p, ...update }))
  }

  // Legacy helper kept for any remaining direct calls (e.g. mark as paid)
  async function changeStatus(id, status) {
    const updates = { status }
    if(status==='approved') updates.approved_date = today()
    await supabase.from('ipc_certificates').update(updates).eq('id',id)
    fetchIPCs(projectId)
    if(selectedIpc?.id===id) setSelectedIpc(p=>({...p,...updates}))
  }

  async function deleteIPC(id, stage) {
    // Bugfix: was checking tempRole !== 'contract_manager' -- same dead
    // role as the button's visibility condition above (never selectable
    // from the Role dropdown). Fixed to match the actual authority owner
    // (commercial_engineer). This check is now redundant with the button's
    // own visibility condition, but kept as defense-in-depth in case this
    // function is ever called from another path.
    if (!can('IPC.Delete')) { alert('حذف المستخلص متاح فقط لدور Commercial Engineer.'); return }
    if ((stage || 'draft') !== 'draft') { alert('لا يمكن حذف المستخلص إلا في حالة Draft.'); return }
    if(!window.confirm('Delete this IPC?')) return
    // Legacy #2/#3: delete the record first, verify it, and only then remove its documents (never the other way round)
    const result = await deleteRecordThenDocuments('ipc_certificates', id)
    const problem = describeDeleteResult(result, 'المستخلص')
    if (problem) alert(problem)
    fetchIPCs(projectId)
  }

  async function eqDeleteIPC(id, stage) {
    // Bugfix: same fix as deleteIPC() above.
    if (!can('IPC.Delete')) { alert('حذف المستخلص متاح فقط لدور Commercial Engineer.'); return }
    if ((stage || 'draft') !== 'draft') { alert('لا يمكن حذف المستخلص إلا في حالة Draft.'); return }
    if(!window.confirm('Delete this IPC?')) return
    // Legacy #2/#3: delete the certificate first, verify it, and only then remove its documents (never the other way round).
    // The IPC line items are removed by the database itself (ipc_line_items.ipc_id is ON DELETE CASCADE), so they are no longer
    // deleted separately BEFORE the certificate: if the certificate could not be deleted they used to be lost.
    const result = await deleteRecordThenDocuments('ipc_certificates', id)
    const problem = describeDeleteResult(result, 'المستخلص')
    if (problem) alert(problem)
    eqFetchIPCs(projectId)
  }

  // ── BULK UPLOAD ────────────────────────────────────────────
  function downloadIpcTemplate() {
    // Phase 2: period_from/period_to added as REAL Excel date cells (JS Date
    // objects — SheetJS writes these as genuine date cells, not text), so
    // users fill them in via Excel's own date picker/formatting rather than
    // typing an ambiguous free-text label.
    // Bugfix (separate investigation, post-Phase-2): actual_date's example
    // value is now ALSO a real Excel date cell (previously a free-text
    // label like 'Oct-2025', which the upload path accepted uncritically
    // and stored as-is in ipc_line_items.actual_cost_date — corrupting
    // that column's YYYY-MM-DD contract, which every other write path in
    // the app enforces via <input type="date">). actual_date's BUSINESS
    // MEANING (line-item cost recognition date, independent from the IPC
    // certification period) is unchanged — only its accepted input format
    // is now enforced consistently, via the same parseExcelDateCell().
    const ws = XLSX.utils.json_to_sheet([
      { 'Sub.code':'Mat-Dew2', 'Supplier Name':'Construction Force For General Contracting', 'PO Number (per Item)':'2401825', 'Cost Code':'31 23 19', 'Description':'Electrical Submersible Pump 300WQ800', 'invoiced amount':1200000, 'invoice number':1, 'period_from': new Date(2026, 4, 1), 'period_to': new Date(2026, 4, 31), 'submission_date':'2026-07-01', 'actual_date': new Date(2025, 9, 31), 'unit':'SET', 'QTY':4 },
    ])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'IPC')
    XLSX.writeFile(wb, 'ipc_bulk_template.xlsx')
  }

  function handleIpcFileUpload(e) {
    const file = e.target.files[0]
    if (!file) return
    setUploadError('')
    setUploadResult(null)
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'binary' }) // Bugfix: cellDates:true removed — it made SheetJS's own serial->Date conversion timezone-dependent (see parseExcelDateCell() above). period_from/period_to now read as raw Excel serial numbers instead.
        const ws = wb.Sheets[wb.SheetNames[0]]

        // Try range:2 first (header on row 3 — original Excel format)
        let rows = XLSX.utils.sheet_to_json(ws, { range: 2, defval: '' })

        // If no 'PO Number (per Item)' column, try default (template format — header on row 1)
        const hasCorrectHeader = rows.length > 0 && ('PO Number (per Item)' in rows[0] || 'po_number' in rows[0])
        if (!hasCorrectHeader) {
          rows = XLSX.utils.sheet_to_json(ws, { defval: '' })
        }

        // Filter junk rows
        rows = rows.filter(r => {
          const po = String(r['PO Number (per Item)'] || r.po_number || r['PO Number'] || '').trim().replace('.0','')
          return po && po !== 'PO Number (per Item)' && po.length > 3
        })

        if (!rows.length) { setUploadError('مفيش بيانات صالحة — تأكد من الملف'); return }
        setUploadRows(rows)
      } catch(e) { setUploadError('خطأ في قراءة الملف: ' + e.message) }
    }
    reader.readAsBinaryString(file)
  }

  async function confirmIpcUpload() {
    if (!uploadRows.length) return
    setUploading(true)
    let success = 0, failed = 0, skipped = 0
    const rejectedDetails = [] // Phase 2: detailed reason per rejected group, surfaced to the user

    // Fetch all approved subcontracts
    const { data: allContracts } = await supabase
      .from('subcontracts')
      .select('id, contract_no, retention_pct, advance_pct, po_id, purchase_orders(po_number)')
      .eq('project_id', projectId)
      .eq('status', 'approved')

    // Group rows by PO + invoice number: each unique combination becomes one IPC certificate.
    const groupMap = new Map()
    for (const row of uploadRows) {
      const poNum = String(
        row['PO Number (per Item)'] || row.po_number || row['PO Number'] || ''
      ).trim().replace('.0','')
      if (!poNum || poNum.length < 3) continue

      const invKey   = Object.keys(row).find(k => k.toLowerCase().includes('invoiced'))
      const qtyKey   = Object.keys(row).find(k => k.trim().toUpperCase() === 'QTY' || k.trim().toLowerCase() === 'qty')
      const priceKey = Object.keys(row).find(k => k.toLowerCase().includes('unit price') || k.toLowerCase().includes('unit_price'))
      const unitKey  = Object.keys(row).find(k => k.trim().toLowerCase() === 'unit')
      const ipcNoKey = Object.keys(row).find(k => k.trim().toLowerCase() === 'invoice number' || k.trim().toLowerCase() === 'ipc number' || k.trim().toLowerCase() === 'ipc_number')
      const dateKey  = Object.keys(row).find(k => k.trim().toLowerCase().replace(/\s+/g,'') === 'actual_date' || k.trim().toLowerCase().replace(/\s+/g,'') === 'actualdate')
      // Phase 2: period_from/period_to — the IPC Certification Period,
      // completely independent from actual_date (dateKey above, which
      // remains untouched and still only feeds ipc_line_items.actual_cost_date).
      const periodFromKey = Object.keys(row).find(k => k.trim().toLowerCase().replace(/\s+/g,'_') === 'period_from')
      const periodToKey   = Object.keys(row).find(k => k.trim().toLowerCase().replace(/\s+/g,'_') === 'period_to')

      const amt     = parseFloat(row[invKey]   ?? 0) || 0
      const qty     = parseFloat(row[qtyKey]   ?? 0) || 0
      const uPrice  = parseFloat(row[priceKey] ?? 0) || 0
      const unit    = String(row[unitKey] || 'LS').trim()
      const subCode = String(row['Sub.code'] || row.sub_code || '').trim()
      const ipcNo   = parseInt(row[ipcNoKey]) || null
      // actual_date -> actual_cost_date: business contract is YYYY-MM-DD,
      // parsed via the SAME parseExcelDateCell() used for period_from/
      // period_to (no second parser). Unlike the period columns, this is
      // extracted here but NOT validated yet — validation/rejection happens
      // per-group below (after grouping), consistent with how the period
      // fields are validated, so a rejection message can reference the
      // group's PO/IPC number.
      const actualDate = parseExcelDateCell(row[dateKey])
      const periodFrom = periodFromKey ? parseExcelDateCell(row[periodFromKey]) : null
      const periodTo   = periodToKey   ? parseExcelDateCell(row[periodToKey])   : null

      if (!ipcNo) continue // invoice number is required to know which IPC this row belongs to

      const groupKey = `${poNum}::${ipcNo}`
      // All rows sharing the same PO+invoice number belong to the SAME IPC
      // certificate, so they must share the same period. The first row
      // encountered for a group sets the group's period; later rows for
      // the same group are not expected to redefine it (matching how
      // gross/lines already accumulate per group below).
      if (!groupMap.has(groupKey)) groupMap.set(groupKey, { poNum, ipcNo, actualDate, periodFrom, periodTo, lines: [] })
      groupMap.get(groupKey).lines.push({ subCode, amt, qty, uPrice, unit })
    }

    for (const { poNum, ipcNo, actualDate, periodFrom, periodTo, lines } of groupMap.values()) {
      const contract = allContracts?.find(c =>
        String(c.purchase_orders?.po_number || '').trim() === poNum
      )
      if (!contract) { failed++; continue }

      // Skip if this PO + IPC number combination was already uploaded
      const { data: existing } = await supabase
        .from('ipc_certificates').select('id')
        .eq('subcontract_id', contract.id).eq('ipc_number', ipcNo).limit(1)
      if (existing?.length) { skipped++; continue }

      // actual_date validation — business contract is YYYY-MM-DD (matches
      // every other IPC date-entry path: the Wizard's <input type="date">,
      // manual line-item edits, and 99.88% of existing historical data).
      // Ambiguous or free-text values ('Apr-2026', 'Oct-2025', etc.) are
      // REJECTED, never guessed — this was previously silently accepted
      // as raw text and stored as-is in actual_cost_date, corrupting the
      // one confirmed downstream consumer (buildByMonthData()'s monthly
      // grouping via actual_cost_date.slice(0,7)) with a nonsense bucket
      // key. A missing/invalid actual_date rejects the row rather than
      // inserting actual_cost_date = null, per the approved rule.
      if (!actualDate) {
        failed++
        rejectedDetails.push(`PO ${poNum} / IPC ${ipcNo}: actual_date is missing or not in a supported format (expected YYYY-MM-DD or a real Excel date cell).`)
        continue
      }

      // Phase 2: IPC period validation — MUST happen before any INSERT.
      // Business rules (approved):
      //   - Both missing/invalid -> reject the group.
      //   - period_from > period_to (when both valid) -> reject the group.
      //   - Otherwise derive period_month via the SAME derivePeriodMonth()
      //     the Wizard uses (period_from priority, exactly matching the
      //     existing, unchanged Wizard rule). Different months between
      //     period_from and period_to is explicitly allowed.
      // NEVER falls back to today()/actual_date/system date for a missing
      // or invalid period — a rejection is always preferred over an
      // invented value.
      if (periodFrom && periodTo && periodFrom > periodTo) {
        failed++
        rejectedDetails.push(`PO ${poNum} / IPC ${ipcNo}: period_from (${periodFrom}) is after period_to (${periodTo}).`)
        continue
      }
      const periodResult = derivePeriodMonth(periodFrom, periodTo)
      if (!periodResult.valid) {
        failed++
        rejectedDetails.push(`PO ${poNum} / IPC ${ipcNo}: ${periodResult.error}`)
        continue
      }

      // Phase 1 (IPC financial engine consolidation): this block previously
      // recomputed retention/advance recovery locally with a SIMPLIFIED
      // formula that omitted the retention cap and prevRetentionHeld
      // subtraction entirely (confirmed by audit to diverge from the
      // Wizard's net_payable by a material amount whenever cumulative
      // retention approaches the contract's retention cap). It now calls
      // the SAME computeIpcFinancials() engine wizardTotals()/
      // eqWizardTotals() call, so this path can never again silently
      // under- or over-withhold retention relative to the Wizard.
      //
      // prevRetentionHeld/prevCertifiedForRet were NOT computed at all in
      // the prior version of this function — they are newly added here,
      // using the exact same filter (subcontract_id match, status in
      // approved/paid, excluding nothing since this is a new IPC with no
      // existing id yet) as wizardTotals() uses.
      //
      // Per the approved Phase 1 scope, this block's calculation logic
      // (gross/retention/advance/net) matches wizardTotals() exactly via
      // computeIpcFinancials(). period_month/period_from/period_to are NO
      // LONGER hardcoded here — they are now derived above (Phase 2) from
      // the Excel columns via derivePeriodMonth(), consistent with the
      // Wizard's own rule, instead of new Date()/null.
      const gross  = lines.reduce((s, l) => s + l.amt, 0)
      const retPct = parseFloat(contract.retention_pct) || 0
      const advRecoveryRate = parseFloat(contract.adv_recovery_rate) || 0
      const vatCase   = parseInt(contract.vat_case) || 1
      const vatRate   = (parseFloat(contract.vat_pct) || 15) / 100
      const contractValue = parseFloat(contract.contract_value) || 0
      const realAdvPay = advPayByContract[contract.id] || 0
      const advPct0    = parseFloat(contract.advance_pct) || 0
      const prevRecoveries = (ipcs||[])
        .filter(i => i.subcontract_id === contract.id)
        .reduce((s,i) => s + (parseFloat(i.advance_recovery)||0), 0)
      const prevRetentionHeld = (ipcs||[])
        .filter(i => i.subcontract_id === contract.id && ['approved','paid'].includes(i.status))
        .reduce((s,i) => s + (parseFloat(i.retention_amount ?? i.retention_held)||0), 0)
      const prevCertifiedForRet = (ipcs||[])
        .filter(i => i.subcontract_id === contract.id && ['approved','paid'].includes(i.status))
        .reduce((s,i) => s + (parseFloat(i.gross_amount)||0), 0)

      const totals = computeIpcFinancials({
        gross, retPct, contractValue, isFinal: false, // Excel upload never creates a Final IPC — Phase 0 decision
        prevRetentionHeld, prevCertifiedForRet,
        advRecoveryRate, realAdvPay, advPct0, prevRecoveries,
        vatCase, vatRate, totalDeductions: 0,
      })

      // Insert IPC header
      const { data: ipcData, error: ipcErr } = await supabase
        .from('ipc_certificates')
        .insert({
          project_id:        projectId,
          subcontract_id:    contract.id,
          ipc_number:        ipcNo,
          period_month:      periodResult.periodMonth, // Phase 2: derived from period_from/period_to via derivePeriodMonth(), never today()
          period_from:       periodFrom, period_to: periodTo,
          submission_date:   today(),
          gross_amount:      totals.gross,
          vat_amount:        totals.vat,
          total_inc_vat:     totals.totalIncVat,
          retention_held:    totals.ret, retention_amount: totals.ret,
          advance_recovery:  totals.advRecovery,
          advance_recovery_vat: totals.advRecoveryVat,
          total_adv_inc_vat: totals.totalAdvIncVat,
          advance_deduction: totals.advRecovery,
          net_exc_vat:       totals.gross, net_payable: totals.net,
          status:            'draft',
          approval_stage:    'draft',
          is_final:          false, // Excel upload never creates a Final IPC — Phase 0 decision
          notes:             '',
        }).select().single()

      if (ipcErr) { failed++; continue }

      // Fetch po_line_items using po_id directly from subcontract
      const poId = contract.po_id
      const { data: poLines } = poId ? await supabase
        .from('po_line_items').select('id, sub_code, description, unit, qty, unit_rate').eq('po_id', poId)
        : { data: [] }

      // Insert one line per sub_code row
      const linePayloads = lines.map(l => {
        const poLine = (poLines || []).find(pl =>
          String(pl.sub_code || '').trim().toLowerCase() === l.subCode.toLowerCase()
        )
        // Always use unit_rate and qty from Excel
        const unitRate = l.uPrice > 0 ? l.uPrice : (parseFloat(poLine?.unit_rate) || l.amt)
        const qty      = l.qty > 0 ? l.qty : (unitRate > 0 && l.amt > 0 ? +(l.amt / unitRate).toFixed(6) : (l.amt > 0 ? 1 : 0))
        return {
          ipc_id:           ipcData.id,
          po_line_item_id:  poLine?.id || null,
          sub_code:         l.subCode || null,
          description:      poLine?.description || l.subCode,
          unit:             l.unit && l.unit !== '-' ? l.unit : (poLine?.unit || 'LS'),
          boq_qty:          parseFloat(poLine?.qty) || 1,
          unit_rate:        unitRate,
          this_period_qty:  qty,
          invoiced_amount:  l.amt,
          actual_cost_date: actualDate || null,
        }
      })

      if (linePayloads.length) {
        const { error: linesErr } = await supabase.from('ipc_line_items').insert(linePayloads)
        if (linesErr) {
          console.error('[MAT IPC UPLOAD] lines insert error:', linesErr.message, JSON.stringify(linePayloads[0]))
          failed++
          await supabase.from('ipc_certificates').delete().eq('id', ipcData.id)
          continue
        }
      }

      success++
    }

    setUploading(false)
    setUploadResult({ success, failed, skipped, rejectedDetails }) // Phase 2: rejectedDetails surfaces the exact reason per rejected group
    if (success > 0) {
      await fetchIPCs(projectId)
      setUploadRows([])
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  // ═══ Equipment IPC — Template & Upload (multi-IPC, numbered per file) ═══
  function eqDownloadIpcTemplate() {
    // Phase 2: same period_from/period_to addition as the Material template
    // above, as real Excel date cells. actual_date's example is now also a
    // real Excel date cell (same bugfix as the Material template above).
    // Note rows 2/3 share invoice number 2 (same IPC) — both must carry
    // the SAME period_from/period_to since they belong to one certificate;
    // shown here to make that expectation explicit in the template itself.
    const ws = XLSX.utils.json_to_sheet([
      { 'EQP ID':'9000 NAA', 'Sub.code':'telehander', 'Supplier Name':'Fatima International', 'PO Number (per Item)':'2505670', 'Cost Code':'03 10 00', 'Description':'Concrete Forming and Accessories', 'invoiced amount':2188, 'invoice number':1, 'period_from': new Date(2025, 9, 1), 'period_to': new Date(2025, 9, 31), 'submission_date':'2026-07-01', 'actual_date': new Date(2025, 9, 31), 'unit':'hours', 'QTY':30 },
      { 'EQP ID':'608fdj3533686', 'Sub.code':'FORKLIFT', 'Supplier Name':'Fatima International', 'PO Number (per Item)':'2505670', 'Cost Code':'03 10 00', 'Description':'Concrete Forming and Accessories', 'invoiced amount':9749.94, 'invoice number':2, 'period_from': new Date(2025, 10, 1), 'period_to': new Date(2025, 10, 30), 'submission_date':'2026-07-01', 'actual_date': new Date(2025, 10, 30), 'unit':'hours', 'QTY':286 },
      { 'EQP ID':'9000 NAA', 'Sub.code':'telehander', 'Supplier Name':'Fatima International', 'PO Number (per Item)':'2505670', 'Cost Code':'03 10 00', 'Description':'Concrete Forming and Accessories', 'invoiced amount':15500, 'invoice number':2, 'period_from': new Date(2025, 10, 1), 'period_to': new Date(2025, 10, 30), 'submission_date':'2026-07-01', 'actual_date': new Date(2025, 10, 30), 'unit':'hours', 'QTY':260 },
    ])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Equipment IPC')
    XLSX.writeFile(wb, 'equipment_ipc_bulk_template.xlsx')
  }

  function handleEqIpcFileUpload(e) {
    const file = e.target.files[0]
    if (!file) return
    setEqUploadError('')
    setEqUploadResult(null)
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'binary' }) // Bugfix: cellDates:true removed — it made SheetJS's own serial->Date conversion timezone-dependent (see parseExcelDateCell() above). period_from/period_to now read as raw Excel serial numbers instead.
        const ws = wb.Sheets[wb.SheetNames[0]]
        let rows = XLSX.utils.sheet_to_json(ws, { range: 2, defval: '' })
        const hasCorrectHeader = rows.length > 0 && ('PO Number (per Item)' in rows[0] || 'po_number' in rows[0])
        if (!hasCorrectHeader) rows = XLSX.utils.sheet_to_json(ws, { defval: '' })
        rows = rows.filter(r => {
          const po = String(r['PO Number (per Item)'] || r.po_number || r['PO Number'] || '').trim().replace('.0','')
          return po && po !== 'PO Number (per Item)' && po.length > 3
        })
        if (!rows.length) { setEqUploadError('مفيش بيانات صالحة — تأكد من الملف'); return }
        setEqUploadRows(rows)
      } catch(e) { setEqUploadError('خطأ في قراءة الملف: ' + e.message) }
    }
    reader.readAsBinaryString(file)
  }

  async function eqConfirmIpcUpload() {
    if (!eqUploadRows.length) return
    setEqUploading(true)
    let success = 0, failed = 0, skipped = 0
    const rejectedDetails = [] // Phase 2: same as Material path

    const { data: allContracts } = await supabase
      .from('equipment_contracts')
      .select('id, contract_no, retention_pct, advance_pct, po_id, rental_equipment_pos(po_number)')
      .eq('project_id', projectId)
      .eq('status', 'approved')

    // Group rows by PO + invoice number: each unique combination becomes one IPC certificate.
    const groupMap = new Map()
    for (const row of eqUploadRows) {
      const poNum = String(
        row['PO Number (per Item)'] || row.po_number || row['PO Number'] || ''
      ).trim().replace('.0','')
      if (!poNum || poNum.length < 3) continue

      const invKey     = Object.keys(row).find(k => k.toLowerCase().includes('invoiced'))
      const qtyKey     = Object.keys(row).find(k => k.trim().toUpperCase() === 'QTY' || k.trim().toLowerCase() === 'qty')
      const priceKey   = Object.keys(row).find(k => k.toLowerCase().includes('unit price') || k.toLowerCase().includes('unit_price'))
      const unitKey    = Object.keys(row).find(k => k.trim().toLowerCase() === 'unit')
      const ipcNoKey   = Object.keys(row).find(k => k.trim().toLowerCase() === 'invoice number' || k.trim().toLowerCase() === 'ipc number' || k.trim().toLowerCase() === 'ipc_number')
      const dateKey    = Object.keys(row).find(k => k.trim().toLowerCase().replace(/\s+/g,'') === 'actual_date' || k.trim().toLowerCase().replace(/\s+/g,'') === 'actualdate')
      const eqpIdKey   = Object.keys(row).find(k => k.trim().toUpperCase() === 'EQP ID' || k.trim().toLowerCase() === 'equipment_id')
      // Phase 2: same period_from/period_to extraction as the Material path
      const periodFromKey = Object.keys(row).find(k => k.trim().toLowerCase().replace(/\s+/g,'_') === 'period_from')
      const periodToKey   = Object.keys(row).find(k => k.trim().toLowerCase().replace(/\s+/g,'_') === 'period_to')

      const amt         = parseFloat(row[invKey]   ?? 0) || 0
      const qty         = parseFloat(row[qtyKey]   ?? 0) || 0
      const uPrice      = parseFloat(row[priceKey] ?? 0) || 0
      const unit        = String(row[unitKey] || 'Hours').trim()
      const subCode     = String(row['Sub.code'] || row.sub_code || '').trim()
      const equipmentId = String(row[eqpIdKey] || '').trim()
      const ipcNo       = parseInt(row[ipcNoKey]) || null
      // actual_date -> actual_cost_date: same rule and same shared
      // parseExcelDateCell() as the Material path above.
      const actualDate  = parseExcelDateCell(row[dateKey])
      const periodFrom  = periodFromKey ? parseExcelDateCell(row[periodFromKey]) : null
      const periodTo    = periodToKey   ? parseExcelDateCell(row[periodToKey])   : null

      if (!ipcNo) continue // invoice number is required to know which IPC this row belongs to

      const groupKey = `${poNum}::${ipcNo}`
      if (!groupMap.has(groupKey)) groupMap.set(groupKey, { poNum, ipcNo, actualDate, periodFrom, periodTo, lines: [] })
      groupMap.get(groupKey).lines.push({ subCode, amt, qty, uPrice, unit, equipmentId })
    }
    

    for (const { poNum, ipcNo, actualDate, periodFrom, periodTo, lines } of groupMap.values()) {
      const contract = allContracts?.find(c =>
        String(c.rental_equipment_pos?.po_number || '').trim() === poNum
      )
      
      if (!contract) { failed++; continue }

      const { data: existing } = await supabase
        .from('ipc_certificates').select('id')
        .eq('equipment_contract_id', contract.id).eq('ipc_number', ipcNo).limit(1)
      if (existing?.length) { skipped++; continue }

      // actual_date validation — same rule as confirmIpcUpload() above.
      if (!actualDate) {
        failed++
        rejectedDetails.push(`PO ${poNum} / IPC ${ipcNo}: actual_date is missing or not in a supported format (expected YYYY-MM-DD or a real Excel date cell).`)
        continue
      }

      // Phase 2: IPC period validation — same rules as confirmIpcUpload() above.
      if (periodFrom && periodTo && periodFrom > periodTo) {
        failed++
        rejectedDetails.push(`PO ${poNum} / IPC ${ipcNo}: period_from (${periodFrom}) is after period_to (${periodTo}).`)
        continue
      }
      const periodResult = derivePeriodMonth(periodFrom, periodTo)
      if (!periodResult.valid) {
        failed++
        rejectedDetails.push(`PO ${poNum} / IPC ${ipcNo}: ${periodResult.error}`)
        continue
      }

      // Phase 1 (IPC financial engine consolidation) — same fix and same
      // reasoning as confirmIpcUpload() above: calls the SAME
      // computeIpcFinancials() engine instead of a locally-duplicated,
      // simplified formula. prevRetentionHeld/prevCertifiedForRet are
      // newly computed here (were absent before). period_month/
      // period_from/period_to are now derived from the Excel columns via
      // derivePeriodMonth() above (Phase 2) instead of new Date().
      const gross  = lines.reduce((s, l) => s + l.amt, 0)
      const retPct = parseFloat(contract.retention_pct) || 0
      const advRecoveryRate = parseFloat(contract.adv_recovery_rate) || 0
      const vatCase   = parseInt(contract.vat_case) || 1
      const vatRate   = (parseFloat(contract.vat_pct) || 15) / 100
      const contractValue = parseFloat(contract.contract_value) || 0
      const realAdvPay = eqAdvPayByContract[contract.id] || 0
      const advPct0    = parseFloat(contract.advance_pct) || 0
      const prevRecoveries = (eqIpcs||[])
        .filter(i => i.equipment_contract_id === contract.id)
        .reduce((s,i) => s + (parseFloat(i.advance_recovery)||0), 0)
      const prevRetentionHeld = (eqIpcs||[])
        .filter(i => i.equipment_contract_id === contract.id && ['approved','paid'].includes(i.status))
        .reduce((s,i) => s + (parseFloat(i.retention_amount ?? i.retention_held)||0), 0)
      const prevCertifiedForRet = (eqIpcs||[])
        .filter(i => i.equipment_contract_id === contract.id && ['approved','paid'].includes(i.status))
        .reduce((s,i) => s + (parseFloat(i.gross_amount)||0), 0)

      const totals = computeIpcFinancials({
        gross, retPct, contractValue, isFinal: false, // Excel upload never creates a Final IPC — Phase 0 decision
        prevRetentionHeld, prevCertifiedForRet,
        advRecoveryRate, realAdvPay, advPct0, prevRecoveries,
        vatCase, vatRate, totalDeductions: 0,
      })

      const { data: ipcData, error: ipcErr } = await supabase
        .from('ipc_certificates')
        .insert({
          project_id:        projectId,
          equipment_contract_id: contract.id,
          ipc_number:        ipcNo,
          period_month:      periodResult.periodMonth, // Phase 2: derived, never today()
          period_from:       periodFrom, period_to: periodTo,
          submission_date:   today(),
          gross_amount:      totals.gross,
          vat_amount:        totals.vat,
          total_inc_vat:     totals.totalIncVat,
          retention_held:    totals.ret, retention_amount: totals.ret,
          advance_recovery:  totals.advRecovery,
          advance_recovery_vat: totals.advRecoveryVat,
          total_adv_inc_vat: totals.totalAdvIncVat,
          advance_deduction: totals.advRecovery,
          net_exc_vat:       totals.gross, net_payable: totals.net,
          status:            'draft',
          approval_stage:    'draft',
          ipc_type:          'equipment',
          is_final:          false, // Excel upload never creates a Final IPC — Phase 0 decision
          notes:             '',
        }).select().single()

      if (ipcErr) { failed++; continue }

      const poId = contract.po_id
      const { data: poLines } = poId ? await supabase
        .from('rental_equipment_po_line_items').select('id, equipment_type, unit, quantity, rate, cost_code_id, cost_codes(id,code,description,parent_id,level)').eq('po_id', poId)
        : { data: [] }

      // Resolve Resource Code ancestry for all PO lines up front (batch, not per-row)
      const bulkParentIds = [...new Set((poLines||[]).map(pl => pl.cost_codes?.parent_id).filter(Boolean))]
      let bulkParentMap = {}
      if (bulkParentIds.length) {
        const { data: parents } = await supabase.from('cost_codes').select('id,code').in('id', bulkParentIds)
        ;(parents || []).forEach(p => { bulkParentMap[p.id] = p })
      }

      const linePayloads = lines.map(l => {
        const poLine = (poLines || []).find(pl =>
          String(pl.equipment_type || '').trim().toLowerCase() === l.subCode.toLowerCase()
        )
        const unitRate = l.uPrice > 0 ? l.uPrice : (parseFloat(poLine?.rate) || l.amt)
        const qty      = l.qty > 0 ? l.qty : (unitRate > 0 && l.amt > 0 ? +(l.amt / unitRate).toFixed(6) : (l.amt > 0 ? 1 : 0))
        const cc = poLine?.cost_codes || null
        const isResourceLevel = cc && parseInt(cc.level) !== 1 && bulkParentMap[cc.parent_id]
        const realCostCodeId = isResourceLevel ? bulkParentMap[cc.parent_id].id : (poLine?.cost_code_id || cc?.id || null)
        return {
          ipc_id:           ipcData.id,
          po_line_item_id:  poLine?.id || null,
          cost_code_id:     realCostCodeId,
          sub_code:         l.subCode || null,
          description:      poLine?.equipment_type || l.subCode,
          unit:             l.unit && l.unit !== '-' ? l.unit : (poLine?.unit || 'Hours'),
          boq_qty:          parseFloat(poLine?.quantity) || 1,
          unit_rate:        unitRate,
          this_period_qty:  qty,
          invoiced_amount:  l.amt,
          equipment_id:     l.equipmentId || null,
          actual_cost_date: actualDate || null,
        }
      })

      if (linePayloads.length) {
        const { error: linesErr } = await supabase.from('ipc_line_items').insert(linePayloads)
        if (linesErr) {
          console.error('[EQ IPC UPLOAD] lines insert error:', linesErr.message, JSON.stringify(linePayloads[0]))
          failed++
          // Roll back the header since its lines failed to save
          await supabase.from('ipc_certificates').delete().eq('id', ipcData.id)
          continue
        }
      }

      success++
    }

    setEqUploading(false)
    setEqUploadResult({ success, failed, skipped, rejectedDetails })
    if (success > 0) {
      await eqFetchIPCs(projectId)
      setEqUploadRows([])
      if (eqFileRef.current) eqFileRef.current.value = ''
    }
  }

  // ── EDIT IPC ───────────────────────────────────────────────
  function openEditModal() {
    setEditForm({
      ipc_number:      selectedIpc.ipc_number,
      period_from:     selectedIpc.period_from     || '',
      period_to:       selectedIpc.period_to       || '',
      submission_date: selectedIpc.submission_date || '',
      retention_pct:   selectedIpc.retention_amount && selectedIpc.gross_amount
                         ? +((selectedIpc.retention_amount / selectedIpc.gross_amount) * 100).toFixed(2)
                         : 10,
      advance_pct:     selectedIpc.advance_deduction && selectedIpc.gross_amount
                         ? +((selectedIpc.advance_deduction / selectedIpc.gross_amount) * 100).toFixed(2)
                         : 0,
      notes:           selectedIpc.notes || '',
      lines:           selectedLines.map(l => ({
        id:              l.id,
        sub_code:        l.sub_code || l.po_line_items?.sub_code || '—',
        sub_code_desc:   l.sub_code_desc || null,
        cost_code_label: l.cost_codes?.code || null,
        cost_code_desc:  l.cost_codes?.description || null,
        resource_code_label: l.resource_code_info?.code || null,
        resource_code_desc:  l.resource_code_info?.description || null,
        description:     l.description,
        unit:            l.unit || 'LS',
        unit_rate:       parseFloat(l.unit_rate) || 0,
        this_period_qty: parseFloat(l.this_period_qty) || 0,
        actual_cost_date: l.actual_cost_date || '2026-04-30',
      }))
    })
    setShowEditModal(true)
  }

  async function updateOtherDeductions(ipcId, value, isEquipment) {
    const v = parseFloat(value) || 0
    const { error } = await supabase.from('ipc_certificates').update({ other_deductions: v }).eq('id', ipcId)
    if (error) { alert('خطأ: ' + error.message); return }
    if (isEquipment) {
      setEqSelectedIpc(p => p ? { ...p, other_deductions: v } : p)
      eqFetchIPCs(projectId)
    } else {
      setSelectedIpc(p => p ? { ...p, other_deductions: v } : p)
      fetchIPCs(projectId)
    }
  }

  async function addIpcDeduction(ipcId, deduction, isEquipment) {
    if (!(parseFloat(deduction.amount)>0)) { alert('أدخل قيمة الخصم'); return }
    const { data, error } = await supabase.from('ipc_deductions').insert({
      ipc_id: ipcId,
      deduction_type: deduction.deduction_type||'other',
      description: deduction.description||null,
      amount: parseFloat(deduction.amount)||0,
      created_by: actorName,
    }).select().single()
    if (error) { alert('خطأ: ' + error.message); return }
    if (isEquipment) {
      setEqSelectedIpc(p => p ? { ...p, _deductions: [...(p._deductions||[]), data] } : p)
      await eqFetchIPCs(projectId)
    } else {
      setSelectedIpc(p => p ? { ...p, _deductions: [...(p._deductions||[]), data] } : p)
      await fetchIPCs(projectId)
    }
  }

  async function removeIpcDeduction(deductionId, isEquipment) {
    if (!window.confirm('حذف بند الخصم؟')) return
    const { error } = await supabase.from('ipc_deductions').delete().eq('id', deductionId)
    if (error) { alert('خطأ: ' + error.message); return }
    if (isEquipment) {
      setEqSelectedIpc(p => p ? { ...p, _deductions: (p._deductions||[]).filter(d=>d.id!==deductionId) } : p)
      await eqFetchIPCs(projectId)
    } else {
      setSelectedIpc(p => p ? { ...p, _deductions: (p._deductions||[]).filter(d=>d.id!==deductionId) } : p)
      await fetchIPCs(projectId)
    }
  }

  async function saveEditIpc() {
    const lines  = editForm.lines || []
    const gross  = lines.reduce((s, l) => s + (l.this_period_qty * l.unit_rate), 0)
    const ret    = +(gross * (parseFloat(editForm.retention_pct) || 0) / 100).toFixed(2)
    const adv    = +(gross * (parseFloat(editForm.advance_pct)   || 0) / 100).toFixed(2)
    const net    = +(gross - ret - adv).toFixed(2)

    // Update each line
    for (const l of lines) {
      await supabase.from('ipc_line_items')
        .update({ this_period_qty: l.this_period_qty, actual_cost_date: l.actual_cost_date || '2026-04-30' })
        .eq('id', l.id)
    }

    // Update IPC header
    const { error } = await supabase.from('ipc_certificates').update({
      ipc_number:        editForm.ipc_number,
      period_from:       editForm.period_from    || null,
      period_to:         editForm.period_to      || null,
      submission_date:   editForm.submission_date || null,
      gross_amount:      gross,
      retention_held:    ret,
      retention_amount:  ret,
      advance_deduction: adv,
      net_exc_vat:       gross,
      net_payable:       net,
      notes:             editForm.notes || null,
    }).eq('id', selectedIpc.id)

    if (error) { alert('خطأ: ' + error.message); return }
    setShowEditModal(false)
    await fetchIPCs(projectId)
    const newLines = await fetchIpcLines(selectedIpc.id)
    setSelectedLines(newLines)
    setSelectedIpc(prev => ({ ...prev, gross_amount: gross, retention_amount: ret, retention_held: ret, advance_deduction: adv, net_payable: net, notes: editForm.notes }))
  }

  function eqOpenEditModal() {
    setEqEditForm({
      ipc_number:      eqSelectedIpc.ipc_number,
      period_from:     eqSelectedIpc.period_from     || '',
      period_to:       eqSelectedIpc.period_to       || '',
      submission_date: eqSelectedIpc.submission_date || '',
      retention_pct:   eqSelectedIpc.retention_amount && eqSelectedIpc.gross_amount
                         ? +((eqSelectedIpc.retention_amount / eqSelectedIpc.gross_amount) * 100).toFixed(2)
                         : 10,
      advance_pct:     eqSelectedIpc.advance_deduction && eqSelectedIpc.gross_amount
                         ? +((eqSelectedIpc.advance_deduction / eqSelectedIpc.gross_amount) * 100).toFixed(2)
                         : 0,
      notes:           eqSelectedIpc.notes || '',
      lines:           eqIpcLines.map(l => ({
        id:              l.id,
        sub_code:        l.sub_code || '—',
        sub_code_desc:   l.sub_code_desc || null,
        cost_code_label: l.cost_codes?.code || null,
        cost_code_desc:  l.cost_codes?.description || null,
        resource_code_label: l.resource_code_info?.code || null,
        resource_code_desc:  l.resource_code_info?.description || null,
        description:     l.description,
        unit:            l.unit || 'Hours',
        unit_rate:       parseFloat(l.unit_rate) || 0,
        this_period_qty: parseFloat(l.this_period_qty) || 0,
        equipment_id:    l.equipment_id || '',
        actual_cost_date: l.actual_cost_date || '',
      }))
    })
    setEqShowEditModal(true)
  }

  async function eqSaveEditIpc() {
    const lines  = eqEditForm.lines || []
    const gross  = lines.reduce((s, l) => s + (l.this_period_qty * l.unit_rate), 0)
    const ret    = +(gross * (parseFloat(eqEditForm.retention_pct) || 0) / 100).toFixed(2)
    const adv    = +(gross * (parseFloat(eqEditForm.advance_pct)   || 0) / 100).toFixed(2)
    const net    = +(gross - ret - adv).toFixed(2)

    for (const l of lines) {
      await supabase.from('ipc_line_items')
        .update({ this_period_qty: l.this_period_qty, equipment_id: l.equipment_id || null, actual_cost_date: l.actual_cost_date || null })
        .eq('id', l.id)
    }

    const { error } = await supabase.from('ipc_certificates').update({
      ipc_number:        eqEditForm.ipc_number,
      period_from:       eqEditForm.period_from    || null,
      period_to:         eqEditForm.period_to      || null,
      submission_date:   eqEditForm.submission_date || null,
      gross_amount:      gross,
      retention_held:    ret,
      retention_amount:  ret,
      advance_deduction: adv,
      net_exc_vat:       gross,
      net_payable:       net,
      notes:             eqEditForm.notes || null,
    }).eq('id', eqSelectedIpc.id)

    if (error) { alert('خطأ: ' + error.message); return }
    setEqShowEditModal(false)
    await eqFetchIPCs(projectId)
    const newLines = await eqFetchIpcLines(eqSelectedIpc.id)
    setEqSelectedIpc(prev => ({ ...prev, gross_amount: gross, retention_amount: ret, retention_held: ret, advance_deduction: adv, net_payable: net, notes: eqEditForm.notes }))
  }


  function exportToExcel() {
    const rows = ipcs.map(i=>({
      'Contract No':       i.subcontracts?.contract_no,
      'Contractor':        i.subcontracts?.contractor_name,
      'IPC No':            `IPC-${String(i.ipc_number).padStart(4,'0')}`,
      'Period From':       i.period_from,
      'Period To':         i.period_to,
      'Gross Amount':      i.gross_amount,
      'Retention':         i.retention_amount||i.retention_held,
      'Advance Deduction': i.advance_deduction,
      'Net Payable':       i.net_payable,
      'Status':            i.status,
    }))
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'IPC')
    XLSX.writeFile(wb,'ipc_certificates.xlsx')
  }

  function eqExportToExcel() {
    const rows = eqIpcs.map(i=>({
      'Contract No':       i.equipment_contracts?.contract_no,
      'Contractor':        i.equipment_contracts?.contractor_name,
      'IPC No':            `IPC-${String(i.ipc_number).padStart(4,'0')}`,
      'Period From':       i.period_from,
      'Period To':         i.period_to,
      'Actual Date':       i._actual_date,
      'Gross Amount':      i.gross_amount,
      'Retention':         i.retention_amount||i.retention_held,
      'Advance Deduction': i.advance_deduction,
      'Net Payable':       i.net_payable,
      'Status':            i.status,
    }))
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Equipment IPC')
    XLSX.writeFile(wb,'equipment_ipc_certificates.xlsx')
  }


  // ── COMPUTED ───────────────────────────────────────────────
  const filtered = ipcs.filter(i=>{
    if(filterStatus!=='all'   && i.status!==filterStatus)          return false
    if(filterContract!=='all' && i.subcontract_id!==filterContract) return false
    if(filterPO && !(i.subcontracts?.purchase_orders?.po_number||'').toLowerCase().includes(filterPO.toLowerCase())) return false
    if(filterType2!=='all') {
      const t2 = i.type_2 || i.subcontracts?.purchase_orders?.po_type || ''
      if(t2 !== filterType2) return false
    }
    if(filterType!=='all') {
      const lt = (i.subcontracts?.logistics_type||'').toLowerCase()
      if(filterType==='eqp'      && lt!=='eqp')        return false
      if(filterType==='oceanarium'&& lt!=='oceanarium') return false
      if(filterType==='logistics' && lt!=='logistics')  return false
    }
    if(searchText) {
      const q = searchText.toLowerCase()
      const matchContract = (i.subcontracts?.contract_no||'').toLowerCase().includes(q)
      const matchContractor = (i.subcontracts?.contractor_name||'').toLowerCase().includes(q)
      const matchIpc = String(i.ipc_number||'').includes(q)
      const matchPO = (i.subcontracts?.purchase_orders?.po_number||'').toLowerCase().includes(q)
      if(!matchContract && !matchContractor && !matchIpc && !matchPO) return false
    }
    if(filterGross && String(parseFloat(i.gross_amount)||0).includes(filterGross.replace(/,/g,''))) {}
    else if(filterGross && !String(Math.round(parseFloat(i.gross_amount)||0)).includes(filterGross.replace(/,/g,''))) return false
    if(filterRet && !String(Math.round(parseFloat(i.retention_amount||i.retention_held)||0)).includes(filterRet.replace(/,/g,''))) return false
    if(filterNet && !String(Math.round(parseFloat(i.net_payable)||0)).includes(filterNet.replace(/,/g,''))) return false
    return true
  })
  const selLinesTotal = selectedLines.reduce((s,l)=>s+(parseFloat(l.this_period_amount)||0),0)

  // ═══ Equipment IPC filtered ═══
  // Only list contracts that actually have at least one IPC (not all approved contracts)
  const eqContractsWithIpc = (() => {
    const seen = new Map()
    eqIpcs.forEach(i => {
      if (i.equipment_contract_id && !seen.has(i.equipment_contract_id)) {
        seen.set(i.equipment_contract_id, i.equipment_contracts?.contract_no || '—')
      }
    })
    return [...seen.entries()].map(([id, contract_no]) => ({ id, contract_no })).sort((a,b)=>a.contract_no.localeCompare(b.contract_no))
  })()

  const eqFiltered = eqIpcs.filter(i=>{
    if(eqFilterStatus!=='all' && i.status!==eqFilterStatus) return false
    if(eqFilterContract!=='all' && i.equipment_contract_id!==eqFilterContract) return false
    if(eqFilterPO && !(i.equipment_contracts?.rental_equipment_pos?.po_number||'').toLowerCase().includes(eqFilterPO.toLowerCase())) return false
    if(eqFilterGross && Math.round(parseFloat(i.gross_amount)||0) !== Math.round(parseFloat(eqFilterGross))) return false
    if(eqFilterRet && Math.round(parseFloat(i.retention_amount||i.retention_held)||0) !== Math.round(parseFloat(eqFilterRet))) return false
    if(eqFilterNet && Math.round(parseFloat(i.net_payable)||0) !== Math.round(parseFloat(eqFilterNet))) return false
    if(eqSearchText) {
      const q = eqSearchText.toLowerCase()
      const matchContract = (i.equipment_contracts?.contract_no||'').toLowerCase().includes(q)
      const matchContractor = (i.equipment_contracts?.contractor_name||'').toLowerCase().includes(q)
      const matchIpc = String(i.ipc_number||'').includes(q)
      if(!matchContract && !matchContractor && !matchIpc) return false
    }
    return true
  })

  // Re-check horizontal overflow for both floating navs whenever the
  // visible row count changes or the window resizes.
  useEffect(() => {
    const check = (ref, fn) => {
      fn()
      if (!ref.current) return null
      const ro = new ResizeObserver(fn)
      ro.observe(ref.current)
      return ro
    }
    const roMat = check(ipcMatScrollRef, updateIpcMatScrollState)
    const roEq = check(ipcEqScrollRef, updateIpcEqScrollState)
    const onResize = () => { updateIpcMatScrollState(); updateIpcEqScrollState() }
    window.addEventListener('resize', onResize)
    return () => { roMat?.disconnect(); roEq?.disconnect(); window.removeEventListener('resize', onResize) }
  }, [filtered.length, eqFiltered.length, updateIpcMatScrollState, updateIpcEqScrollState])

  // ── PROJECT SELECTOR ───────────────────────────────────────
  if(!projectId) return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-[#1C1A18] mb-6">📄 IPC Certificates</h1>
      <p className="text-[#6B655C] mb-4">Select a project to continue</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {projects.map(p=>(
          <button key={p.id} onClick={()=>{ setProjectId(p.id); setProjectName(p.name); fetchAll(p.id) }}
            className="text-left bg-white border border-[#DDD9D0] rounded-xl p-4 hover:border-green-400 hover:shadow-sm transition-all">
            <p className="font-semibold text-[#1C1A18]">{p.name}</p>
            <p className="text-sm text-[#6B655C]">{p.organizations?.name}</p>
          </button>
        ))}
      </div>
    </div>
  )

  // ── WIZARD VIEW ────────────────────────────────────────────
  if(wizard) {
    const { step, form, lines } = wizard
    const totals = wizardTotals()
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={()=>setWizard(null)} className="text-[#6B655C] hover:text-[#2E2C29] text-sm">← Back</button>
          <h1 className="text-xl font-bold text-[#1C1A18]">
            {step===1 ? '📄 New IPC — Step 1: معلومات المستخلص' : '📄 New IPC — Step 2: بنود المستخلص'}
          </h1>
          <div className="flex gap-1 ml-auto">
            {[1,2].map(s=>(
              <div key={s} className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step===s?'bg-green-600 text-white':'bg-gray-200 text-[#6B655C]'}`}>{s}</div>
            ))}
          </div>
        </div>

        {step===1 ? (
          <div className="bg-[#EDE7DA] rounded-2xl border border-[#DDD9D0] p-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-xs text-[#6B655C] mb-1">العقد * (Approved فقط)</label>
                {/* Searchable contract picker */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="ابحث برقم العقد أو اسم المقاول أو رقم PO..."
                    value={contractSearch}
                    onChange={e=>setContractSearch(e.target.value)}
                    className="w-full border border-gray-300 rounded-t-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:outline-none"
                  />
                  {form.subcontract_id && !contractSearch && (
                    <div className="w-full border border-t-0 border-gray-300 rounded-b-lg px-3 py-2 text-sm bg-green-50 text-green-800 font-medium flex justify-between items-center">
                      <span>{(()=>{ const c=contracts.find(x=>x.id===form.subcontract_id); return c?`${c.purchase_orders?.po_number?c.purchase_orders.po_number+' / ':''}${c.contract_no} — ${c.contractor_name}`:'' })()}</span>
                      <button type="button" onClick={()=>setWizard(w=>({...w,form:{...w.form,subcontract_id:''}}))} className="text-[#9A948C] hover:text-red-500 ml-2 text-xs">✕</button>
                    </div>
                  )}
                  {contractSearch && (
                    <div className="absolute z-20 w-full bg-white border border-t-0 border-gray-300 rounded-b-lg shadow-lg max-h-56 overflow-y-auto">
                      {contracts.filter(c=>{
                        const q=contractSearch.toLowerCase()
                        return c.contract_no?.toLowerCase().includes(q)||c.contractor_name?.toLowerCase().includes(q)||(c.purchase_orders?.po_number||'').toLowerCase().includes(q)
                      }).length===0
                        ? <div className="px-3 py-3 text-xs text-[#9A948C]">لا توجد نتائج</div>
                        : contracts.filter(c=>{
                            const q=contractSearch.toLowerCase()
                            return c.contract_no?.toLowerCase().includes(q)||c.contractor_name?.toLowerCase().includes(q)||(c.purchase_orders?.po_number||'').toLowerCase().includes(q)
                          }).map(c=>{
                            const isClosed = ipcs.some(i => i.subcontract_id === c.id && i.is_final && ['approved','paid'].includes(i.status))
                            return (
                            <button key={c.id} type="button" disabled={isClosed}
                              onClick={()=>{ if(isClosed) return; setWizard(w=>({...w,form:{...w.form,subcontract_id:c.id}})); setContractSearch('') }}
                              className={`w-full text-left px-3 py-2.5 text-sm border-b border-[#E8E4DC] last:border-0 ${isClosed?'opacity-50 cursor-not-allowed bg-red-50':'hover:bg-green-50'}`}>
                              <span className="font-medium text-[#1C1A18]">{c.purchase_orders?.po_number?`${c.purchase_orders.po_number} / `:''}{c.contract_no}</span>
                              <span className="text-[#6B655C] ml-2">— {c.contractor_name}</span>
                              {isClosed && <span className="text-red-600 text-xs font-semibold ml-2">🔒 Contract Closed (Final IPC issued)</span>}
                            </button>
                          )})
                      }
                    </div>
                  )}
                </div>
                {contracts.length===0 && <p className="text-xs text-orange-500 mt-1">⚠ لا يوجد عقود Approved.</p>}
              </div>
              <div>
                <label className="block text-xs text-[#6B655C] mb-1">Certificate No *</label>
                <input type="number" min="1" value={form.ipc_number} readOnly disabled
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-100 text-[#6B655C] cursor-not-allowed" />
              </div>
              <div>
                <label className="block text-xs text-[#6B655C] mb-1">Submission Date</label>
                <input type="date" value={form.submission_date}
                  onChange={e=>setWizard(w=>({...w,form:{...w.form,submission_date:e.target.value}}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="block text-xs text-[#6B655C] mb-1">Period From *</label>
                <input type="date" value={form.period_from}
                  onChange={e=>setWizard(w=>({...w,form:{...w.form,period_from:e.target.value}}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="block text-xs text-[#6B655C] mb-1">Period To *</label>
                <input type="date" value={form.period_to}
                  onChange={e=>setWizard(w=>({...w,form:{...w.form,period_to:e.target.value}}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="block text-xs text-[#6B655C] mb-1">Retention % (from contract)</label>
                <div className="relative">
                  <input type="text" readOnly disabled
                    value={contracts.find(c=>c.id===form.subcontract_id)?.retention_pct ?? '—'}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-100 text-[#6B655C] cursor-not-allowed pr-7" />
                  <span className="absolute right-3 top-2.5 text-xs text-[#9A948C]">%</span>
                </div>
              </div>
              <div>
                <label className="block text-xs text-[#6B655C] mb-1">Advance Deduction % (from contract)</label>
                <div className="relative">
                  <input type="text" readOnly disabled
                    value={contracts.find(c=>c.id===form.subcontract_id)?.advance_pct ?? '—'}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-100 text-[#6B655C] cursor-not-allowed pr-7" />
                  <span className="absolute right-3 top-2.5 text-xs text-[#9A948C]">%</span>
                </div>
              </div>
              <div className="col-span-2">
                <label className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 cursor-pointer">
                  <input type="checkbox" checked={!!form.is_final}
                    onChange={e=>setWizard(w=>({...w,form:{...w.form,is_final:e.target.checked}}))}
                    className="w-4 h-4 accent-red-600" />
                  <span className="text-sm font-medium text-red-700">هذا هو المستخلص النهائي (Final IPC) — سيتم قفل العقد نهائيًا ولن يمكن إصدار أي مستخلص آخر عليه بعد اعتماده</span>
                </label>
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-[#6B655C] mb-1">Notes</label>
                <textarea value={form.notes} rows={2}
                  onChange={e=>setWizard(w=>({...w,form:{...w.form,notes:e.target.value}}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500" />
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button onClick={wizardStep1Next}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium">
                Next: أدخل الكميات →
              </button>
            </div>
          </div>
        ) : (
          /* ── STEP 2: LINES ── */
          <div>
            {/* Summary bar */}
            <div className="grid grid-cols-4 gap-3 mb-3">
              {[
                {label:'Gross',          value:`${totals.currency} ${fmtN(totals.gross)}`,          color:'text-green-700'},
                {label:`VAT ${((totals.vatRate||0.15)*100).toFixed(0)}%`, value:`${totals.currency} ${fmtN(totals.vat)}`,            color:'text-blue-600'},
                {label:'Total Inc VAT',  value:`${totals.currency} ${fmtN(totals.totalIncVat)}`,    color:'text-blue-800'},
                {label:`Retention ${totals.retPct}%`, value:`${totals.currency} ${fmtN(totals.ret)}`, color:'text-red-500'},
              ].map(s=>(
                <div key={s.label} className="bg-[#EDE7DA] rounded-xl border border-[#DDD9D0] p-3">
                  <p className="text-xs text-[#6B655C]">{s.label}</p>
                  <p className={`text-base font-bold ${s.color}`}>{s.value}</p>
                </div>
              ))}
            </div>
            {/* Adv Pay row */}
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 col-span-2">
                <p className="text-xs text-amber-700 mb-1">Advance Payment Status</p>
                <div className="grid grid-cols-3 gap-3 mt-1">
                  <div>
                    <p className="text-[10px] text-amber-600">Total Advance</p>
                    <p className="text-sm font-bold text-amber-700">SAR {fmtN(totals.totalAdvPay)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-amber-600">Recovered So Far</p>
                    <p className="text-sm font-bold text-amber-700">SAR {fmtN(totals.prevRecoveries + totals.advRecovery)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-amber-600">Remaining</p>
                    <p className={`text-sm font-bold ${totals.outstanding>0?'text-orange-700':'text-green-700'}`}>SAR {fmtN(totals.outstanding)}</p>
                  </div>
                </div>
                <div className="mt-1.5">
                  <div className="w-full bg-amber-100 rounded-full h-1.5 overflow-hidden">
                    <div className="bg-amber-600 h-full rounded-full" style={{width: `${totals.totalAdvPay>0 ? Math.min(100,((totals.prevRecoveries+totals.advRecovery)/totals.totalAdvPay*100)) : 0}%`}}></div>
                  </div>
                  <p className="text-[10px] text-amber-600 mt-1">{totals.totalAdvPay>0 ? Math.round((totals.prevRecoveries+totals.advRecovery)/totals.totalAdvPay*100) : 0}% recovered{totals.outstanding===0 && totals.totalAdvPay>0 ? ' — ✓ Fully Recovered':''}</p>
                </div>
              </div>
              <div className="bg-[#EDE7DA] border border-[#DDD9D0] rounded-xl p-3">
                <p className="text-xs text-[#6B655C]">{totals.vatCase===2 ? 'Adv Recovery + VAT' : 'Advance Recovery'}</p>
                <p className="text-base font-bold text-orange-600">SAR {fmtN(totals.vatCase===2 ? totals.totalAdvIncVat : totals.advRecovery)}</p>
                {totals.vatCase===2 && <p className="text-xs text-[#9CA3AF] mt-0.5">Recovery: {fmtN(totals.advRecovery)} + VAT: {fmtN(totals.advRecoveryVat)}</p>}
              </div>
              <div className="bg-[#1A7A4A] rounded-xl p-3">
                <p className="text-xs text-green-200">Net Payable</p>
                <p className="text-base font-bold text-white">SAR {fmtN(totals.net)}</p>
                <p className="text-xs text-green-300 mt-0.5">Gross + VAT − Retention − Advance Recovery{totals.totalDeductions>0?' − Deductions':''}</p>
              </div>
            </div>
            {totals.totalDeductions>0 && (
              <div className="grid grid-cols-1 gap-3 mb-3">
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-3">
                  <p className="text-xs text-rose-700">Total Deductions</p>
                  <p className="text-base font-bold text-rose-700">SAR {fmtN(totals.totalDeductions)}</p>
                </div>
              </div>
            )}

            {/* Contract Value Ceiling row */}
            <div className="grid grid-cols-4 gap-3 mb-3">
              <div className="bg-[#EDE7DA] border border-[#DDD9D0] rounded-xl p-3">
                <p className="text-xs text-[#6B655C]">Contract Value</p>
                <p className="text-base font-bold text-[#2E2C29]">SAR {fmtN(totals.contractValue)}</p>
              </div>
              <div className="bg-[#EDE7DA] border border-[#DDD9D0] rounded-xl p-3">
                <p className="text-xs text-[#6B655C]">Previously Certified</p>
                <p className="text-base font-bold text-[#2E2C29]">SAR {fmtN(totals.prevCertified)}</p>
              </div>
              <div className="bg-[#EDE7DA] border border-[#DDD9D0] rounded-xl p-3">
                <p className="text-xs text-[#6B655C]">Remaining Contract Value</p>
                <p className="text-base font-bold text-[#2E2C29]">SAR {fmtN(totals.remainingContractValue)}</p>
              </div>
              <div className={`rounded-xl border p-3 ${totals.overContractValue?'bg-red-50 border-red-300':'bg-green-50 border-green-200'}`}>
                <p className={`text-xs ${totals.overContractValue?'text-red-700':'text-green-700'}`}>Cumulative After This IPC</p>
                <p className={`text-base font-bold ${totals.overContractValue?'text-red-700':'text-green-700'}`}>SAR {fmtN(totals.cumulativeCertified)}</p>
                {totals.overContractValue && <p className="text-xs text-red-600 mt-0.5 font-semibold">⚠ Exceeds Contract Value</p>}
              </div>
            </div>
            {totals.overContractValue && (
              <div className="bg-red-50 border border-red-300 rounded-xl p-3 mb-3 flex items-center gap-2">
                <span className="text-red-600 text-lg">⚠️</span>
                <p className="text-sm text-red-700 font-medium">
                  تحذير: إجمالي المستخلصات المعتمدة (شامل هذا المستخلص) هيتخطى قيمة العقد بمقدار SAR {fmtN(totals.cumulativeCertified - totals.contractValue)}. يُنصح بمراجعة الكميات أو عمل Variation Order قبل الاعتماد.
                </p>
              </div>
            )}

            <div className="bg-[#EDE7DA] rounded-xl border border-[#DDD9D0] overflow-hidden mb-5">
              <div className="px-5 py-3 bg-[#EDE7DA] border-b border-[#DDD9D0] flex items-center justify-between">
                <h3 className="font-semibold text-[#2E2C29]">بنود العقد — أدخل الكميات المنفذة هذه الفترة</h3>
                <span className="text-xs text-[#9A948C]">{lines.filter(l=>parseFloat(l.this_period_qty)>0).length} بند مع كميات</span>
              </div>
              <div className="overflow-auto" style={{maxHeight:'50vh'}}>
                <table className="w-full text-sm">
                  <thead className="bg-[#F5F1EA] text-xs text-[#6B655C] border-b border-[#E8E4DC] sticky top-0 z-10">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Sub Code</th>
                      <th className="text-left px-4 py-2 font-medium">Cost Code</th>
                      <th className="text-left px-4 py-2 font-medium text-purple-700">Resource Code</th>
                      <th className="text-left px-4 py-2 font-medium">Description</th>
                      <th className="text-center px-4 py-2 font-medium">Unit</th>
                      <th className="text-right px-4 py-2 font-medium">BOQ Qty</th>
                      <th className="text-right px-4 py-2 font-medium">Unit Rate</th>
                      <th className="text-right px-4 py-2 font-medium w-36">This Period Qty</th>
                      <th className="text-right px-4 py-2 font-medium">Amount</th>
                      <th className="px-4 py-2 font-medium">Actual Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E8E4DC]">
                    {lines.map((l,i)=>(
                      <tr key={l.po_line_item_id} className={parseFloat(l.this_period_qty)>0?'bg-green-50':''}>
                        <td className="px-4 py-2 font-mono text-xs text-[#1A7A4A]">
                          {l.sub_code || l.po_line_items?.sub_code || '—'}
                          {l.sub_code_desc && <div className="text-[10px] text-[#9CA3AF] font-sans">{l.sub_code_desc}</div>}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-[#6B655C]">
                          {l.cost_code_label || '—'}
                          {l.cost_code_desc && <div className="text-[10px] text-[#9CA3AF] font-sans">{l.cost_code_desc}</div>}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-purple-700">
                          {l.resource_code_label || '—'}
                          {l.resource_code_desc && <div className="text-[10px] text-[#9CA3AF] font-sans">{l.resource_code_desc}</div>}
                        </td>
                        <td className="px-4 py-2 font-medium text-[#1C1A18]">{l.description}</td>
                        <td className="px-4 py-2 text-center text-[#6B655C]">{l.unit||'—'}</td>
                        <td className="px-4 py-2 text-right text-[#6B655C]">{fmt(l.boq_qty)}</td>
                        <td className="px-4 py-2 text-right text-[#6B655C]">{fmt(l.unit_rate)}</td>
                        <td className="px-4 py-2 text-right">
                          <input type="number" min="0" step="0.01"
                            value={l.this_period_qty}
                            onChange={e=>wizardUpdateQty(i, e.target.value)}
                            className="w-32 border border-gray-300 rounded px-2 py-1 text-right text-sm focus:ring-1 focus:ring-green-500 focus:border-green-500"
                            placeholder="0" />
                        </td>
                        <td className="px-4 py-2 text-right font-bold text-green-700">
                          {fmtN(l.this_period_amount)}
                        </td>
                        <td className="px-4 py-2">
                          <input type="date" value={l.actual_cost_date||'2026-04-30'}
                            onChange={e=>setWizard(prev=>({...prev, lines: prev.lines.map((x,ii)=>ii===i?{...x,actual_cost_date:e.target.value}:x)}))}
                            className="border border-[#E5E7EB] rounded px-2 py-1 text-xs w-32 focus:outline-none focus:border-[#1A7A4A]"/>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-[#F5F1EA] border-t-2 border-[#DDD9D0]">
                    <tr>
                      <td colSpan={5} className="px-4 py-3 font-medium text-[#6B655C]">Total</td>
                      <td className="px-4 py-3 text-right font-bold text-green-700">{fmtN(totals.gross)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Deductions section */}
            <div className="bg-[#EDE7DA] rounded-xl border border-[#DDD9D0] overflow-hidden mb-5">
              <div className="px-5 py-3 bg-[#EDE7DA] border-b border-[#DDD9D0] flex items-center justify-between">
                <h3 className="font-semibold text-[#2E2C29]">Deductions (اختياري)</h3>
                <button type="button"
                  onClick={()=>setWizard(w=>({...w, deductions:[...w.deductions,{deduction_type:'material',description:'',amount:''}]}))}
                  className="text-xs px-3 py-1.5 rounded-lg bg-[#1A7A4A] text-white font-medium hover:bg-[#15633B]">+ Add Deduction</button>
              </div>
              {wizard.deductions.length>0 && (
                <div className="divide-y divide-[#E8E4DC]">
                  {wizard.deductions.map((d,i)=>(
                    <div key={i} className="flex items-center gap-2 px-4 py-2">
                      <select value={d.deduction_type}
                        onChange={e=>setWizard(w=>({...w, deductions:w.deductions.map((x,ii)=>ii===i?{...x,deduction_type:e.target.value}:x)}))}
                        className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm w-32">
                        <option value="material">Material</option>
                        <option value="equipment">Equipment</option>
                        <option value="labors">Labors</option>
                        <option value="other">Other</option>
                      </select>
                      <input type="text" placeholder="Description (optional)" value={d.description}
                        onChange={e=>setWizard(w=>({...w, deductions:w.deductions.map((x,ii)=>ii===i?{...x,description:e.target.value}:x)}))}
                        className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
                      <input type="number" min="0" step="0.01" placeholder="Amount" value={d.amount}
                        onChange={e=>setWizard(w=>({...w, deductions:w.deductions.map((x,ii)=>ii===i?{...x,amount:e.target.value}:x)}))}
                        className="w-32 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right" />
                      <button type="button" onClick={()=>setWizard(w=>({...w, deductions:w.deductions.filter((_,ii)=>ii!==i)}))}
                        className="text-[#9A948C] hover:text-red-500 px-1">✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-between">
              <button onClick={()=>setWizard(w=>({...w,step:1}))}
                className="px-5 py-2 border border-gray-300 rounded-lg hover:bg-[#F5F1EA] text-sm">
                ← Back
              </button>
              <div className="flex gap-3">
                <button onClick={()=>wizardSave(false)} disabled={saving}
                  className="px-5 py-2 border border-green-300 text-green-700 rounded-lg hover:bg-green-50 text-sm disabled:opacity-50">
                  {saving?'Saving...':'Save as Draft'}
                </button>
                <button onClick={()=>wizardSave(true)} disabled={saving||totals.gross===0}
                  className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm disabled:opacity-50">
                  {saving?'Saving...':'✓ Submit for Approval'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ── DETAIL VIEW ────────────────────────────────────────────
  if(selectedIpc) {
    const contract = contracts.find(c=>c.id===selectedIpc.subcontract_id)
    const notAdded = selectedPoLines.filter(pl=>!selectedLines.find(sl=>sl.po_line_item_id===pl.id))
    return (
      <>
      <div className="p-6 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={()=>setSelectedIpc(null)} className="text-green-600 hover:text-green-800 text-sm">← Back</button>
            <div>
              <h1 className="text-xl font-bold text-[#1C1A18]">
                IPC-{String(selectedIpc.ipc_number).padStart(4,'0')} — {selectedIpc.subcontracts?.contractor_name}
              </h1>
              <p className="text-sm text-[#6B655C]">{selectedIpc.subcontracts?.contract_no} | {selectedIpc.period_from} → {selectedIpc.period_to}</p>
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[selectedIpc.status]}`}>
              {(selectedIpc.approval_stage||'draft').replace('_',' ')}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getCommercialStatus(selectedIpc).color}`}>
              {getCommercialStatus(selectedIpc).emoji} {getCommercialStatus(selectedIpc).label}
            </span>
            {selectedIpc.is_final && <span className="px-2 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">FINAL</span>}
            {/* Phase 3: Edit is now available for draft, rejected, AND
                revoked — matching the DB trigger's ipc_financial_edit_guard
                allowed-states exactly (draft/rejected/revoked). Previously
                this button only appeared for 'draft', which meant a
                rejected or revoked IPC could never be corrected before
                resubmission from the UI, even though nothing technically
                prevented it once the DB-side rule was fixed — the UI was
                the last remaining gap.
                Bugfix: role check was 'contract_manager', a role that was
                NEVER selectable from the Role dropdown (options are only
                commercial_engineer/cost_control/director/site) — this
                button (and Delete, below) was therefore unreachable by any
                real user regardless of role chosen. Confirmed by direct UI
                testing. There is no "Contract Manager" role concept in
                IPC's business model; edit/delete authority belongs to the
                same commercial_engineer role that creates/uploads IPCs. */}
            {can('IPC.Delete') && ['draft','rejected','revoked'].includes(selectedIpc.approval_stage||'draft') && (
              <button onClick={openEditModal}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-[#F5F1EA]">✏️ {(selectedIpc.approval_stage==='rejected'||selectedIpc.approval_stage==='revoked') ? 'تصحيح المستخلص' : 'Edit'}</button>
            )}
            {getIpcApprovalButtons(selectedIpc).btns.map(b => (
              <button key={b.action}
                onClick={()=> b.action==='reject' ? setRejectIpc(selectedIpc) : handleIpcApproval(selectedIpc, b.action)}
                className={`px-4 py-2 text-sm rounded-lg font-medium ${b.color}`}>{b.label}</button>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div style={{display:'flex',borderBottom:'1px solid #E5E7EB',background:'#fff',padding:'0 24px',marginBottom:16}}>
          {[{id:'lines',label:'Line Items'},{id:'audit',label:'Audit Trail'}].map(t=>(
            <button key={t.id} onClick={()=>setIpcDetailTab(t.id)}
              style={{padding:'10px 18px',fontSize:12,fontWeight:ipcDetailTab===t.id?700:500,color:ipcDetailTab===t.id?'#0F1017':'#6B7280',background:'transparent',border:'none',borderBottom:`2px solid ${ipcDetailTab===t.id?'#1A7A4A':'transparent'}`,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap'}}>
              {t.label}
            </button>
          ))}
        </div>
        {ipcDetailTab==='audit' && (
          <div style={{padding:'0 24px'}}><IPCAuditTrail entityId={selectedIpc.id}/></div>
        )}
        {ipcDetailTab==='lines' && <div>

        {/* Summary */}
        {(() => {
          const t = computeIpcTotals(selectedIpc)
          return (
            <>
              {/* 1. Contract context */}
              <div className="grid grid-cols-4 gap-3 mb-3">
                <div className="bg-[#EDE7DA] border border-[#DDD9D0] rounded-xl p-4">
                  <p className="text-xs text-[#6B655C] mb-1">Contract Value</p>
                  <p className="text-lg font-bold text-[#2E2C29]">SAR {fmtN(t.contractValue)}</p>
                </div>
                <div className="bg-[#EDE7DA] border border-[#DDD9D0] rounded-xl p-4">
                  <p className="text-xs text-[#6B655C] mb-1">Previously Certified</p>
                  <p className="text-lg font-bold text-[#2E2C29]">SAR {fmtN(t.prevCertified)}</p>
                </div>
                <div className="bg-[#EDE7DA] border border-[#DDD9D0] rounded-xl p-4">
                  <p className="text-xs text-[#6B655C] mb-1">Remaining Contract Value</p>
                  <p className="text-lg font-bold text-[#2E2C29]">SAR {fmtN(t.remainingContractValue)}</p>
                </div>
                <div className={`rounded-xl border p-4 ${t.overContractValue?'bg-red-50 border-red-300':'bg-green-50 border-green-200'}`}>
                  <p className={`text-xs mb-1 ${t.overContractValue?'text-red-700':'text-green-700'}`}>Cumulative After This IPC</p>
                  <p className={`text-lg font-bold ${t.overContractValue?'text-red-700':'text-green-700'}`}>SAR {fmtN(t.cumulativeCertified)}</p>
                </div>
              </div>

              {/* 2. This IPC's own figures */}
              <div className="grid grid-cols-3 gap-3 mb-3">
                {[
                  {label:'Gross Amount',   value:`${t.currency} ${fmtN(t.gross)}`,        color:'text-green-700'},
                  {label:`VAT ${((t.vatRate||0.15)*100).toFixed(0)}%`,        value:`${t.currency} ${fmtN(t.vat)}`,          color:'text-blue-600'},
                  {label:'Total Inc VAT',  value:`${t.currency} ${fmtN(t.totalIncVat)}`,  color:'text-blue-800'},
                ].map(s=>(
                  <div key={s.label} className="bg-[#EDE7DA] rounded-xl border border-[#DDD9D0] p-4">
                    <p className="text-xs text-[#6B655C] mb-1">{s.label}</p>
                    <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
                  </div>
                ))}
              </div>

              {/* 3. Retention */}
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-3">
                <p className="text-xs text-red-700 mb-1">Retention ({t.retPct}%)</p>
                <div className="grid grid-cols-2 gap-3 mt-1">
                  <div>
                    <p className="text-[10px] text-red-500">This IPC</p>
                    <p className="text-lg font-bold text-red-600">SAR {fmtN(t.ret)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-red-500">Cumulative Held (all IPCs)</p>
                    <p className="text-lg font-bold text-red-700">SAR {fmtN(t.cumulativeRetentionHeld)}</p>
                  </div>
                </div>
              </div>

              {/* 4. Advance payment status + recovery */}
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 col-span-2">
                  <p className="text-xs text-amber-700 mb-1">Advance Payment Status</p>
                  <div className="grid grid-cols-3 gap-3 mt-2">
                    <div>
                      <p className="text-[10px] text-amber-600">Total Advance</p>
                      <p className="text-base font-bold text-amber-700">SAR {fmtN(t.totalAdvPay)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-amber-600">Recovered So Far</p>
                      <p className="text-base font-bold text-amber-700">SAR {fmtN(t.prevRecoveries + t.advRecovery)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-amber-600">Remaining</p>
                      <p className={`text-base font-bold ${t.outstanding>0?'text-orange-700':'text-green-700'}`}>SAR {fmtN(t.outstanding)}</p>
                    </div>
                  </div>
                  <div className="mt-2">
                    <div className="w-full bg-amber-100 rounded-full h-1.5 overflow-hidden">
                      <div className="bg-amber-600 h-full rounded-full" style={{width: `${t.totalAdvPay>0 ? Math.min(100,((t.prevRecoveries+t.advRecovery)/t.totalAdvPay*100)) : 0}%`}}></div>
                    </div>
                    <p className="text-[10px] text-amber-600 mt-1">{t.totalAdvPay>0 ? Math.round((t.prevRecoveries+t.advRecovery)/t.totalAdvPay*100) : 0}% recovered{t.outstanding===0 && t.totalAdvPay>0 ? ' — ✓ Fully Recovered':''}</p>
                  </div>
                </div>
                <div className="bg-[#EDE7DA] border border-[#DDD9D0] rounded-xl p-4">
                  <p className="text-xs text-[#6B655C] mb-1">{t.vatCase===2 ? 'Adv Recovery + VAT' : 'Advance Recovery'}</p>
                  <p className="text-lg font-bold text-orange-600">SAR {fmtN(t.vatCase===2 ? t.totalAdvIncVat : t.advRecovery)}</p>
                  {t.vatCase===2
                    ? <p className="text-xs text-[#9CA3AF] mt-0.5">{fmtN(t.advRecovery)} + VAT {fmtN(t.advRecoveryVat)}</p>
                    : <p className="text-xs text-[#9CA3AF] mt-0.5">Rate: {t.advRecoveryRate}% {t.hasRealAdvPayment ? '(locked — real payment on file)' : ''}</p>
                  }
                </div>
              </div>

              {/* 5. Total Deductions — always shown, even at zero, to keep the flow to Net Payable clear */}
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 mb-3">
                <p className="text-xs text-rose-700 mb-1">Total Deductions</p>
                <p className="text-lg font-bold text-rose-700">SAR {fmtN(t.otherDeductions)}</p>
              </div>

              {/* 6. Net Payable — final result */}
              <div className="bg-[#1A7A4A] rounded-xl p-4 mb-5">
                <p className="text-xs text-green-200 mb-1">Net Payable</p>
                <p className="text-lg font-bold text-white">SAR {fmtN(t.net)}</p>
                <p className="text-xs text-green-300 mt-0.5">Gross + VAT − Retention − Advance Recovery{t.otherDeductions>0?' − Deductions':''}</p>
              </div>

              {(selectedIpc._deductions||[]).length > 0 && (
                <div className="bg-white border border-rose-200 rounded-xl p-4 mb-3">
                  <p className="text-sm font-semibold text-rose-700 mb-2">Deductions — Total: SAR {fmtN(t.otherDeductions)}</p>
                  <div className="divide-y divide-rose-100">
                    {selectedIpc._deductions.map(d=>(
                      <div key={d.id} className="flex items-center justify-between py-1.5 text-sm">
                        <div>
                          <span className="inline-block px-2 py-0.5 rounded bg-rose-100 text-rose-700 text-xs font-medium capitalize mr-2">{d.deduction_type}</span>
                          <span className="text-[#6B655C]">{d.description || '—'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-rose-700">SAR {fmtN(d.amount)}</span>
                          {selectedIpc.approval_stage==='draft' && (
                            <button onClick={()=>removeIpcDeduction(d.id, false)} className="text-[#9A948C] hover:text-red-500">✕</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selectedIpc.approval_stage==='draft' && (
                <div className="bg-white border border-[#DDD9D0] rounded-xl p-4 mb-5 flex items-center gap-2">
                  <select value={newDedForm.deduction_type} onChange={e=>setNewDedForm(p=>({...p,deduction_type:e.target.value}))}
                    className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm w-32">
                    <option value="material">Material</option>
                    <option value="equipment">Equipment</option>
                    <option value="labors">Labors</option>
                    <option value="other">Other</option>
                  </select>
                  <input type="text" placeholder="Description (optional)" value={newDedForm.description}
                    onChange={e=>setNewDedForm(p=>({...p,description:e.target.value}))}
                    className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
                  <input type="number" min="0" step="0.01" placeholder="Amount" value={newDedForm.amount}
                    onChange={e=>setNewDedForm(p=>({...p,amount:e.target.value}))}
                    className="w-32 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right" />
                  <button onClick={async()=>{ await addIpcDeduction(selectedIpc.id, newDedForm, false); setNewDedForm({deduction_type:'material',description:'',amount:''}) }}
                    className="px-3 py-1.5 text-xs rounded-lg bg-[#1A7A4A] text-white font-medium hover:bg-[#15633B]">+ Add Deduction</button>
                </div>
              )}
            </>
          )
        })()}

        {/* Add more lines (draft only) */}
        {selectedIpc.status==='draft' && notAdded.length>0 && (
          <div className="bg-[#EDE7DA] rounded-xl border border-[#DDD9D0] mb-4 overflow-hidden">
            <div className="px-4 py-3 bg-[#F5F1EA] border-b border-[#E8E4DC] flex items-center justify-between">
              <h3 className="font-semibold text-[#2E2C29] text-sm">+ إضافة بنود من العقد</h3>
              <span className="text-xs text-[#9A948C]">{notAdded.length} بند متاح</span>
            </div>
            <div className="divide-y divide-[#E8E4DC] max-h-40 overflow-y-auto">
              {notAdded.map(pl=>(
                <div key={pl.id} className="flex items-center justify-between px-4 py-2">
                  <div>
                    <span className="text-sm text-[#1C1A18]">{pl.description}</span>
                    <span className="text-xs text-[#9A948C] ml-2">{pl.unit} | {fmt(pl.unit_rate)}</span>
                  </div>
                  <button onClick={()=>addLineToExisting(pl)}
                    className="text-xs px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700">+ Add</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lines table */}
        <div className="bg-[#EDE7DA] rounded-xl border border-[#DDD9D0] overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8E4DC]">
            <h3 className="font-semibold text-[#2E2C29]">بنود المستخلص</h3>
            <div className="flex items-center gap-3">
              {selectedIpc?.status === 'draft' && selectedLines.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#6B655C] font-medium">Bulk Date:</span>
                  <input type="date" value={bulkDate} onChange={e=>setBulkDate(e.target.value)}
                    className="border border-[#DDD9D0] rounded px-2 py-1 text-xs focus:outline-none focus:border-[#1A7A4A]"/>
                  <button disabled={!bulkDate||bulkUpdating}
                    onClick={()=>applyBulkDateToLines(selectedLines.map(l=>l.id))}
                    className="px-3 py-1 bg-[#1A7A4A] text-white text-xs font-bold rounded hover:bg-[#15693F] disabled:opacity-40">
                    {bulkUpdating ? '...' : 'Apply All'}
                  </button>
                </div>
              )}
              <span className="font-bold text-green-700">Total: SAR {fmtN(selLinesTotal)}</span>
            </div>
          </div>
          {selectedLines.length===0 ? (
            <div className="py-10 text-center text-[#9A948C] text-sm">لا يوجد بنود</div>
          ) : (
            <div className="overflow-auto" style={{maxHeight:'50vh'}}>
              <table className="w-full text-sm">
                <thead className="bg-[#F5F1EA] text-xs text-[#6B655C] border-b border-[#E8E4DC] sticky top-0 z-10">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Sub Code</th>
                    <th className="text-left px-4 py-2 font-medium">Cost Code</th>
                    <th className="text-left px-4 py-2 font-medium text-purple-700">Resource Code</th>
                    <th className="text-left px-4 py-2 font-medium">Description</th>
                    <th className="text-center px-4 py-2 font-medium">Unit</th>
                    <th className="text-right px-4 py-2 font-medium">BOQ Qty</th>
                    <th className="text-right px-4 py-2 font-medium">Unit Rate</th>
                    <th className="text-right px-4 py-2 font-medium">This Period Qty</th>
                    <th className="text-right px-4 py-2 font-medium">Amount</th>
                    <th className="px-4 py-2 font-medium">Actual Date</th>
                    {selectedIpc.status==='draft' && <th className="px-4 py-2"></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E8E4DC]">
                  {selectedLines.map(line=>(
                    <tr key={line.id} className="hover:bg-[#F5F1EA]">
                      <td className="px-4 py-2 font-mono text-xs text-[#1A7A4A]">
                        {line.sub_code || line.po_line_items?.sub_code || '—'}
                        {line.sub_code_desc && <div className="text-[10px] text-[#9CA3AF] font-sans">{line.sub_code_desc}</div>}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-[#6B655C]">
                        {line.cost_codes?.code || '—'}
                        {line.cost_codes?.description && <div className="text-[10px] text-[#9CA3AF] font-sans">{line.cost_codes.description}</div>}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-purple-700">
                        {line.resource_code_info?.code || '—'}
                        {line.resource_code_info?.description && <div className="text-[10px] text-[#9CA3AF] font-sans">{line.resource_code_info.description}</div>}
                      </td>
                      <td className="px-4 py-2 font-medium">{line.description}</td>
                      <td className="px-4 py-2 text-center text-[#6B655C]">{line.unit||'—'}</td>
                      <td className="px-4 py-2 text-right text-[#6B655C]">{fmt(line.boq_qty)}</td>
                      <td className="px-4 py-2 text-right text-[#6B655C]">{fmt(line.unit_rate)}</td>
                      <td className="px-4 py-2 text-right">
                        {selectedIpc.status==='draft' ? (
                          <input type="number" min="0" step="0.01"
                            defaultValue={line.this_period_qty}
                            onBlur={e=>updateExistingLine(line.id,e.target.value)}
                            className="w-28 border border-gray-300 rounded px-2 py-1 text-right text-sm focus:ring-1 focus:ring-green-500" />
                        ) : (
                          <span>{fmt(line.this_period_qty)}</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right font-bold text-green-700">{fmtN(line.this_period_amount)}</td>
                      <td className="px-4 py-2">
                        {selectedIpc.status==='draft' ? (
                          <input type="date" defaultValue={line.actual_cost_date||'2026-04-30'}
                            onBlur={async e=>{
                              await supabase.from('ipc_line_items').update({actual_cost_date:e.target.value}).eq('id',line.id)
                              const newLines = await fetchIpcLines(selectedIpc.id)
                              setSelectedLines(newLines)
                            }}
                            className="border border-[#E5E7EB] rounded px-2 py-1 text-xs w-32 focus:outline-none focus:border-[#1A7A4A]"/>
                        ) : (
                          <span className="text-xs text-[#6B7280]">{line.actual_cost_date||'—'}</span>
                        )}
                      </td>
                      {selectedIpc.status==='draft' && (
                        <td className="px-4 py-2 text-center">
                          <button onClick={()=>deleteExistingLine(line.id)} className="text-gray-300 hover:text-red-500 text-lg">×</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-[#F5F1EA] border-t-2 border-[#DDD9D0]">
                  <tr>
                    <td colSpan={selectedIpc.status==='draft'?6:6} className="px-4 py-3 font-medium">Total</td>
                    <td className="px-4 py-3 text-right font-bold text-green-700">{fmtN(selLinesTotal)}</td>
                    {selectedIpc.status==='draft' && <td></td>}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          {(selectedIpc.approval_stage||'draft')==='draft' && can('IPC.Delete') && (
            <div className="p-4 bg-[#EDE7DA] border-t border-[#1A7A4A]/30 flex justify-end">
              <button onClick={()=>handleIpcApproval(selectedIpc, 'submit')}
                className="px-5 py-2 bg-[#1A7A4A] text-white text-sm rounded-lg hover:bg-[#156637] font-medium">
                Submit for Approval →
              </button>
            </div>
          )}
          {selectedIpc.approval_stage==='approved' && (
            <div className="p-4 bg-green-50 border-t border-green-200">
              <span className="text-sm text-green-700 font-medium">
                ✓ Approved {selectedIpc.approved_date&&`on ${selectedIpc.approved_date}`} — SAR {fmtN(selectedIpc.gross_amount)} recorded as Actual Cost
              </span>
            </div>
          )}
        </div>
        </div>}
      </div>

      {/* Edit IPC Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#DDD9D0]">
              <h2 className="font-bold text-[#1C1A18]">✏️ تعديل المستخلص — IPC-{String(editForm.ipc_number).padStart(4,'0')}</h2>
              <button onClick={()=>setShowEditModal(false)} className="text-[#9A948C] hover:text-[#6B655C] text-2xl leading-none">×</button>
            </div>

            {/* IPC Info */}
            <div className="px-6 py-4 border-b border-[#E8E4DC] grid grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-[#6B655C] mb-1">IPC Number</label>
                <input type="number" value={editForm.ipc_number}
                  onChange={e=>setEditForm(f=>({...f, ipc_number: parseInt(e.target.value)||0}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-green-500"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B655C] mb-1">Submission Date</label>
                <input type="date" value={editForm.submission_date}
                  onChange={e=>setEditForm(f=>({...f, submission_date: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-green-500"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B655C] mb-1">Period From</label>
                <input type="date" value={editForm.period_from}
                  onChange={e=>setEditForm(f=>({...f, period_from: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-green-500"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B655C] mb-1">Period To</label>
                <input type="date" value={editForm.period_to}
                  onChange={e=>setEditForm(f=>({...f, period_to: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-green-500"/>
              </div>
            </div>

            {/* Lines Table */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F1EA] text-xs text-[#6B655C]">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Sub Code</th>
                    <th className="text-left px-3 py-2 font-medium">Cost Code</th>
                    <th className="text-left px-3 py-2 font-medium text-purple-700">Resource Code</th>
                    <th className="text-left px-3 py-2 font-medium">Description</th>
                    <th className="text-center px-3 py-2 font-medium">Unit</th>
                    <th className="text-right px-3 py-2 font-medium">Unit Rate</th>
                    <th className="text-right px-3 py-2 font-medium w-32">Qty</th>
                    <th className="text-right px-3 py-2 font-medium">Amount</th>
                    <th className="px-3 py-2 font-medium">Actual Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E8E4DC]">
                  {(editForm.lines||[]).map((l, idx) => {
                    const amt = +(l.this_period_qty * l.unit_rate).toFixed(2)
                    return (
                      <tr key={l.id} className="hover:bg-[#F5F1EA]">
                        <td className="px-3 py-2 font-mono text-xs text-[#1A7A4A]">
                          {l.sub_code}
                          {l.sub_code_desc && <div className="text-[10px] text-[#9CA3AF] font-sans">{l.sub_code_desc}</div>}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-[#6B655C]">
                          {l.cost_code_label || '—'}
                          {l.cost_code_desc && <div className="text-[10px] text-[#9CA3AF] font-sans">{l.cost_code_desc}</div>}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-purple-700">
                          {l.resource_code_label || '—'}
                          {l.resource_code_desc && <div className="text-[10px] text-[#9CA3AF] font-sans">{l.resource_code_desc}</div>}
                        </td>
                        <td className="px-3 py-2 text-[#2E2C29] max-w-xs truncate">{l.description}</td>
                        <td className="px-3 py-2 text-center text-[#6B655C]">{l.unit}</td>
                        <td className="px-3 py-2 text-right text-[#6B655C]">{l.unit_rate.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number" min="0" step="0.01"
                            value={l.this_period_qty}
                            onChange={e => setEditForm(f => ({
                              ...f,
                              lines: f.lines.map((ll, i) => i === idx
                                ? { ...ll, this_period_qty: parseFloat(e.target.value) || 0 }
                                : ll
                              )
                            }))}
                            className="w-28 border border-gray-300 rounded px-2 py-1 text-right text-sm focus:ring-2 focus:ring-green-500"
                          />
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${amt > 0 ? 'text-green-700' : 'text-[#9A948C]'}`}>
                          {amt.toLocaleString()}
                        </td>
                        <td className="px-3 py-2">
                          <input type="date" value={l.actual_cost_date||'2026-04-30'}
                            onChange={e => setEditForm(f => ({
                              ...f,
                              lines: f.lines.map((ll, i) => i === idx
                                ? { ...ll, actual_cost_date: e.target.value }
                                : ll
                              )
                            }))}
                            className="border border-[#E5E7EB] rounded px-2 py-1 text-xs w-32 focus:outline-none focus:border-[#1A7A4A]"/>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot className="bg-[#F5F1EA] border-t-2 border-[#DDD9D0]">
                  <tr>
                    <td colSpan={5} className="px-3 py-3 font-medium text-[#6B655C]">Gross Total</td>
                    <td className="px-3 py-3 text-right font-bold text-green-700">
                      {(editForm.lines||[]).reduce((s,l) => s + l.this_period_qty * l.unit_rate, 0).toLocaleString(undefined, {maximumFractionDigits:2})}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Footer */}
            <div className="flex items-center gap-4 px-6 py-4 border-t border-[#DDD9D0] bg-[#F5F1EA] rounded-b-2xl">
              <div className="flex gap-3 items-center flex-1">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-[#6B655C]">Ret%</label>
                  <input type="number" value={editForm.retention_pct}
                    onChange={e=>setEditForm(f=>({...f, retention_pct: e.target.value}))}
                    className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-center focus:ring-2 focus:ring-green-500"/>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-[#6B655C]">Adv%</label>
                  <input type="number" value={editForm.advance_pct}
                    onChange={e=>setEditForm(f=>({...f, advance_pct: e.target.value}))}
                    className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-center focus:ring-2 focus:ring-green-500"/>
                </div>
                <input type="text" placeholder="Notes" value={editForm.notes}
                  onChange={e=>setEditForm(f=>({...f, notes: e.target.value}))}
                  className="flex-1 border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-green-500"/>
              </div>
              <button onClick={()=>setShowEditModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-[#6B655C] hover:bg-[#EDE7DA]">إلغاء</button>
              <button onClick={saveEditIpc}
                className="px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700">💾 حفظ</button>
            </div>
          </div>
        </div>
      )}
      </>
    )
  }

  // ── MAIN LIST ──────────────────────────────────────────────
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold text-[#1C1A18]">📄 IPC Certificates</h1>
          <button onClick={()=>{ setProjectId(null) }}
            className="text-xs text-[#9A948C] hover:text-[#6B655C] border border-[#DDD9D0] rounded px-2 py-0.5">
            {projectName} ×
          </button>
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex items-center gap-1 bg-white border border-[#DDD9D0] rounded-lg p-1">
            <button onClick={()=>setMainTab('material')}
              className={`px-4 py-1.5 rounded text-xs font-medium transition-colors ${mainTab==='material'?'bg-[#1A7A4A] text-white':'text-[#6B655C] hover:bg-[#F5F1EA]'}`}>
              📦 Material
            </button>
            <button onClick={()=>setMainTab('equipment')}
              className={`px-4 py-1.5 rounded text-xs font-medium transition-colors ${mainTab==='equipment'?'bg-orange-500 text-white':'text-[#6B655C] hover:bg-[#F5F1EA]'}`}>
              🚜 Equipment
            </button>
          </div>
          {mainTab === 'material' ? (
            <>
              <button onClick={downloadIpcTemplate} className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-[#F5F1EA]">📥 Template</button>
              {can('IPC.Create') && (
                <button onClick={()=>{ setShowUpload(!showUpload); setUploadResult(null) }} className="px-3 py-2 text-sm border border-green-300 text-green-600 rounded-lg hover:bg-green-50">↑ Bulk Upload</button>
              )}
              <button onClick={exportToExcel} className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-[#F5F1EA]">↓ Export</button>
              {can('IPC.Create') && (
                <button onClick={openNewWizard} className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">+ New IPC</button>
              )}
            </>
          ) : (
            <>
              <button onClick={eqDownloadIpcTemplate} className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-[#F5F1EA]">📥 Template</button>
              {can('IPC.Create') && (
                <button onClick={()=>{ setEqShowUpload(!eqShowUpload); setEqUploadResult(null) }} className="px-3 py-2 text-sm border border-green-300 text-green-600 rounded-lg hover:bg-green-50">↑ Bulk Upload</button>
              )}
              <button onClick={eqExportToExcel} className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-[#F5F1EA]">↓ Export</button>
              {can('IPC.Create') && (
                <button onClick={eqOpenNewWizard} className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600">+ New Equipment IPC</button>
              )}
            </>
          )}
        </div>
      </div>

      {mainTab === 'equipment' ? (
        <>
          {/* Equipment Bulk Upload Panel */}
          {eqShowUpload && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-5 mb-5">
              <h3 className="font-semibold text-orange-800 mb-1">رفع Opening Balance من Excel</h3>
              <p className="text-xs text-orange-700 mb-3">
                بيقبل الملف الأصلي مباشرة — الأعمدة المطلوبة: <strong>PO Number (per Item)</strong> و <strong>invoiced amount</strong> — القيم بتتجمع تلقائياً لكل PO وتتحفظ كـ Opening Balance بحالة Approved
              </p>
              <div className="flex items-center gap-3 mb-3">
                <input ref={eqFileRef} type="file" accept=".xlsx,.xls,.csv"
                  onChange={handleEqIpcFileUpload}
                  className="text-sm text-[#6B655C] file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border file:border-orange-300 file:text-orange-700 file:bg-white file:text-sm file:hover:bg-orange-50" />
                <button onClick={eqDownloadIpcTemplate}
                  className="text-xs px-3 py-1.5 border border-orange-300 text-orange-700 rounded-lg hover:bg-orange-100">
                  📥 Template
                </button>
              </div>
              {eqUploadError && <p className="text-red-500 text-sm mb-2">{eqUploadError}</p>}
              {eqUploadResult && (
                <div className={`text-sm font-medium mb-3 px-3 py-2 rounded-lg ${eqUploadResult.failed===0?'bg-green-100 text-green-700':'bg-orange-100 text-orange-700'}`}>
                  ✅ {eqUploadResult.success} تم | ⏭ {eqUploadResult.skipped} متكرر/مش موجود | ❌ {eqUploadResult.failed} فشل
                  {eqUploadResult.rejectedDetails?.length > 0 && (
                    <ul className="mt-2 text-xs font-normal list-disc list-inside space-y-0.5">
                      {eqUploadResult.rejectedDetails.map((msg, i) => <li key={i}>{msg}</li>)}
                    </ul>
                  )}
                </div>
              )}
              {eqUploadRows.length > 0 && (
                <div>
                  <p className="text-sm text-orange-700 mb-2">تم قراءة <strong>{eqUploadRows.length}</strong> سجل — معاينة أول 3:</p>
                  <div className="overflow-x-auto mb-3">
                    <table className="text-xs border border-orange-200 rounded-lg overflow-hidden">
                      <thead className="bg-orange-100">
                        <tr>
                          <th className="px-2 py-1 text-left font-medium text-orange-700">PO Number</th>
                          <th className="px-2 py-1 text-left font-medium text-orange-700">Supplier</th>
                          <th className="px-2 py-1 text-right font-medium text-orange-700">Invoiced Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eqUploadRows.slice(0,3).map((r,i)=>(
                          <tr key={i} className="border-t border-orange-100 bg-white">
                            <td className="px-2 py-1 text-[#6B655C]">{String(r['PO Number (per Item)'] || r.po_number || '')}</td>
                            <td className="px-2 py-1 text-[#6B655C] max-w-48 truncate">{String(r['Supplier Name'] || r.supplier_name || '')}</td>
                            <td className="px-2 py-1 text-right font-medium text-orange-700">
                              {(parseFloat(
                                Object.entries(r).find(([k]) => k.toLowerCase().includes('invoiced'))?.[1] ?? 0
                              )).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button onClick={eqConfirmIpcUpload} disabled={eqUploading}
                    className="px-4 py-2 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2">
                    {eqUploading ? <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>Uploading...</> : `✓ تأكيد الرفع (${eqUploadRows.length} سجل)`}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Equipment Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              {label:'Total IPCs',       value:eqFiltered.length,                                                                                          color:'text-orange-600'},
              {label:'Pending Approval', value:eqFiltered.filter(i=>['submitted','under_review'].includes(i.status)).length,                               color:'text-yellow-600'},
              // "SAR" kept literal here deliberately: this sums gross_amount
              // across ALL filtered IPCs, which may belong to contracts in
              // different transaction currencies. Summing raw amounts across
              // currencies and labeling the result in any single currency
              // would be a real (if differently-shaped) correctness problem
              // -- currency-aware aggregation is out of scope for this
              // change and would require its own explicit design.
              ...(() => {
                const certifiedList = eqFiltered.filter(i=>['approved','paid'].includes(i.status))
                const paidList = eqFiltered.filter(i=>i.status==='paid')
                const certified = sumIpcsInProjectCurrency(certifiedList, 'gross_amount', ipc => ipc.equipment_contracts)
                const paid = sumIpcsInProjectCurrency(paidList, 'net_payable', ipc => ipc.equipment_contracts)
                const pc = projects.find(p => p.id === projectId)?.currency || 'SAR'
                return [
                  { label:`Total Certified (${pc})`, value: certified.hasIncomplete ? `${pc} ${fmtN(certified.total)} *` : `${pc} ${fmtN(certified.total)}`, color:'text-green-600' },
                  { label:`Total Paid (${pc})`,       value: paid.hasIncomplete ? `${pc} ${fmtN(paid.total)} *` : `${pc} ${fmtN(paid.total)}`,   color:'text-[#2E2C29]' },
                ]
              })(),
            ].map(s=>(
              <div key={s.label} className="bg-[#EDE7DA] rounded-xl border border-[#DDD9D0] p-4">
                <p className="text-xs text-[#6B655C] mb-1">{s.label}</p>
                <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* Equipment Search + Filter */}
          <div className="flex gap-2 mb-4">
            <input value={eqSearchText} onChange={e=>setEqSearchText(e.target.value)} placeholder="Search IPC number, contract..."
              className="flex-1 max-w-sm border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm" />
            <select value={eqFilterStatus} onChange={e=>setEqFilterStatus(e.target.value)}
              className="border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm">
              {['all','draft','submitted','under_review','approved','rejected','paid'].map(s=>(
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Equipment IPC Table */}
          <div className="bg-white rounded-xl border border-[#DDD9D0] overflow-hidden">
            {eqFiltered.length === 0 ? (
              <div className="p-10 text-center text-[#9A948C]">لا يوجد مستخلصات معدات</div>
            ) : (
              <>
              {/* Phase 3: Bulk Approval bar for Equipment — same design as Material. */}
              {eqBulkSelectedIds.size > 0 && (
                <div className="px-4 py-3 bg-blue-50 border-b border-blue-200 flex items-center gap-3 flex-wrap">
                  <span className="text-sm font-medium text-blue-900">{eqBulkSelectedIds.size} محدد</span>
                  <button onClick={()=>{ const v = validateBulkSelection(eqBulkSelectedIds, eqIpcs); setEqBulkValidation(v) }}
                    className="px-3 py-1.5 text-sm bg-white border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-100">
                    تحقق من التحديد
                  </button>
                  {eqBulkValidation && (
                    eqBulkValidation.allValid ? (
                      <button onClick={()=>runBulkApprove(true)} disabled={eqBulkApproving}
                        className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                        {eqBulkApproving ? 'جاري الاعتماد...' : `✓ اعتماد الكل (${eqBulkSelectedIds.size})`}
                      </button>
                    ) : (
                      <span className="text-sm text-red-600">✗ لا يمكن اعتماد الدفعة — راجع الأسباب أدناه</span>
                    )
                  )}
                  <button onClick={()=>{ setEqBulkSelectedIds(new Set()); setEqBulkValidation(null) }}
                    className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700">إلغاء التحديد</button>
                  {eqBulkValidation && !eqBulkValidation.allValid && (
                    <div className="w-full mt-1 text-xs text-red-700 space-y-0.5">
                      {eqBulkValidation.results.filter(r=>!r.ok).map(r=>(
                        <div key={r.id}>• IPC-{String(r.ipc_number).padStart(4,'0')}: {r.reason}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div ref={ipcEqScrollRef} onScroll={updateIpcEqScrollState} className="overflow-auto" style={{maxHeight:'70vh'}}>
              <table className="text-sm" style={{width:'100%',minWidth:2300}}>
                <thead className="sticky top-0 z-10">
                  <tr className="bg-[#F5F1EA] border-b border-[#DDD9D0]">
                    <th className="px-2 py-2">
                      <input type="checkbox"
                        checked={eqFiltered.length > 0 && eqFiltered.every(i => eqBulkSelectedIds.has(i.id))}
                        onChange={e=>{
                          const next = new Set(eqBulkSelectedIds)
                          if (e.target.checked) eqFiltered.forEach(i => next.add(i.id))
                          else eqFiltered.forEach(i => next.delete(i.id))
                          setEqBulkSelectedIds(next); setEqBulkValidation(null)
                        }}
                        className="w-4 h-4"/>
                    </th>
                    <th className="text-left px-3 py-2 font-medium text-[#6B655C]">
                      <div className="flex flex-col gap-1">
                        <span>Contract</span>
                        <select value={eqFilterContract} onChange={e=>setEqFilterContract(e.target.value)}
                          className="text-xs border border-[#DDD9D0] rounded px-1 py-0.5 font-normal bg-white">
                          <option value="all">All</option>
                          {eqContractsWithIpc.map(c=><option key={c.id} value={c.id}>{c.contract_no}</option>)}
                        </select>
                      </div>
                    </th>
                    <th className="text-left px-3 py-2 font-medium text-[#6B655C]">
                      <div className="flex flex-col gap-1">
                        <span>PO No</span>
                        <input value={eqFilterPO} onChange={e=>setEqFilterPO(e.target.value)}
                          placeholder="Filter..." className="text-xs border border-[#DDD9D0] rounded px-1 py-0.5 font-normal w-20"/>
                      </div>
                    </th>
                    <th className="text-left px-3 py-2 font-medium text-[#6B655C]">
                      <div className="flex flex-col gap-1">
                        <span>IPC No</span>
                        <input value={eqSearchText} onChange={e=>setEqSearchText(e.target.value)}
                          placeholder="Filter..." className="text-xs border border-[#DDD9D0] rounded px-1 py-0.5 font-normal w-16"/>
                      </div>
                    </th>
                    <th className="text-left px-3 py-2 font-medium text-[#6B655C]">Period</th>
                    <th className="text-left px-3 py-2 font-medium text-[#6B655C]">Actual Date</th>
                    <th className="text-right px-4 py-2 font-medium text-[#6B655C]">
                      <div className="flex flex-col gap-1 items-end">
                        <span>Gross</span>
                        <input type="number" value={eqFilterGross} onChange={e=>setEqFilterGross(e.target.value)}
                          placeholder="= value" className="text-xs border border-[#DDD9D0] rounded px-1 py-0.5 font-normal w-24 text-right"/>
                      </div>
                    </th>
                    <th className="text-right px-4 py-2 font-medium text-blue-600">VAT</th>
                    <th className="text-right px-4 py-2 font-medium text-blue-800">Total Inc VAT</th>
                    <th className="text-right px-4 py-2 font-medium text-amber-600">Adv Pay</th>
                    <th className="text-right px-4 py-2 font-medium text-orange-600">Adv Recovery</th>
                    <th className="text-right px-4 py-2 font-medium text-orange-500">Adv VAT</th>
                    <th className="text-right px-4 py-2 font-medium text-orange-700">Total Adv Inc VAT</th>
                    <th className="text-right px-4 py-2 font-medium text-[#6B655C]">
                      <div className="flex flex-col gap-1 items-end">
                        <span>Retention</span>
                        <input type="number" value={eqFilterRet} onChange={e=>setEqFilterRet(e.target.value)}
                          placeholder="= value" className="text-xs border border-[#DDD9D0] rounded px-1 py-0.5 font-normal w-24 text-right"/>
                      </div>
                    </th>
                    <th className="text-right px-4 py-2 font-medium text-[#6B655C]">
                      <div className="flex flex-col gap-1 items-end">
                        <span>Net Payable</span>
                        <input type="number" value={eqFilterNet} onChange={e=>setEqFilterNet(e.target.value)}
                          placeholder="= value" className="text-xs border border-[#DDD9D0] rounded px-1 py-0.5 font-normal w-24 text-right"/>
                      </div>
                    </th>
                    <th className="text-center px-4 py-2 font-medium text-[#6B655C]">
                      <div className="flex flex-col gap-1">
                        <span>Stage</span>
                        <select value={eqFilterStatus} onChange={e=>setEqFilterStatus(e.target.value)}
                          className="text-xs border border-[#DDD9D0] rounded px-1 py-0.5 font-normal bg-white">
                          <option value="all">All</option>
                          {STATUSES.map(s=><option key={s} value={s}>{s.replace('_',' ')}</option>)}
                        </select>
                      </div>
                    </th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E8E4DC]">
                  {eqFiltered.map(ipc => {
                    const stage = ipc.approval_stage || 'draft'
                    const { btns } = getEqIpcApprovalButtons(ipc)
                    return (
                      <tr key={ipc.id} className="hover:bg-[#F5F1EA]">
                        <td className="px-2 py-3 text-center">
                          <input type="checkbox" checked={eqBulkSelectedIds.has(ipc.id)}
                            onChange={e=>{
                              const next = new Set(eqBulkSelectedIds)
                              if (e.target.checked) next.add(ipc.id); else next.delete(ipc.id)
                              setEqBulkSelectedIds(next); setEqBulkValidation(null)
                            }}
                            className="w-4 h-4"/>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{ipc.equipment_contracts?.contract_no}</div>
                          <div className="text-xs text-[#9A948C]">{ipc.equipment_contracts?.contractor_name}</div>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-[#6B655C]">
                          {ipc.equipment_contracts?.rental_equipment_pos?.po_number||'—'}
                        </td>
                        <td className="px-4 py-3 font-mono text-orange-600 font-bold cursor-pointer hover:underline"
                            onClick={()=>eqOpenDetail(ipc)}>
                          IPC-{String(ipc.ipc_number).padStart(4,'0')}
                        </td>
                        <td className="px-4 py-3 text-xs text-[#6B655C]">
                          {ipc.period_from&&ipc.period_to?`${ipc.period_from} → ${ipc.period_to}`:ipc.period_month||'—'}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          <input type="date" defaultValue={ipc._actual_date||''}
                            onBlur={e => { if(e.target.value !== ipc._actual_date) eqUpdateActualDateForIpc(ipc.id, e.target.value) }}
                            className="w-32 border border-gray-200 rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-orange-400"/>
                        </td>
                        <td className="px-4 py-3 text-right font-medium">{fmtN(ipc.gross_amount)}</td>
                        <td className="px-4 py-3 text-right text-blue-600">{fmtN(ipc.vat_amount != null ? ipc.vat_amount : (parseFloat(ipc.gross_amount)||0)*((parseFloat(ipc.equipment_contracts?.vat_pct)||15)/100))}</td>
                        <td className="px-4 py-3 text-right text-blue-800 font-medium">{fmtN(ipc.total_inc_vat != null ? ipc.total_inc_vat : (parseFloat(ipc.gross_amount)||0)*(1+(parseFloat(ipc.equipment_contracts?.vat_pct)||15)/100))}</td>
                        <td className="px-4 py-3 text-right text-amber-600">{fmtN(eqAdvPayByContract[ipc.equipment_contract_id]||0)}</td>
                        <td className="px-4 py-3 text-right text-orange-600">{fmtN(ipc.advance_recovery||ipc.advance_deduction)}</td>
                        <td className="px-4 py-3 text-right text-orange-500">{fmtN(ipc.advance_recovery_vat != null ? ipc.advance_recovery_vat : (parseFloat(ipc.advance_recovery||ipc.advance_deduction)||0)*((parseFloat(ipc.equipment_contracts?.vat_pct)||15)/100))}</td>
                        <td className="px-4 py-3 text-right text-orange-700">{fmtN(ipc.total_adv_inc_vat != null ? ipc.total_adv_inc_vat : (parseFloat(ipc.advance_recovery||ipc.advance_deduction)||0)*(1+(parseFloat(ipc.equipment_contracts?.vat_pct)||15)/100))}</td>
                        <td className="px-4 py-3 text-right text-red-500">{fmtN(ipc.retention_amount||ipc.retention_held)}</td>
                        <td className="px-4 py-3 text-right font-bold text-[#1A7A4A]">{fmtN(ipc.net_payable)}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[stage]||'bg-gray-100'}`}>{stage}</span>
                          <span className="ml-1" title={getCommercialStatus(ipc).label}>{getCommercialStatus(ipc).emoji}</span>
                          {ipc.is_final && <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">FINAL</span>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 justify-end flex-wrap">
                            <button onClick={()=>eqOpenDetail(ipc)} className="text-green-600 text-xs px-2 py-1 rounded border border-green-200 hover:bg-green-50">View</button>
                            <button onClick={()=>openContractHistory(ipc.equipment_contract_id, 'equipment')} className="text-orange-600 text-xs px-2 py-1 rounded border border-orange-200 hover:bg-orange-50">View All</button>
                            {btns.map(b => (
                              <button key={b.action}
                                onClick={()=> b.action==='reject' ? setEqRejectIpc(ipc) : eqHandleIpcApproval(ipc, b.action)}
                                className={`text-xs px-2 py-1 rounded-lg font-medium ${b.color}`}>{b.label}</button>
                            ))}
                            <DocButton entityId={ipc.id} entityType="equipment_ipc" projectId={ipc.project_id}/>
                            {can('IPC.Delete') && (ipc.approval_stage || 'draft') === 'draft' && (
                              <button onClick={()=>eqDeleteIPC(ipc.id, ipc.approval_stage)} className="text-[#9A948C] hover:text-red-500">🗑</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              </div>
              {ipcEqScrollState.hasOverflow && (
                <div style={{
                  position:'fixed', bottom:0, left:ipcEqNavLeftRight.left, right:ipcEqNavLeftRight.right, zIndex:50,
                  display:'flex', alignItems:'center', gap:8,
                  padding:'6px 12px', background:'rgba(255,255,255,0.97)',
                  borderTop:'1px solid #E5E7EB', backdropFilter:'blur(2px)',
                }}>
                  <button onClick={()=>ipcEqScrollBy(-1)} disabled={ipcEqScrollState.atStart} aria-label="Scroll table left"
                    style={{width:24,height:24,borderRadius:6,border:'1px solid #E5E7EB',background:ipcEqScrollState.atStart?'#F9FAFB':'#fff',color:ipcEqScrollState.atStart?'#D1D5DB':'#6B7280',cursor:ipcEqScrollState.atStart?'default':'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,flexShrink:0}}>‹</button>
                  <input type="range" min={0} max={1000} step={1} value={Math.round(ipcEqScrollState.ratio*1000)}
                    onChange={e=>ipcEqScrollToRatio(Number(e.target.value)/1000)} aria-label="Scroll table horizontally"
                    style={{flex:1,height:4,accentColor:'#1B3A6B',cursor:'pointer'}}/>
                  <button onClick={()=>ipcEqScrollBy(1)} disabled={ipcEqScrollState.atEnd} aria-label="Scroll table right"
                    style={{width:24,height:24,borderRadius:6,border:'1px solid #E5E7EB',background:ipcEqScrollState.atEnd?'#F9FAFB':'#fff',color:ipcEqScrollState.atEnd?'#D1D5DB':'#6B7280',cursor:ipcEqScrollState.atEnd?'default':'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,flexShrink:0}}>›</button>
                </div>
              )}
              </>
            )}
          </div>
        </>
      ) : (
      <>


      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          {label:'Total IPCs',       value:filtered.length,                                                                                          color:'text-[#1A7A4A]'},
          {label:'Pending Approval', value:filtered.filter(i=>['submitted','under_review'].includes(i.status)).length,                               color:'text-yellow-600'},
          ...(() => {
            const certifiedList = filtered.filter(i=>['approved','paid'].includes(i.status))
            const paidList = filtered.filter(i=>i.status==='paid')
            const certified = sumIpcsInProjectCurrency(certifiedList, 'gross_amount', ipc => ipc.subcontracts)
            const paid = sumIpcsInProjectCurrency(paidList, 'net_payable', ipc => ipc.subcontracts)
            const pc = projects.find(p => p.id === projectId)?.currency || 'SAR'
            return [
              { label:`Total Certified (${pc})`, value: certified.hasIncomplete ? `${pc} ${fmtN(certified.total)} *` : `${pc} ${fmtN(certified.total)}`, color:'text-green-600' },
              { label:`Total Paid (${pc})`,       value: paid.hasIncomplete ? `${pc} ${fmtN(paid.total)} *` : `${pc} ${fmtN(paid.total)}`,   color:'text-[#2E2C29]' },
            ]
          })(),
        ].map(s=>(
          <div key={s.label} className="bg-[#EDE7DA] rounded-xl border border-[#DDD9D0] p-4">
            <p className="text-xs text-[#6B655C] mb-1">{s.label}</p>
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Bulk Upload Panel */}
      {showUpload && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-5">
          <h3 className="font-semibold text-green-800 mb-1">رفع Opening Balance من Excel</h3>
          <p className="text-xs text-green-700 mb-3">
            بيقبل الملف الأصلي مباشرة — الأعمدة المطلوبة: <strong>PO Number (per Item)</strong> و <strong>invoiced amount</strong> — القيم بتتجمع تلقائياً لكل PO وتتحفظ كـ Opening Balance بحالة Approved
          </p>
          <div className="flex items-center gap-3 mb-3">
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv"
              onChange={handleIpcFileUpload}
              className="text-sm text-[#6B655C] file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border file:border-green-300 file:text-green-700 file:bg-white file:text-sm file:hover:bg-green-50" />
            <button onClick={downloadIpcTemplate}
              className="text-xs px-3 py-1.5 border border-green-300 text-green-700 rounded-lg hover:bg-green-100">
              📥 Template
            </button>
          </div>
          {uploadError && <p className="text-red-500 text-sm mb-2">{uploadError}</p>}
          {uploadResult && (
            <div className={`text-sm font-medium mb-3 px-3 py-2 rounded-lg ${uploadResult.failed===0?'bg-green-100 text-green-700':'bg-orange-100 text-orange-700'}`}>
              ✅ {uploadResult.success} تم | ⏭ {uploadResult.skipped} متكرر/مش موجود | ❌ {uploadResult.failed} فشل
              {uploadResult.rejectedDetails?.length > 0 && (
                <ul className="mt-2 text-xs font-normal list-disc list-inside space-y-0.5">
                  {uploadResult.rejectedDetails.map((msg, i) => <li key={i}>{msg}</li>)}
                </ul>
              )}
            </div>
          )}
          {uploadRows.length > 0 && (
            <div>
              <p className="text-sm text-green-700 mb-2">تم قراءة <strong>{uploadRows.length}</strong> سجل — معاينة أول 3:</p>
              <div className="overflow-x-auto mb-3">
                <table className="text-xs border border-green-200 rounded-lg overflow-hidden">
                  <thead className="bg-green-100">
                    <tr>
                      <th className="px-2 py-1 text-left font-medium text-green-700">PO Number</th>
                      <th className="px-2 py-1 text-left font-medium text-green-700">Supplier</th>
                      <th className="px-2 py-1 text-right font-medium text-green-700">Invoiced Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadRows.slice(0,3).map((r,i)=>(
                      <tr key={i} className="border-t border-green-100 bg-white">
                        <td className="px-2 py-1 text-[#6B655C]">{String(r['PO Number (per Item)'] || r.po_number || '')}</td>
                        <td className="px-2 py-1 text-[#6B655C] max-w-48 truncate">{String(r['Supplier Name'] || r.supplier_name || '')}</td>
                        <td className="px-2 py-1 text-right font-medium text-green-700">
                          {(parseFloat(
                            Object.entries(r).find(([k]) => k.toLowerCase().includes('invoiced'))?.[1] ?? 0
                          )).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button onClick={confirmIpcUpload} disabled={uploading}
                className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
                {uploading ? <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>Uploading...</> : `✓ تأكيد الرفع (${uploadRows.length} سجل)`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Filters - Excel Style */}
      <div className="flex gap-2 flex-wrap items-center mb-3">
        <input value={searchText} onChange={e=>setSearchText(e.target.value)}
          placeholder="🔍 ابحث..."
          className="pl-3 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 w-48"/>
        <span className="text-xs text-[#9A948C] ml-auto">{filtered.length} IPCs</span>
        {(filterStatus!=='all'||filterType!=='all'||filterType2!=='all'||filterContract!=='all'||searchText||filterGross||filterRet||filterNet||filterPO) &&
          <button onClick={()=>{setFilterStatus('all');setFilterType('all');setFilterType2('all');setFilterContract('all');setSearchText('');setFilterGross('');setFilterRet('');setFilterNet('');setFilterPO('')}}
            className="text-xs text-red-500 border border-red-200 px-2 py-1 rounded hover:bg-red-50">✕ Clear</button>
        }
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-[#9A948C]">Loading...</div>
      ) : filtered.length===0 ? (
        <div className="text-center py-16 bg-[#EDE7DA] rounded-xl border border-[#DDD9D0]">
          <div className="text-5xl mb-3">📄</div>
          <p className="text-[#6B655C]">لا يوجد مستخلصات.</p>
          {contracts.length===0 && <p className="text-xs text-orange-500 mt-2">⚠ أضف عقوداً بحالة Approved من Subcontracts أولاً.</p>}
        </div>
      ) : (
        <div className="bg-[#EDE7DA] rounded-xl border border-[#DDD9D0] overflow-hidden">
          {/* Phase 3: Bulk Approval bar — Validate All -> Approve All, no
              partial approval. Shown only when at least one row is
              selected. Selecting rows itself has no effect until
              "Validate Selected" is pressed. */}
          {bulkSelectedIds.size > 0 && (
            <div className="px-4 py-3 bg-blue-50 border-b border-blue-200 flex items-center gap-3 flex-wrap">
              <span className="text-sm font-medium text-blue-900">{bulkSelectedIds.size} محدد</span>
              <button onClick={()=>{ const v = validateBulkSelection(bulkSelectedIds, ipcs); setBulkValidation(v) }}
                className="px-3 py-1.5 text-sm bg-white border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-100">
                تحقق من التحديد
              </button>
              {bulkValidation && (
                bulkValidation.allValid ? (
                  <button onClick={()=>runBulkApprove(false)} disabled={bulkApproving}
                    className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                    {bulkApproving ? 'جاري الاعتماد...' : `✓ اعتماد الكل (${bulkSelectedIds.size})`}
                  </button>
                ) : (
                  <span className="text-sm text-red-600">✗ لا يمكن اعتماد الدفعة — راجع الأسباب أدناه</span>
                )
              )}
              <button onClick={()=>{ setBulkSelectedIds(new Set()); setBulkValidation(null) }}
                className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700">إلغاء التحديد</button>
              {bulkValidation && !bulkValidation.allValid && (
                <div className="w-full mt-1 text-xs text-red-700 space-y-0.5">
                  {bulkValidation.results.filter(r=>!r.ok).map(r=>(
                    <div key={r.id}>• IPC-{String(r.ipc_number).padStart(4,'0')}: {r.reason}</div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div ref={ipcMatScrollRef} onScroll={updateIpcMatScrollState} className="overflow-auto" style={{maxHeight:'70vh'}}>
          <table className="text-sm" style={{width:'100%',minWidth:2600}}>
            <thead className="bg-[#EDE7DA] border-b border-[#DDD9D0] sticky top-0 z-10">
              <tr>
                <th className="px-2 py-2">
                  <input type="checkbox"
                    checked={filtered.length > 0 && filtered.every(i => bulkSelectedIds.has(i.id))}
                    onChange={e=>{
                      const next = new Set(bulkSelectedIds)
                      if (e.target.checked) filtered.forEach(i => next.add(i.id))
                      else filtered.forEach(i => next.delete(i.id))
                      setBulkSelectedIds(next); setBulkValidation(null)
                    }}
                    className="w-4 h-4"/>
                </th>
                <th className="text-left px-3 py-2 font-medium text-[#6B655C]">
                  <div className="flex flex-col gap-1">
                    <span>Contract</span>
                    <select value={filterContract} onChange={e=>setFilterContract(e.target.value)}
                      className="text-xs border border-[#DDD9D0] rounded px-1 py-0.5 font-normal bg-white">
                      <option value="all">All</option>
                      {contracts.map(c=><option key={c.id} value={c.id}>{c.contract_no}</option>)}
                    </select>
                  </div>
                </th>
                <th className="text-left px-4 py-2 font-medium text-[#6B655C]">
                  <div className="flex flex-col gap-1">
                    <span>PO No</span>
                    <input value={filterPO} onChange={e=>setFilterPO(e.target.value)}
                      placeholder="Filter..."
                      className="text-xs border border-[#DDD9D0] rounded px-1 py-0.5 font-normal w-20"/>
                  </div>
                </th>
                <th className="text-left px-4 py-2 font-medium text-[#6B655C]">
                  <div className="flex flex-col gap-1">
                    <span>Type</span>
                    <select value={filterType} onChange={e=>setFilterType(e.target.value)}
                      className="text-xs border border-[#DDD9D0] rounded px-1 py-0.5 font-normal bg-white">
                      <option value="all">All</option>
                      <option value="oceanarium">Ocn</option>
                      <option value="logistics">Log</option>
                      <option value="eqp">EQP</option>
                    </select>
                  </div>
                </th>
                <th className="text-left px-4 py-2 font-medium text-[#6B655C]">
                  <div className="flex flex-col gap-1">
                    <span>Type 2</span>
                    <select value={filterType2} onChange={e=>setFilterType2(e.target.value)}
                      className="text-xs border border-[#DDD9D0] rounded px-1 py-0.5 font-normal bg-white">
                      <option value="all">All</option>
                      <option value="standard">Standard</option>
                      <option value="complex">Complex</option>
                      <option value="equipment">Equipment</option>
                      <option value="service">Service</option>
                    </select>
                  </div>
                </th>
                <th className="text-left px-4 py-2 font-medium text-[#6B655C]">
                  <div className="flex flex-col gap-1">
                    <span>IPC No</span>
                    <input value={searchText} onChange={e=>setSearchText(e.target.value)}
                      placeholder="Filter..."
                      className="text-xs border border-[#DDD9D0] rounded px-1 py-0.5 font-normal w-16"/>
                  </div>
                </th>
                <th className="text-left px-4 py-2 font-medium text-[#6B655C]">
                  <div className="flex flex-col gap-1">
                    <span>Period</span>
                    <input type="month" onChange={e=>{
                      const v = e.target.value
                      setSearchText(v ? v.replace('-','') : '')
                    }}
                      className="text-xs border border-[#DDD9D0] rounded px-1 py-0.5 font-normal"/>
                  </div>
                </th>
                <th className="text-left px-4 py-2 font-medium text-[#6B655C]">Actual Date</th>
                <th className="text-right px-4 py-2 font-medium text-[#6B655C]">
                  <div className="flex flex-col gap-1 items-end">
                    <span>Gross</span>
                    <input type="number" value={filterGross} onChange={e=>setFilterGross(e.target.value)}
                      placeholder="= value" className="text-xs border border-[#DDD9D0] rounded px-1 py-0.5 font-normal w-24 text-right"/>
                  </div>
                </th>
                <th className="text-right px-4 py-2 font-medium text-blue-600">VAT 15%</th>
                <th className="text-right px-4 py-2 font-medium text-blue-800">Total Inc VAT</th>
                <th className="text-right px-4 py-2 font-medium text-amber-600">Adv Pay</th>
                <th className="text-right px-4 py-2 font-medium text-orange-600">Adv Recovery</th>
                <th className="text-right px-4 py-2 font-medium text-orange-500">Adv VAT</th>
                <th className="text-right px-4 py-2 font-medium text-orange-700">Total Adv Inc VAT</th>
                <th className="text-right px-4 py-2 font-medium text-[#6B655C]">
                  <div className="flex flex-col gap-1 items-end">
                    <span>Retention</span>
                    <input type="number" value={filterRet} onChange={e=>setFilterRet(e.target.value)}
                      placeholder="= value" className="text-xs border border-[#DDD9D0] rounded px-1 py-0.5 font-normal w-24 text-right"/>
                  </div>
                </th>
                <th className="text-right px-4 py-2 font-medium text-[#6B655C]">
                  <div className="flex flex-col gap-1 items-end">
                    <span>Net Payable</span>
                    <input type="number" value={filterNet} onChange={e=>setFilterNet(e.target.value)}
                      placeholder="= value" className="text-xs border border-[#DDD9D0] rounded px-1 py-0.5 font-normal w-24 text-right"/>
                  </div>
                </th>
                <th className="text-center px-4 py-2 font-medium text-[#6B655C]">
                  <div className="flex flex-col gap-1">
                    <span>Status</span>
                    <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
                      className="text-xs border border-[#DDD9D0] rounded px-1 py-0.5 font-normal bg-white">
                      <option value="all">All</option>
                      {STATUSES.map(s=><option key={s} value={s}>{s.replace('_',' ')}</option>)}
                    </select>
                  </div>
                </th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E4DC]">
              {filtered.map(ipc=>(
                <tr key={ipc.id} className="hover:bg-[#F5F1EA]">
                  <td className="px-2 py-3 text-center">
                    <input type="checkbox" checked={bulkSelectedIds.has(ipc.id)}
                      onChange={e=>{
                        const next = new Set(bulkSelectedIds)
                        if (e.target.checked) next.add(ipc.id); else next.delete(ipc.id)
                        setBulkSelectedIds(next); setBulkValidation(null)
                      }}
                      className="w-4 h-4"/>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{ipc.subcontracts?.contract_no}</div>
                    <div className="text-xs text-[#9A948C]">{ipc.subcontracts?.contractor_name}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-[#6B655C]">
                    {ipc.subcontracts?.purchase_orders?.po_number||'—'}
                  </td>
                  <td className="px-4 py-3">
                    {(()=>{
                      const lt=(ipc.subcontracts?.logistics_type||'').toLowerCase()
                      if(lt==='eqp')       return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">EQP</span>
                      if(lt==='logistics') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">Log</span>
                      return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#DDD9D0] text-[#1A7A4A]">Ocn</span>
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    {(()=>{
                      const t2 = ipc.type_2 || ipc.subcontracts?.purchase_orders?.po_type
                      if(!t2) return <span className="text-gray-300">—</span>
                      const colors = {
                        standard:  'bg-gray-100 text-[#2E2C29]',
                        complex:   'bg-[#DDD9D0] text-[#1C1A18]',
                        equipment: 'bg-orange-100 text-orange-700',
                        service:   'bg-[#EDE7DA] text-[#6B655C]',
                      }
                      return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[t2]||'bg-gray-100 text-[#6B655C]'}`}>{t2}</span>
                    })()}
                  </td>
                  <td className="px-4 py-3 font-mono text-green-600 font-bold cursor-pointer hover:underline"
                      onClick={()=>openDetail(ipc)}>
                    IPC-{String(ipc.ipc_number).padStart(4,'0')}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#6B655C]">
                    {ipc.period_from&&ipc.period_to?`${ipc.period_from} → ${ipc.period_to}`:ipc.period_month||'—'}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <input type="date" defaultValue={ipc._actual_date||''}
                      onBlur={e => { if(e.target.value !== ipc._actual_date) updateActualDateForIpc(ipc.id, e.target.value) }}
                      className="w-32 border border-gray-200 rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#1A7A4A]"/>
                  </td>
                  <td className="px-4 py-3 text-right font-medium">{fmtN(ipc.gross_amount)}</td>
                  <td className="px-4 py-3 text-right text-blue-600">{fmtN(ipc.vat_amount != null ? ipc.vat_amount : (parseFloat(ipc.gross_amount)||0)*((parseFloat(ipc.subcontracts?.vat_pct)||15)/100))}</td>
                  <td className="px-4 py-3 text-right text-blue-800 font-medium">{fmtN(ipc.total_inc_vat != null ? ipc.total_inc_vat : (parseFloat(ipc.gross_amount)||0)*(1+(parseFloat(ipc.subcontracts?.vat_pct)||15)/100))}</td>
                  <td className="px-4 py-3 text-right text-amber-600">{(() => { const v = advPayByContract[ipc.subcontract_id]||0; if(v>0) console.log('[RENDER] IPC',ipc.id,'subcontract_id',ipc.subcontract_id,'advPay',v); return fmtN(v) })()}</td>
                  <td className="px-4 py-3 text-right text-orange-600">{fmtN(ipc.advance_recovery||ipc.advance_deduction)}</td>
                  <td className="px-4 py-3 text-right text-orange-500">{fmtN(ipc.advance_recovery_vat != null ? ipc.advance_recovery_vat : (parseFloat(ipc.advance_recovery||ipc.advance_deduction)||0)*((parseFloat(ipc.subcontracts?.vat_pct)||15)/100))}</td>
                  <td className="px-4 py-3 text-right text-orange-700">{fmtN(ipc.total_adv_inc_vat != null ? ipc.total_adv_inc_vat : (parseFloat(ipc.advance_recovery||ipc.advance_deduction)||0)*(1+(parseFloat(ipc.subcontracts?.vat_pct)||15)/100))}</td>
                  <td className="px-4 py-3 text-right text-red-500">{fmtN(ipc.retention_amount||ipc.retention_held)}</td>
                  <td className="px-4 py-3 text-right font-bold text-[#1A7A4A]">{fmtN(ipc.net_payable)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[ipc.status]||'bg-gray-100'}`}>
                      {(ipc.approval_stage||'draft').replace('_',' ')}
                    </span>
                    <span className="ml-1" title={getCommercialStatus(ipc).label}>{getCommercialStatus(ipc).emoji}</span>
                    {ipc.is_final && <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">FINAL</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end">
                      <button onClick={()=>openDetail(ipc)} className="text-green-600 text-xs px-2 py-1 rounded border border-green-200 hover:bg-green-50">View</button>
                      <button onClick={()=>openContractHistory(ipc.subcontract_id, 'material')} className="text-[#1A7A4A] text-xs px-2 py-1 rounded border border-[#1A7A4A]/30 hover:bg-[#EDE7DA]">View All</button>
                      {getIpcApprovalButtons(ipc).btns.map(b => (
                        <button key={b.action}
                          onClick={()=> b.action==='reject' ? setRejectIpc(ipc) : handleIpcApproval(ipc, b.action)}
                          className={`text-xs px-2 py-1 rounded border font-medium ${b.action==='approve'?'text-green-600 border-green-200 hover:bg-green-50':b.action==='reject'?'text-red-600 border-red-200 hover:bg-red-50':b.action==='revoke'?'text-amber-600 border-amber-200 hover:bg-amber-50':'text-[#1A7A4A] border-[#1A7A4A]/30 hover:bg-[#EDE7DA]'}`}>
                          {b.label}
                        </button>
                      ))}
                      <DocButton entityId={ipc.id} entityType="ipc" projectId={ipc.project_id}/>
                      {can('IPC.Delete') && (ipc.approval_stage || 'draft') === 'draft' && (
                        <button onClick={()=>deleteIPC(ipc.id, ipc.approval_stage)} className="text-[#9A948C] hover:text-red-500">🗑</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-[#F5F1EA] border-t-2 border-[#DDD9D0]">
              <tr>
                <td colSpan={4} className="px-4 py-3 text-sm font-medium">Total ({filtered.length})</td>
                <td className="px-4 py-3 text-right font-bold">{fmtN(filtered.reduce((s,i)=>s+(parseFloat(i.gross_amount)||0),0))}</td>
                <td className="px-4 py-3 text-right font-bold text-blue-600">{fmtN(filtered.reduce((s,i)=>s+(i.vat_amount != null ? parseFloat(i.vat_amount) : (parseFloat(i.gross_amount)||0)*((parseFloat(i.subcontracts?.vat_pct)||15)/100)),0))}</td>
                <td className="px-4 py-3 text-right font-bold text-blue-800">{fmtN(filtered.reduce((s,i)=>s+(i.total_inc_vat != null ? parseFloat(i.total_inc_vat) : (parseFloat(i.gross_amount)||0)*(1+(parseFloat(i.subcontracts?.vat_pct)||15)/100)),0))}</td>
                <td className="px-4 py-3"></td>
                <td className="px-4 py-3 text-right font-bold text-orange-600">{fmtN(filtered.reduce((s,i)=>s+(parseFloat(i.advance_recovery||i.advance_deduction)||0),0))}</td>
                <td className="px-4 py-3"></td>
                <td className="px-4 py-3 text-right font-bold text-orange-700">{fmtN(filtered.reduce((s,i)=>s+(i.total_adv_inc_vat != null ? parseFloat(i.total_adv_inc_vat) : (parseFloat(i.advance_recovery||i.advance_deduction)||0)*(1+(parseFloat(i.subcontracts?.vat_pct)||15)/100)),0))}</td>
                <td className="px-4 py-3 text-right font-bold text-red-500">{fmtN(filtered.reduce((s,i)=>s+(parseFloat(i.retention_amount||i.retention_held)||0),0))}</td>
                <td className="px-4 py-3 text-right font-bold text-[#1A7A4A]">{fmtN(filtered.reduce((s,i)=>s+(parseFloat(i.net_payable)||0),0))}</td>
                <td colSpan={3}></td>
              </tr>
            </tfoot>
          </table>
          </div>
          {ipcMatScrollState.hasOverflow && (
            <div style={{
              position:'fixed', bottom:0, left:ipcMatNavLeftRight.left, right:ipcMatNavLeftRight.right, zIndex:50,
              display:'flex', alignItems:'center', gap:8,
              padding:'6px 12px', background:'rgba(255,255,255,0.97)',
              borderTop:'1px solid #E5E7EB',
            }}>
              <button onClick={()=>ipcMatScrollBy(-1)} disabled={ipcMatScrollState.atStart} aria-label="Scroll table left"
                style={{width:24,height:24,borderRadius:6,border:'1px solid #E5E7EB',background:ipcMatScrollState.atStart?'#F9FAFB':'#fff',color:ipcMatScrollState.atStart?'#D1D5DB':'#6B7280',cursor:ipcMatScrollState.atStart?'default':'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,flexShrink:0}}>‹</button>
              <input type="range" min={0} max={1000} step={1} value={Math.round(ipcMatScrollState.ratio*1000)}
                onChange={e=>ipcMatScrollToRatio(Number(e.target.value)/1000)} aria-label="Scroll table horizontally"
                style={{flex:1,height:4,accentColor:'#1B3A6B',cursor:'pointer'}}/>
              <button onClick={()=>ipcMatScrollBy(1)} disabled={ipcMatScrollState.atEnd} aria-label="Scroll table right"
                style={{width:24,height:24,borderRadius:6,border:'1px solid #E5E7EB',background:ipcMatScrollState.atEnd?'#F9FAFB':'#fff',color:ipcMatScrollState.atEnd?'#D1D5DB':'#6B7280',cursor:ipcMatScrollState.atEnd?'default':'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,flexShrink:0}}>›</button>
            </div>
          )}
        </div>
      )}

      {/* Reject Modal */}
      {rejectIpc && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}}>
          <div style={{background:'#fff',borderRadius:12,padding:24,width:400}}>
            <h3 style={{fontWeight:700,marginBottom:12}}>سبب رفض المستخلص</h3>
            <textarea value={rejectReason} onChange={e=>setRejectReason(e.target.value)} rows={3}
              style={{width:'100%',border:'1px solid #E5E7EB',borderRadius:8,padding:8,fontSize:13}} placeholder="اكتب السبب..." />
            <div style={{display:'flex',gap:8,marginTop:16}}>
              <button onClick={()=>{setRejectIpc(null); setRejectReason('')}} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">إلغاء</button>
              <button onClick={async ()=>{ await handleIpcApproval(rejectIpc,'reject',rejectReason); setRejectIpc(null); setRejectReason('') }}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm">تأكيد الرفض</button>
            </div>
          </div>
        </div>
      )}
      </>
      )}

      {/* Equipment Reject Modal */}
      {eqRejectIpc && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}}>
          <div style={{background:'#fff',borderRadius:12,padding:24,width:400}}>
            <h3 style={{fontWeight:700,marginBottom:12}}>سبب رفض المستخلص</h3>
            <textarea value={eqRejectReason} onChange={e=>setEqRejectReason(e.target.value)} rows={3}
              style={{width:'100%',border:'1px solid #E5E7EB',borderRadius:8,padding:8,fontSize:13}} placeholder="اكتب السبب..." />
            <div style={{display:'flex',gap:8,marginTop:16}}>
              <button onClick={()=>{setEqRejectIpc(null); setEqRejectReason('')}} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">إلغاء</button>
              <button onClick={async ()=>{ await eqHandleIpcApproval(eqRejectIpc,'reject',eqRejectReason); setEqRejectIpc(null); setEqRejectReason('') }}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm">تأكيد الرفض</button>
            </div>
          </div>
        </div>
      )}

      {/* Equipment New IPC Wizard */}
      {eqWizard && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:20}}>
          <div style={{background:'#fff',borderRadius:12,width:'100%',maxWidth:900,maxHeight:'90vh',overflow:'auto'}}>
            <div style={{padding:'16px 24px',borderBottom:'1px solid #E5E7EB',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <h3 style={{fontWeight:700,fontSize:16}}>New Equipment IPC — Step {eqWizard.step}</h3>
              <button onClick={()=>setEqWizard(null)}>✕</button>
            </div>
            {eqWizard.step === 1 ? (
              <div style={{padding:24,display:'flex',flexDirection:'column',gap:16}}>
                <div>
                  <label className="text-xs font-semibold text-[#6B655C] uppercase">Equipment Contract *</label>
                  <select value={eqWizard.form.equipment_contract_id} onChange={e=>setEqWizard(w=>({...w,form:{...w.form,equipment_contract_id:e.target.value}}))}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    <option value="">-- Select Contract --</option>
                    {eqContracts.map(c=><option key={c.id} value={c.id}>{c.contract_no} — {c.contractor_name}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-[#6B655C] uppercase">Period From</label>
                    <input type="date" value={eqWizard.form.period_from} onChange={e=>setEqWizard(w=>({...w,form:{...w.form,period_from:e.target.value}}))}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#6B655C] uppercase">Period To</label>
                    <input type="date" value={eqWizard.form.period_to} onChange={e=>setEqWizard(w=>({...w,form:{...w.form,period_to:e.target.value}}))}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                </div>
                <label className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 cursor-pointer">
                  <input type="checkbox" checked={!!eqWizard.form.is_final}
                    onChange={e=>setEqWizard(w=>({...w,form:{...w.form,is_final:e.target.checked}}))}
                    className="w-4 h-4 accent-red-600" />
                  <span className="text-sm font-medium text-red-700">هذا هو المستخلص النهائي (Final IPC)</span>
                </label>
                <div className="flex gap-3 justify-end">
                  <button onClick={()=>setEqWizard(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                  <button onClick={eqWizardStep1Next} className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium">Next →</button>
                </div>
              </div>
            ) : (
              <div style={{padding:24,display:'flex',flexDirection:'column',gap:16}}>
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-[#F5F1EA]">
                      <th className="text-left px-2 py-1.5">Sub Code</th>
                      <th className="text-left px-2 py-1.5">Cost Code</th>
                      <th className="text-left px-2 py-1.5 text-purple-700">Resource Code</th>
                      <th className="text-left px-2 py-1.5">Equipment</th>
                      <th className="text-right px-2 py-1.5">BOQ Qty</th>
                      <th className="text-left px-2 py-1.5">Unit</th>
                      <th className="text-right px-2 py-1.5">Rate</th>
                      <th className="text-right px-2 py-1.5">This Period Qty</th>
                      <th className="text-right px-2 py-1.5">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eqWizard.lines.map((l,idx)=>(
                      <tr key={idx} className="border-b border-[#F3F4F6]">
                        <td className="px-2 py-1.5 font-mono text-orange-600 font-semibold">
                          {l.sub_code || '—'}
                          {l.sub_code_desc && <div className="text-[10px] text-[#9CA3AF] font-sans">{l.sub_code_desc}</div>}
                        </td>
                        <td className="px-2 py-1.5 font-mono text-[#1A7A4A] font-semibold">
                          {l.cost_code_label || l.cost_code || '—'}
                          {l.cost_code_desc && <div className="text-[10px] text-[#9CA3AF] font-sans">{l.cost_code_desc}</div>}
                        </td>
                        <td className="px-2 py-1.5 font-mono text-purple-700 font-semibold">
                          {l.resource_code_label || '—'}
                          {l.resource_code_desc && <div className="text-[10px] text-[#9CA3AF] font-sans">{l.resource_code_desc}</div>}
                        </td>
                        <td className="px-2 py-1.5">{l.description}</td>
                        <td className="px-2 py-1.5 text-right">{l.boq_qty}</td>
                        <td className="px-2 py-1.5">{l.unit}</td>
                        <td className="px-2 py-1.5 text-right">{l.unit_rate}</td>
                        <td className="px-2 py-1.5">
                          <input type="number" value={l.this_period_qty} onChange={e=>eqWizardUpdateQty(idx,e.target.value)}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs text-right" />
                        </td>
                        <td className="px-2 py-1.5 text-right font-semibold">{fmtN(l.this_period_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(() => {
                  const totals = eqWizardTotals()
                  return (
                    <div className="grid grid-cols-4 gap-3">
                      <div className="bg-[#EDE7DA] rounded-xl p-3"><p className="text-xs text-[#6B655C]">Gross</p><p className="text-base font-bold">{fmtN(totals.gross)}</p></div>
                      <div className="bg-[#EDE7DA] rounded-xl p-3"><p className="text-xs text-[#6B655C]">Retention</p><p className="text-base font-bold text-red-500">{fmtN(totals.ret)}</p></div>
                      <div className={`rounded-xl p-3 border ${totals.overContractValue?'bg-red-50 border-red-300':'bg-green-50 border-green-200'}`}>
                        <p className={`text-xs ${totals.overContractValue?'text-red-700':'text-green-700'}`}>Cumulative</p>
                        <p className={`text-base font-bold ${totals.overContractValue?'text-red-700':'text-green-700'}`}>{fmtN(totals.cumulativeCertified)}</p>
                      </div>
                      <div className="bg-[#1A7A4A] rounded-xl p-3"><p className="text-xs text-green-200">Net Payable</p><p className="text-base font-bold text-white">{fmtN(totals.net)}</p></div>
                    </div>
                  )
                })()}

                {/* Deductions section */}
                <div className="bg-[#EDE7DA] rounded-xl border border-[#DDD9D0] overflow-hidden">
                  <div className="px-4 py-2 bg-[#EDE7DA] border-b border-[#DDD9D0] flex items-center justify-between">
                    <h3 className="font-semibold text-[#2E2C29] text-sm">Deductions (اختياري)</h3>
                    <button type="button"
                      onClick={()=>setEqWizard(w=>({...w, deductions:[...w.deductions,{deduction_type:'equipment',description:'',amount:''}]}))}
                      className="text-xs px-3 py-1.5 rounded-lg bg-[#1A7A4A] text-white font-medium hover:bg-[#15633B]">+ Add Deduction</button>
                  </div>
                  {eqWizard.deductions.length>0 && (
                    <div className="divide-y divide-[#E8E4DC]">
                      {eqWizard.deductions.map((d,i)=>(
                        <div key={i} className="flex items-center gap-2 px-4 py-2">
                          <select value={d.deduction_type}
                            onChange={e=>setEqWizard(w=>({...w, deductions:w.deductions.map((x,ii)=>ii===i?{...x,deduction_type:e.target.value}:x)}))}
                            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm w-32">
                            <option value="material">Material</option>
                            <option value="equipment">Equipment</option>
                            <option value="labors">Labors</option>
                            <option value="other">Other</option>
                          </select>
                          <input type="text" placeholder="Description (optional)" value={d.description}
                            onChange={e=>setEqWizard(w=>({...w, deductions:w.deductions.map((x,ii)=>ii===i?{...x,description:e.target.value}:x)}))}
                            className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
                          <input type="number" min="0" step="0.01" placeholder="Amount" value={d.amount}
                            onChange={e=>setEqWizard(w=>({...w, deductions:w.deductions.map((x,ii)=>ii===i?{...x,amount:e.target.value}:x)}))}
                            className="w-32 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right" />
                          <button type="button" onClick={()=>setEqWizard(w=>({...w, deductions:w.deductions.filter((_,ii)=>ii!==i)}))}
                            className="text-[#9A948C] hover:text-red-500 px-1">✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-3 justify-between">
                  <button onClick={()=>setEqWizard(w=>({...w,step:1}))} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">← Back</button>
                  <div className="flex gap-3">
                    <button onClick={()=>eqWizardSave(false)} disabled={eqSaving} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Save Draft</button>
                    <button onClick={()=>eqWizardSave(true)} disabled={eqSaving} className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium">Submit for Approval</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Equipment IPC Detail — mirrors Material IPC detail page structure */}
      {eqSelectedIpc && (() => {
        const eqNotAdded = eqSelectedPoLines.filter(pl=>!eqIpcLines.find(sl=>sl.po_line_item_id===pl.id))
        const eqLinesTotal = eqIpcLines.reduce((s,l)=>s+(parseFloat(l.this_period_amount)||0),0)
        return (
        <div style={{position:'fixed',inset:0,background:'#F9FAFB',zIndex:9999,overflow:'auto'}}>
          <div className="p-6 max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <button onClick={()=>setEqSelectedIpc(null)} className="text-orange-600 hover:text-orange-800 text-sm">← Back</button>
                <div>
                  <h1 className="text-xl font-bold text-[#1C1A18]">
                    IPC-{String(eqSelectedIpc.ipc_number).padStart(4,'0')} — {eqSelectedIpc.equipment_contracts?.contractor_name}
                  </h1>
                  <p className="text-sm text-[#6B655C]">{eqSelectedIpc.equipment_contracts?.contract_no} | {eqSelectedIpc.period_from} → {eqSelectedIpc.period_to}</p>
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[eqSelectedIpc.status]}`}>
                  {(eqSelectedIpc.approval_stage||'draft').replace('_',' ')}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getCommercialStatus(eqSelectedIpc).color}`}>
                  {getCommercialStatus(eqSelectedIpc).emoji} {getCommercialStatus(eqSelectedIpc).label}
                </span>
                {eqSelectedIpc.is_final && <span className="px-2 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">FINAL</span>}
                {/* Phase 3: same draft/rejected/revoked expansion as the Material path above. */}
                {can('IPC.Delete') && ['draft','rejected','revoked'].includes(eqSelectedIpc.approval_stage||'draft') && (
                  <button onClick={eqOpenEditModal}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-[#F5F1EA]">✏️ {(eqSelectedIpc.approval_stage==='rejected'||eqSelectedIpc.approval_stage==='revoked') ? 'تصحيح المستخلص' : 'Edit'}</button>
                )}
                {getEqIpcApprovalButtons(eqSelectedIpc).btns.map(b => (
                  <button key={b.action}
                    onClick={()=> b.action==='reject' ? setEqRejectIpc(eqSelectedIpc) : eqHandleIpcApproval(eqSelectedIpc, b.action)}
                    className={`px-4 py-2 text-sm rounded-lg font-medium ${b.color}`}>{b.label}</button>
                ))}
              </div>
            </div>

            {/* Tabs */}
            <div style={{display:'flex',borderBottom:'1px solid #E5E7EB',background:'#fff',padding:'0 24px',marginBottom:16}}>
              {[{id:'lines',label:'Line Items'},{id:'audit',label:'Audit Trail'}].map(t=>(
                <button key={t.id} onClick={()=>setEqIpcDetailTab(t.id)}
                  style={{padding:'10px 18px',fontSize:12,fontWeight:eqIpcDetailTab===t.id?700:500,color:eqIpcDetailTab===t.id?'#0F1017':'#6B7280',background:'transparent',border:'none',borderBottom:`2px solid ${eqIpcDetailTab===t.id?'#F97316':'transparent'}`,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap'}}>
                  {t.label}
                </button>
              ))}
            </div>
            {eqIpcDetailTab==='audit' && (
              <div style={{padding:'0 24px'}}><IPCAuditTrail entityId={eqSelectedIpc.id}/></div>
            )}
            {eqIpcDetailTab==='lines' && <div>

            {eqSelectedIpc.rejection_reason && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <b>سبب الرفض:</b> {eqSelectedIpc.rejection_reason}
              </div>
            )}

            {/* Summary */}
            {(() => {
              const t = computeEqIpcTotals(eqSelectedIpc)
              return (
                <>
                  {/* 1. Contract context */}
                  <div className="grid grid-cols-4 gap-3 mb-3">
                    <div className="bg-[#EDE7DA] border border-[#DDD9D0] rounded-xl p-4">
                      <p className="text-xs text-[#6B655C] mb-1">Contract Value</p>
                      <p className="text-lg font-bold text-[#2E2C29]">SAR {fmtN(t.contractValue)}</p>
                    </div>
                    <div className="bg-[#EDE7DA] border border-[#DDD9D0] rounded-xl p-4">
                      <p className="text-xs text-[#6B655C] mb-1">Previously Certified</p>
                      <p className="text-lg font-bold text-[#2E2C29]">SAR {fmtN(t.prevCertified)}</p>
                    </div>
                    <div className="bg-[#EDE7DA] border border-[#DDD9D0] rounded-xl p-4">
                      <p className="text-xs text-[#6B655C] mb-1">Remaining Contract Value</p>
                      <p className="text-lg font-bold text-[#2E2C29]">SAR {fmtN(t.remainingContractValue)}</p>
                    </div>
                    <div className={`rounded-xl border p-4 ${t.overContractValue?'bg-red-50 border-red-300':'bg-green-50 border-green-200'}`}>
                      <p className={`text-xs mb-1 ${t.overContractValue?'text-red-700':'text-green-700'}`}>Cumulative After This IPC</p>
                      <p className={`text-lg font-bold ${t.overContractValue?'text-red-700':'text-green-700'}`}>SAR {fmtN(t.cumulativeCertified)}</p>
                    </div>
                  </div>

                  {/* 2. This IPC's own figures */}
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    {[
                      {label:'Gross Amount',   value:`${t.currency} ${fmtN(t.gross)}`,        color:'text-orange-700'},
                      {label:`VAT ${((t.vatRate||0.15)*100).toFixed(0)}%`,        value:`${t.currency} ${fmtN(t.vat)}`,          color:'text-blue-600'},
                      {label:'Total Inc VAT',  value:`${t.currency} ${fmtN(t.totalIncVat)}`,  color:'text-blue-800'},
                    ].map(s=>(
                      <div key={s.label} className="bg-[#EDE7DA] rounded-xl border border-[#DDD9D0] p-4">
                        <p className="text-xs text-[#6B655C] mb-1">{s.label}</p>
                        <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* 3. Retention */}
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-3">
                    <p className="text-xs text-red-700 mb-1">Retention ({t.retPct}%)</p>
                    <div className="grid grid-cols-2 gap-3 mt-1">
                      <div>
                        <p className="text-[10px] text-red-500">This IPC</p>
                        <p className="text-lg font-bold text-red-600">SAR {fmtN(t.ret)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-red-500">Cumulative Held (all IPCs)</p>
                        <p className="text-lg font-bold text-red-700">SAR {fmtN(t.cumulativeRetentionHeld)}</p>
                      </div>
                    </div>
                  </div>

                  {/* 4. Advance payment status + recovery */}
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 col-span-2">
                      <p className="text-xs text-amber-700 mb-1">Advance Payment Status</p>
                      <div className="grid grid-cols-3 gap-3 mt-2">
                        <div>
                          <p className="text-[10px] text-amber-600">Total Advance</p>
                          <p className="text-base font-bold text-amber-700">SAR {fmtN(t.totalAdvPay)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-amber-600">Recovered So Far</p>
                          <p className="text-base font-bold text-amber-700">SAR {fmtN(t.prevRecoveries + t.advRecovery)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-amber-600">Remaining</p>
                          <p className={`text-base font-bold ${t.outstanding>0?'text-orange-700':'text-green-700'}`}>SAR {fmtN(t.outstanding)}</p>
                        </div>
                      </div>
                      <div className="mt-2">
                        <div className="w-full bg-amber-100 rounded-full h-1.5 overflow-hidden">
                          <div className="bg-amber-600 h-full rounded-full" style={{width: `${t.totalAdvPay>0 ? Math.min(100,((t.prevRecoveries+t.advRecovery)/t.totalAdvPay*100)) : 0}%`}}></div>
                        </div>
                        <p className="text-[10px] text-amber-600 mt-1">{t.totalAdvPay>0 ? Math.round((t.prevRecoveries+t.advRecovery)/t.totalAdvPay*100) : 0}% recovered{t.outstanding===0 && t.totalAdvPay>0 ? ' — ✓ Fully Recovered':''}</p>
                      </div>
                    </div>
                    <div className="bg-[#EDE7DA] border border-[#DDD9D0] rounded-xl p-4">
                      <p className="text-xs text-[#6B655C] mb-1">{t.vatCase===2 ? 'Adv Recovery + VAT' : 'Advance Recovery'}</p>
                      <p className="text-lg font-bold text-orange-600">SAR {fmtN(t.vatCase===2 ? t.totalAdvIncVat : t.advRecovery)}</p>
                      {t.vatCase===2
                        ? <p className="text-xs text-[#9CA3AF] mt-0.5">{fmtN(t.advRecovery)} + VAT {fmtN(t.advRecoveryVat)}</p>
                        : <p className="text-xs text-[#9CA3AF] mt-0.5">Rate: {t.advRecoveryRate}% {t.hasRealAdvPayment ? '(locked — real payment on file)' : ''}</p>
                      }
                    </div>
                  </div>

                  {/* 5. Total Deductions — always shown, even at zero */}
                  <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 mb-3">
                    <p className="text-xs text-rose-700 mb-1">Total Deductions</p>
                    <p className="text-lg font-bold text-rose-700">SAR {fmtN(t.otherDeductions)}</p>
                  </div>

                  {/* 6. Net Payable — final result */}
                  <div className="bg-orange-600 rounded-xl p-4 mb-5">
                    <p className="text-xs text-orange-100 mb-1">Net Payable</p>
                    <p className="text-lg font-bold text-white">SAR {fmtN(t.net)}</p>
                    <p className="text-xs text-orange-200 mt-0.5">Gross + VAT − Retention − Advance Recovery{t.otherDeductions>0?' − Deductions':''}</p>
                  </div>
                  {(eqSelectedIpc._deductions||[]).length > 0 && (
                    <div className="bg-white border border-rose-200 rounded-xl p-4 mb-3">
                      <p className="text-sm font-semibold text-rose-700 mb-2">Deductions — Total: SAR {fmtN(t.otherDeductions)}</p>
                      <div className="divide-y divide-rose-100">
                        {eqSelectedIpc._deductions.map(d=>(
                          <div key={d.id} className="flex items-center justify-between py-1.5 text-sm">
                            <div>
                              <span className="inline-block px-2 py-0.5 rounded bg-rose-100 text-rose-700 text-xs font-medium capitalize mr-2">{d.deduction_type}</span>
                              <span className="text-[#6B655C]">{d.description || '—'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-rose-700">SAR {fmtN(d.amount)}</span>
                              {eqSelectedIpc.approval_stage==='draft' && (
                                <button onClick={()=>removeIpcDeduction(d.id, true)} className="text-[#9A948C] hover:text-red-500">✕</button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {eqSelectedIpc.approval_stage==='draft' && (
                    <div className="bg-white border border-[#DDD9D0] rounded-xl p-4 mb-5 flex items-center gap-2">
                      <select value={eqNewDedForm.deduction_type} onChange={e=>setEqNewDedForm(p=>({...p,deduction_type:e.target.value}))}
                        className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm w-32">
                        <option value="material">Material</option>
                        <option value="equipment">Equipment</option>
                        <option value="labors">Labors</option>
                        <option value="other">Other</option>
                      </select>
                      <input type="text" placeholder="Description (optional)" value={eqNewDedForm.description}
                        onChange={e=>setEqNewDedForm(p=>({...p,description:e.target.value}))}
                        className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
                      <input type="number" min="0" step="0.01" placeholder="Amount" value={eqNewDedForm.amount}
                        onChange={e=>setEqNewDedForm(p=>({...p,amount:e.target.value}))}
                        className="w-32 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right" />
                      <button onClick={async()=>{ await addIpcDeduction(eqSelectedIpc.id, eqNewDedForm, true); setEqNewDedForm({deduction_type:'equipment',description:'',amount:''}) }}
                        className="px-3 py-1.5 text-xs rounded-lg bg-orange-600 text-white font-medium hover:bg-orange-700">+ Add Deduction</button>
                    </div>
                  )}
                </>
              )
            })()}

            {/* Add more lines (draft only) */}
            {eqSelectedIpc.status==='draft' && eqNotAdded.length>0 && (
              <div className="bg-[#EDE7DA] rounded-xl border border-[#DDD9D0] mb-4 overflow-hidden">
                <div className="px-4 py-3 bg-[#F5F1EA] border-b border-[#E8E4DC] flex items-center justify-between">
                  <h3 className="font-semibold text-[#2E2C29] text-sm">+ إضافة بنود من العقد</h3>
                  <span className="text-xs text-[#9A948C]">{eqNotAdded.length} بند متاح</span>
                </div>
                <div className="divide-y divide-[#E8E4DC] max-h-40 overflow-y-auto">
                  {eqNotAdded.map(pl=>(
                    <div key={pl.id} className="flex items-center justify-between px-4 py-2">
                      <div>
                        <span className="font-mono text-xs text-[#1A7A4A] font-semibold mr-2">{pl.cost_codes?.code || '—'}</span>
                        <span className="text-sm text-[#1C1A18]">{pl.equipment_type}</span>
                        <span className="text-xs text-[#9A948C] ml-2">{pl.unit} | {fmt(pl.rate)}</span>
                      </div>
                      <button onClick={()=>eqAddLineToExisting(pl)}
                        className="text-xs px-3 py-1 bg-orange-500 text-white rounded-lg hover:bg-orange-600">+ Add</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Lines table */}
            <div className="bg-[#EDE7DA] rounded-xl border border-[#DDD9D0] overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8E4DC]">
                <h3 className="font-semibold text-[#2E2C29]">بنود المستخلص</h3>
                <div className="flex items-center gap-3">
                  {eqSelectedIpc?.status === 'draft' && eqIpcLines.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#6B655C] font-medium">Bulk Date:</span>
                      <input type="date" value={bulkDate} onChange={e=>setBulkDate(e.target.value)}
                        className="border border-[#DDD9D0] rounded px-2 py-1 text-xs focus:outline-none focus:border-orange-500"/>
                      <button disabled={!bulkDate||bulkUpdating}
                        onClick={async ()=>{ await applyBulkDateToLines(eqIpcLines.map(l=>l.id)); await eqFetchIpcLines(eqSelectedIpc.id) }}
                        className="px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded hover:bg-orange-600 disabled:opacity-40">
                        {bulkUpdating ? '...' : 'Apply All'}
                      </button>
                    </div>
                  )}
                  <span className="font-bold text-orange-700">Total: SAR {fmtN(eqLinesTotal)}</span>
                </div>
              </div>
              {eqIpcLines.length===0 ? (
                <div className="py-10 text-center text-[#9A948C] text-sm">لا يوجد بنود</div>
              ) : (
                <div className="overflow-auto" style={{maxHeight:'50vh'}}>
                  <table className="w-full text-sm">
                    <thead className="bg-[#F5F1EA] text-xs text-[#6B655C] border-b border-[#E8E4DC] sticky top-0 z-10">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium">Cost Code</th>
                        <th className="text-left px-4 py-2 font-medium text-purple-700">Resource Code</th>
                        <th className="text-left px-4 py-2 font-medium">Sub Code</th>
                        <th className="text-left px-4 py-2 font-medium">EQP ID</th>
                        <th className="text-left px-4 py-2 font-medium">Description</th>
                        <th className="text-center px-4 py-2 font-medium">Unit</th>
                        <th className="text-right px-4 py-2 font-medium">BOQ Qty</th>
                        <th className="text-right px-4 py-2 font-medium">Unit Rate</th>
                        <th className="text-right px-4 py-2 font-medium">This Period Qty</th>
                        <th className="text-right px-4 py-2 font-medium">Amount</th>
                        <th className="px-4 py-2 font-medium">Actual Date</th>
                        {eqSelectedIpc.status==='draft' && <th className="px-4 py-2"></th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E8E4DC]">
                      {eqIpcLines.map(line=>(
                        <tr key={line.id} className="hover:bg-[#F5F1EA]">
                          <td className="px-4 py-2">
                            <span className="font-mono text-xs text-[#1A7A4A] font-semibold">{line.cost_codes?.code || '—'}</span>
                            {line.cost_codes?.description && <div className="text-[10px] text-[#9CA3AF]">{line.cost_codes.description}</div>}
                          </td>
                          <td className="px-4 py-2 font-mono text-xs text-purple-700">
                            {line.resource_code_info?.code || '—'}
                            {line.resource_code_info?.description && <div className="text-[10px] text-[#9CA3AF] font-sans">{line.resource_code_info.description}</div>}
                          </td>
                          <td className="px-4 py-2 font-mono text-xs text-orange-600">
                            {line.sub_code || '—'}
                            {line.sub_code_desc && <div className="text-[10px] text-[#9CA3AF] font-sans">{line.sub_code_desc}</div>}
                          </td>
                          <td className="px-4 py-2">
                            {eqSelectedIpc.status==='draft' ? (
                              <input value={line.equipment_id||''} onChange={e=>eqUpdateIpcLineField(line.id,'equipment_id',e.target.value)}
                                placeholder="e.g. 9000 NAA"
                                className="w-24 border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-orange-400"/>
                            ) : (line.equipment_id || '—')}
                          </td>
                          <td className="px-4 py-2 font-medium">{line.description}</td>
                          <td className="px-4 py-2 text-center text-[#6B655C]">{line.unit||'—'}</td>
                          <td className="px-4 py-2 text-right text-[#6B655C]">{fmt(line.boq_qty)}</td>
                          <td className="px-4 py-2 text-right text-[#6B655C]">{fmt(line.unit_rate)}</td>
                          <td className="px-4 py-2 text-right">
                            {eqSelectedIpc.status==='draft' ? (
                              <input type="number" min="0" step="0.01"
                                defaultValue={line.this_period_qty}
                                onBlur={e=>eqUpdateExistingLine(line.id,e.target.value)}
                                className="w-28 border border-gray-300 rounded px-2 py-1 text-right text-sm focus:ring-1 focus:ring-orange-500" />
                            ) : (
                              <span>{fmt(line.this_period_qty)}</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-right font-bold text-orange-700">{fmtN(line.this_period_amount)}</td>
                          <td className="px-4 py-2">
                            {eqSelectedIpc.status==='draft' ? (
                              <input type="date" defaultValue={line.actual_cost_date||''}
                                onBlur={async e=>{
                                  await supabase.from('ipc_line_items').update({actual_cost_date:e.target.value}).eq('id',line.id)
                                  await eqFetchIpcLines(eqSelectedIpc.id)
                                }}
                                className="border border-[#E5E7EB] rounded px-2 py-1 text-xs w-32 focus:outline-none focus:border-orange-500"/>
                            ) : (
                              <span className="text-xs text-[#6B7280]">{line.actual_cost_date||'—'}</span>
                            )}
                          </td>
                          {eqSelectedIpc.status==='draft' && (
                            <td className="px-4 py-2 text-center">
                              <button onClick={()=>eqDeleteExistingLine(line.id)} className="text-gray-300 hover:text-red-500 text-lg">×</button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-[#F5F1EA] border-t-2 border-[#DDD9D0]">
                      <tr>
                        <td colSpan={8} className="px-4 py-3 font-medium">Total</td>
                        <td className="px-4 py-3 text-right font-bold text-orange-700">{fmtN(eqLinesTotal)}</td>
                        {eqSelectedIpc.status==='draft' && <td></td>}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
            {(eqSelectedIpc.approval_stage||'draft')==='draft' && can('IPC.Delete') && (
              <div className="p-4 bg-[#EDE7DA] border-t border-orange-300 flex justify-end mt-4 rounded-xl">
                <button onClick={()=>eqHandleIpcApproval(eqSelectedIpc, 'submit')}
                  className="px-5 py-2 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 font-medium">
                  Submit for Approval
                </button>
              </div>
            )}
            </div>}
          </div>
        </div>
        )
      })()}

      {/* Equipment Edit IPC Modal */}
      {eqShowEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#DDD9D0]">
              <h2 className="font-bold text-[#1C1A18]">✏️ تعديل المستخلص — IPC-{String(eqEditForm.ipc_number).padStart(4,'0')}</h2>
              <button onClick={()=>setEqShowEditModal(false)} className="text-[#9A948C] hover:text-[#6B655C] text-2xl leading-none">×</button>
            </div>

            <div className="px-6 py-4 border-b border-[#E8E4DC] grid grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-[#6B655C] mb-1">IPC Number</label>
                <input type="number" value={eqEditForm.ipc_number}
                  onChange={e=>setEqEditForm(f=>({...f, ipc_number: parseInt(e.target.value)||0}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-orange-500"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B655C] mb-1">Submission Date</label>
                <input type="date" value={eqEditForm.submission_date}
                  onChange={e=>setEqEditForm(f=>({...f, submission_date: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-orange-500"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B655C] mb-1">Period From</label>
                <input type="date" value={eqEditForm.period_from}
                  onChange={e=>setEqEditForm(f=>({...f, period_from: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-orange-500"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B655C] mb-1">Period To</label>
                <input type="date" value={eqEditForm.period_to}
                  onChange={e=>setEqEditForm(f=>({...f, period_to: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-orange-500"/>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F1EA] text-xs text-[#6B655C]">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Sub Code</th>
                    <th className="text-left px-3 py-2 font-medium">Cost Code</th>
                    <th className="text-left px-3 py-2 font-medium text-purple-700">Resource Code</th>
                    <th className="text-left px-3 py-2 font-medium">EQP ID</th>
                    <th className="text-left px-3 py-2 font-medium">Description</th>
                    <th className="text-center px-3 py-2 font-medium">Unit</th>
                    <th className="text-right px-3 py-2 font-medium">Unit Rate</th>
                    <th className="text-right px-3 py-2 font-medium w-32">Qty</th>
                    <th className="text-right px-3 py-2 font-medium">Amount</th>
                    <th className="px-3 py-2 font-medium">Actual Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E8E4DC]">
                  {(eqEditForm.lines||[]).map((l, idx) => {
                    const amt = +(l.this_period_qty * l.unit_rate).toFixed(2)
                    return (
                      <tr key={l.id} className="hover:bg-[#F5F1EA]">
                        <td className="px-3 py-2 font-mono text-xs text-orange-600">
                          {l.sub_code}
                          {l.sub_code_desc && <div className="text-[10px] text-[#9CA3AF] font-sans">{l.sub_code_desc}</div>}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-[#6B655C]">
                          {l.cost_code_label || '—'}
                          {l.cost_code_desc && <div className="text-[10px] text-[#9CA3AF] font-sans">{l.cost_code_desc}</div>}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-purple-700">
                          {l.resource_code_label || '—'}
                          {l.resource_code_desc && <div className="text-[10px] text-[#9CA3AF] font-sans">{l.resource_code_desc}</div>}
                        </td>
                        <td className="px-3 py-2">
                          <input value={l.equipment_id||''} onChange={e => setEqEditForm(f => ({
                              ...f,
                              lines: f.lines.map((ll, i) => i === idx ? { ...ll, equipment_id: e.target.value } : ll)
                            }))}
                            placeholder="e.g. 9000 NAA"
                            className="w-24 border border-gray-300 rounded px-2 py-1 text-xs focus:ring-2 focus:ring-orange-500"/>
                        </td>
                        <td className="px-3 py-2 text-[#2E2C29] max-w-xs truncate">{l.description}</td>
                        <td className="px-3 py-2 text-center text-[#6B655C]">{l.unit}</td>
                        <td className="px-3 py-2 text-right text-[#6B655C]">{l.unit_rate.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number" min="0" step="0.01"
                            value={l.this_period_qty}
                            onChange={e => setEqEditForm(f => ({
                              ...f,
                              lines: f.lines.map((ll, i) => i === idx
                                ? { ...ll, this_period_qty: parseFloat(e.target.value) || 0 }
                                : ll
                              )
                            }))}
                            className="w-28 border border-gray-300 rounded px-2 py-1 text-right text-sm focus:ring-2 focus:ring-orange-500"
                          />
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${amt > 0 ? 'text-orange-700' : 'text-[#9A948C]'}`}>
                          {amt.toLocaleString()}
                        </td>
                        <td className="px-3 py-2">
                          <input type="date" value={l.actual_cost_date||''}
                            onChange={e => setEqEditForm(f => ({
                              ...f,
                              lines: f.lines.map((ll, i) => i === idx
                                ? { ...ll, actual_cost_date: e.target.value }
                                : ll
                              )
                            }))}
                            className="border border-[#E5E7EB] rounded px-2 py-1 text-xs w-32 focus:outline-none focus:border-orange-500"/>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot className="bg-[#F5F1EA] border-t-2 border-[#DDD9D0]">
                  <tr>
                    <td colSpan={6} className="px-3 py-3 font-medium text-[#6B655C]">Gross Total</td>
                    <td className="px-3 py-3 text-right font-bold text-orange-700">
                      {(eqEditForm.lines||[]).reduce((s,l) => s + l.this_period_qty * l.unit_rate, 0).toLocaleString(undefined, {maximumFractionDigits:2})}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="flex items-center gap-4 px-6 py-4 border-t border-[#DDD9D0] bg-[#F5F1EA] rounded-b-2xl">
              <div className="flex gap-3 items-center flex-1">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-[#6B655C]">Ret%</label>
                  <input type="number" value={eqEditForm.retention_pct}
                    onChange={e=>setEqEditForm(f=>({...f, retention_pct: e.target.value}))}
                    className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-center focus:ring-2 focus:ring-orange-500"/>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-[#6B655C]">Adv%</label>
                  <input type="number" value={eqEditForm.advance_pct}
                    onChange={e=>setEqEditForm(f=>({...f, advance_pct: e.target.value}))}
                    className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-center focus:ring-2 focus:ring-orange-500"/>
                </div>
                <input type="text" placeholder="Notes" value={eqEditForm.notes}
                  onChange={e=>setEqEditForm(f=>({...f, notes: e.target.value}))}
                  className="flex-1 border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-orange-500"/>
              </div>
              <button onClick={()=>setEqShowEditModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-[#6B655C] hover:bg-[#EDE7DA]">إلغاء</button>
              <button onClick={eqSaveEditIpc}
                className="px-5 py-2 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600">💾 حفظ</button>
            </div>
          </div>
        </div>
      )}

      {/* Contract History — all IPCs for one contract, side by side */}
      {contractHistory && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:20}}>
          <div style={{background:'#fff',borderRadius:12,width:'100%',maxWidth:1000,maxHeight:'88vh',overflow:'auto'}}>
            <div style={{padding:'16px 24px',borderBottom:'1px solid #E5E7EB',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <h3 style={{fontWeight:700,fontSize:16}}>سجل المستخلصات — {contractHistory.contractLabel}</h3>
                <p style={{fontSize:12,color:'#6B7280',marginTop:2}}>{contractHistory.ipcs.length} مستخلص</p>
              </div>
              <button onClick={()=>setContractHistory(null)} style={{fontSize:20,color:'#6B7280'}}>✕</button>
            </div>
            <div style={{padding:24}}>
              {(() => {
                const totalGross = contractHistory.ipcs.reduce((s,i)=>s+(parseFloat(i.gross_amount)||0),0)
                const totalRet   = contractHistory.ipcs.reduce((s,i)=>s+(parseFloat(i.retention_amount||i.retention_held)||0),0)
                const totalAdv   = contractHistory.ipcs.reduce((s,i)=>s+(parseFloat(i.advance_deduction)||0),0)
                const totalNet   = contractHistory.ipcs.reduce((s,i)=>s+(parseFloat(i.net_payable)||0),0)
                return (
                  <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:20}}>
                    {[
                      {label:'Total Gross',value:totalGross,color:'#1C1A18'},
                      {label:'Total Retention',value:totalRet,color:'#B45309'},
                      {label:'Total Advance Recovery',value:totalAdv,color:'#C2410C'},
                      {label:'Total Net Payable',value:totalNet,color:'#1A7A4A'},
                    ].map(s=>(
                      <div key={s.label} style={{background:'#F9FAFB',border:'1px solid #E5E7EB',borderRadius:8,padding:'10px 14px'}}>
                        <p style={{fontSize:11,color:'#6B7280',marginBottom:4}}>{s.label}</p>
                        <p style={{fontSize:15,fontWeight:700,color:s.color}}>{fmtN(s.value)}</p>
                      </div>
                    ))}
                  </div>
                )
              })()}
              <table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
                <thead>
                  <tr style={{background:'#F9FAFB',borderBottom:'1px solid #E5E7EB'}}>
                    <th style={{padding:'8px 10px',textAlign:'left'}}>IPC #</th>
                    <th style={{padding:'8px 10px',textAlign:'left'}}>Actual Date</th>
                    <th style={{padding:'8px 10px',textAlign:'right'}}>Gross</th>
                    <th style={{padding:'8px 10px',textAlign:'right'}}>Retention</th>
                    <th style={{padding:'8px 10px',textAlign:'right'}}>Adv Recovery</th>
                    <th style={{padding:'8px 10px',textAlign:'right'}}>Net Payable</th>
                    <th style={{padding:'8px 10px',textAlign:'center'}}>Stage</th>
                  </tr>
                </thead>
                <tbody>
                  {contractHistory.ipcs.map(ipc => (
                    <tr key={ipc.id} style={{borderBottom:'1px solid #F3F4F6'}}>
                      <td style={{padding:'8px 10px',fontWeight:700,color:'#1A7A4A'}}>IPC-{String(ipc.ipc_number).padStart(4,'0')}</td>
                      <td style={{padding:'8px 10px',color:'#6B7280'}}>{ipc._actual_date || '—'}</td>
                      <td style={{padding:'8px 10px',textAlign:'right'}}>{fmtN(ipc.gross_amount)}</td>
                      <td style={{padding:'8px 10px',textAlign:'right',color:'#B45309'}}>{fmtN(ipc.retention_amount||ipc.retention_held)}</td>
                      <td style={{padding:'8px 10px',textAlign:'right',color:'#C2410C'}}>{fmtN(ipc.advance_deduction)}</td>
                      <td style={{padding:'8px 10px',textAlign:'right',fontWeight:700,color:'#1A7A4A'}}>{fmtN(ipc.net_payable)}</td>
                      <td style={{padding:'8px 10px',textAlign:'center'}}>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[ipc.approval_stage||ipc.status]||'bg-gray-100'}`}>
                          {(ipc.approval_stage||ipc.status||'draft').replace('_',' ')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{padding:'16px 24px',borderTop:'1px solid #E5E7EB'}}>
              <button onClick={()=>setContractHistory(null)} style={{padding:'8px 16px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:13,fontWeight:600,background:'#fff',cursor:'pointer'}}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}